import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { EMPTY, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, expand, finalize, map, reduce, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ServiceOrderAttachmentDto, ServiceOrdersService } from '../../../services/service-orders.service';
import { ConfigOrder, ServiceOrder, ServicoOS, AnexoOS, ChecklistOS } from '../../../models/ServiceOrder';
import { ConfigOrdersService, ConfigOrder as OsTypeConfig } from '../../../services/config-orders.service';
import { CustomerSelectionModalComponent, CustomerSelection } from '../customer-selection-modal/customer-selection-modal.component';
import { VehicleSelectionModalComponent, VehicleSelection } from '../vehicle-selection-modal/vehicle-selection-modal.component';
import { DirecionamentoModalComponent, DirecionamentoData } from '../direcionamento-modal/direcionamento-modal.component';
import { ServiceOrderCloseModalComponent, ServiceOrderCloseData } from '../service-order-close-modal/service-order-close-modal.component';
import { WarehouseSelectionModalComponent, WarehouseSelection } from '../warehouse-selection-modal/warehouse-selection-modal.component';
import { ProviderSelectionModalComponent, ProviderSelection } from '../provider-selection-modal/provider-selection-modal.component';
import { AuthService } from '../../../services/auth.service';
import {
  filterAccessibleConfigOrders,
  isB2bConfigOrder,
  mergeAccessibleConfigOrderIds
} from '../../../utils/config-order-access.util';
import { parseApiDateTime, combineBrazilDateAndTime, toBrazilWallClockString } from '../../../utils/datetime.utils';
import { ServiceTypeService } from '../../../services/service-type.service';
import { ServiceType } from '../../../models/ServiceType';
import { ItemService } from '../../../services/item.service';
import { Item } from '../../../models/Item';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { UnsavedChangesService } from '../../../shared/services/unsaved-changes.service';
import { UsersService } from '../../../services/users.service';
import { PagedServiceOrderLogs, ServiceOrderLog, ServiceOrderLogFilters, ServiceOrderLogsService } from '../../../services/service-order-logs.service';
import { VehiclesService } from '../../../services/vehicles.service';
import { Vehicle } from '../../../models/Vehicle';
import { ContractsService } from '../../../services/contracts.service';
import { Contract } from '../../../models/Contract';
import { CustomersService } from '../../../services/customers.service';
import { Customer } from '../../../models/Customer';
import {
  extractServiceOrderStockRequestId,
  mapStockRequestStatusToWorkSectionStatus,
  normalizeWorkSectionStatusKey,
  ServiceOrderWorkSection,
  ServiceOrderWorkSectionItem,
  ServiceOrderWorkSectionService,
  ServiceOrderWorkSectionsService,
  ServiceOrderWorkSectionUpdate
} from '../../../services/service-order-work-sections.service';
import {
  VehiclePreventiveMaintenanceHistoryService,
  VehiclePreventiveMaintenanceHistoryItem
} from '../../../services/vehicle-preventive-maintenance-history.service';
import { DepartmentService } from '../../../services/department.service';
import { ExternalLinksService } from '../../../services/external-links.service';
import { StockMovementsService } from '../../../services/stock-movements.service';
import { StockService } from '../../../services/stock.service';
import { Stock } from '../../../models/Stock';
import { AccountsReceivableService } from '../../../services/accounts-receivable.service';
import { AccountsReceivableCreate } from '../../../models/AccountsReceivable';
import { ExternalServiceProvidersService } from '../../../services/external-service-providers.service';
import { BudgetServiceOrdersService } from '../../../services/budget-service-orders.service';
import { BudgetCollaborationService } from '../../../services/budget-collaboration.service';
import { ChecklistItemDto } from '../../../models/Checklist';
import { ChecklistService } from '../../../services/checklist.service';
import { FormShellTab } from '../../../shared/components/form-shell/form-shell.component';
import {
  ServiceOrderOpeningReason,
  ServiceOrderOpeningReasonsService
} from '../../../services/service-order-opening-reasons.service';
import {
  VehicleServiceOrderStatusChange,
  VehicleServiceOrderStatusChangesService
} from '../../../services/vehicle-service-order-status-changes.service';

interface SessaoTrabalhoView {
  id?: number;
  descricao?: string;
  status?: string | null;
  dataSessao: string;
  usuario: string;
  servicos: ServicoOS[];
  pecas: any[];
  valorTotal: number;
  aprovado?: boolean;
  aprovadoPor?: string;
  dataAprovacao?: string;
  approvalLinkId?: number | null;
  approvalLinkCode?: string | null;
}

type StockSelection = Pick<Stock, 'id' | 'name' | 'warehouseId' | 'warehouseName'>;

@Component({
  selector: 'app-service-order-form',
  templateUrl: './service-order-form.component.html',
  styleUrls: ['./service-order-form.component.css']
})
export class ServiceOrderFormComponent implements OnInit, OnDestroy {
  @ViewChild('customerModal') customerModal!: CustomerSelectionModalComponent;
  @ViewChild('vehicleModal') vehicleModal!: VehicleSelectionModalComponent;
  @ViewChild('vehicleAdditionalModal') vehicleAdditionalModal!: VehicleSelectionModalComponent;
  @ViewChild('direcionamentoModal') direcionamentoModal!: DirecionamentoModalComponent;
  @ViewChild('encerramentoModal') encerramentoModal!: ServiceOrderCloseModalComponent;
  /** Sinaliza que o próximo carregamento deve oferecer o encerramento da OS. */
  private sugerirEncerramentoAoCarregar: boolean = false;
  @ViewChild('warehouseModal') warehouseModal!: WarehouseSelectionModalComponent;
  @ViewChild('providerModal') providerModal!: ProviderSelectionModalComponent;

  serviceOrderForm: FormGroup;
  isEditMode: boolean = false;
  isViewMode: boolean = false;
  serviceOrderId?: number;
  loading: boolean = false;
  submitting: boolean = false;
  gerandoOS: boolean = false;
  salvandoSessaoTrabalho: boolean = false;
  atualizandoAvariaSessao: boolean = false;
  salvandoKmVeiculo: boolean = false;
  /** Linhas em que o usuário desmarcou avaria nesta sessão (evita a inferência da corretiva). */
  private avariaDesmarcadaIds = new Set<number>();
  gerandoBoletoAvarias: boolean = false;
  boletoAvariasGerado: boolean = false;
  // Acréscimo % aplicável apenas em OS de Terceiros
  aplicarAcrescimo: boolean = false;
  acrescimoPercentual: number = 0;

  /** Quando preenchido, o salvamento da seção atualiza esta WorkSection
   *  (revisão de orçamento) em vez de criar uma nova. */
  editingWorkSectionId: number | null = null;

  /** Snapshot dos campos persistidos pelo botão Salvar (detecta alterações não salvas). */
  private savedOsStateSnapshot: string | null = null;
  /** Ignora o guard ao sair após salvar, descartar ou direcionamento bem-sucedido. */
  private bypassUnsavedGuard = false;

  // Configuração da OS (controla permissões de cada seção)
  configOrder: ConfigOrder | null = null;

  // Departamento do usuário logado (usado no payload de criação)
  currentUserDepartmentId: number | null = null;

  // Tipos de OS carregados da API (ConfigOrders)
  osTypeConfigs: OsTypeConfig[] = [];
  loadingOsTypeConfigs: boolean = false;
  
  // IDs de ConfigOrders permitidos pelo token
  allowedConfigOrderIds: number[] = [];

  // Config completa do tipo selecionado (inclui arrayTransactionServiceOrderId)
  private selectedOsTypeConfig: OsTypeConfig | null = null;
  selectedCustomer: CustomerSelection | null = null;
  selectedVehicle: VehicleSelection | null = null;
  selectedVehicleAdditional: VehicleSelection | null = null;
  selectedWarehouse: WarehouseSelection | null = null;
  selectedProvider: ProviderSelection | null = null;

  /** Indica que o usuário pediu para adicionar peça e o sistema está aguardando a seleção de almoxarifado. */
  private pendingAddPecaAfterWarehouse: boolean = false;

  // Histórico de preventivas do veículo
  preventiveHistory: VehiclePreventiveMaintenanceHistoryItem[] = [];
  loadingPreventiveHistory: boolean = false;
  
  // Dados mockados para seleção
  stocksList: StockSelection[] = [];
  warehousesList: any[] = [];
  selectedStockId: number | null = null;
  loadingStocks: boolean = false;
  
  statusOptions = ['Aberta', 'Em Andamento', 'Aguardando Peças', 'Aguardando Aprovação', 'Concluída', 'Cancelada'];
  tipoOptions = [
    'Manutenção Preventiva',
    'Manutenção Corretiva',
    'Revisão',
    'Reparo',
    'Instalação',
    'Diagnóstico',
    'Customização',
    'Outros'
  ];

  motivoAberturaOptions: string[] = [
    'Avaria',
    'Recolhimento',
    'Furto',
    'Outros'
  ];

  loadingMotivoAberturaOptions: boolean = false;
  private motivoAberturaConfigOrderId: number | null = null;

  vehicleStatusChangeRules: VehicleServiceOrderStatusChange[] = [];
  loadingVehicleStatusChangeRules: boolean = false;
  private vehicleStatusChangeRulesConfigOrderId: number | null = null;
  
  // Tipos de serviços disponíveis (carregados da API)
  tiposServicos: ServiceType[] = [];
  loadingServicos: boolean = false;
  
  // Itens disponíveis (carregados da API)
  itemsDisponiveis: Item[] = [];
  loadingItems: boolean = false;
  
  // Mapeamento de serviços com peças e valores pré-definidos
  servicosPecasValores: { [key: string]: Array<{item: string, quantidade: number, valorUnitario: number}> } = {
    'Troca de óleo': [
      { item: 'Óleo de motor 5W30', quantidade: 4, valorUnitario: 35.00 },
      { item: 'Filtro de óleo', quantidade: 1, valorUnitario: 25.00 },
      { item: 'Mão de obra', quantidade: 1, valorUnitario: 50.00 }
    ],
    'Troca de filtro de óleo': [
      { item: 'Filtro de óleo', quantidade: 1, valorUnitario: 25.00 },
      { item: 'Mão de obra', quantidade: 1, valorUnitario: 30.00 }
    ],
    'Troca de filtro de ar': [
      { item: 'Filtro de ar', quantidade: 1, valorUnitario: 45.00 },
      { item: 'Mão de obra', quantidade: 1, valorUnitario: 20.00 }
    ],
    'Troca de filtro de combustível': [
      { item: 'Filtro de combustível', quantidade: 1, valorUnitario: 55.00 },
      { item: 'Mão de obra', quantidade: 1, valorUnitario: 40.00 }
    ],
    'Revisão geral': [
      { item: 'Óleo de motor 5W30', quantidade: 4, valorUnitario: 35.00 },
      { item: 'Filtro de óleo', quantidade: 1, valorUnitario: 25.00 },
      { item: 'Filtro de ar', quantidade: 1, valorUnitario: 45.00 },
      { item: 'Filtro de combustível', quantidade: 1, valorUnitario: 55.00 },
      { item: 'Velas de ignição', quantidade: 4, valorUnitario: 28.00 },
      { item: 'Mão de obra', quantidade: 1, valorUnitario: 150.00 }
    ],
    'Alinhamento e balanceamento': [
      { item: 'Alinhamento', quantidade: 1, valorUnitario: 80.00 },
      { item: 'Balanceamento (4 rodas)', quantidade: 1, valorUnitario: 60.00 }
    ],
    'Troca de pastilhas de freio': [
      { item: 'Jogo de pastilhas dianteiras', quantidade: 1, valorUnitario: 120.00 },
      { item: 'Fluido de freio', quantidade: 1, valorUnitario: 35.00 },
      { item: 'Mão de obra', quantidade: 1, valorUnitario: 100.00 }
    ],
    'Troca de discos de freio': [
      { item: 'Par de discos dianteiros', quantidade: 1, valorUnitario: 280.00 },
      { item: 'Jogo de pastilhas', quantidade: 1, valorUnitario: 120.00 },
      { item: 'Fluido de freio', quantidade: 1, valorUnitario: 35.00 },
      { item: 'Mão de obra', quantidade: 1, valorUnitario: 200.00 }
    ],
    'Troca de correia dentada': [
      { item: 'Correia dentada', quantidade: 1, valorUnitario: 180.00 },
      { item: 'Tensor da correia', quantidade: 1, valorUnitario: 95.00 },
      { item: 'Rolamento', quantidade: 1, valorUnitario: 75.00 },
      { item: 'Mão de obra', quantidade: 1, valorUnitario: 300.00 }
    ],
    'Troca de velas': [
      { item: 'Velas de ignição', quantidade: 4, valorUnitario: 28.00 },
      { item: 'Mão de obra', quantidade: 1, valorUnitario: 60.00 }
    ],
    'Troca de bateria': [
      { item: 'Bateria 60Ah', quantidade: 1, valorUnitario: 450.00 },
      { item: 'Mão de obra', quantidade: 1, valorUnitario: 30.00 }
    ],
    'Troca de pneus': [
      { item: 'Pneu 195/65 R15', quantidade: 4, valorUnitario: 350.00 },
      { item: 'Montagem e balanceamento', quantidade: 1, valorUnitario: 120.00 },
      { item: 'Alinhamento', quantidade: 1, valorUnitario: 80.00 }
    ],
    'Diagnóstico eletrônico': [
      { item: 'Diagnóstico com scanner', quantidade: 1, valorUnitario: 100.00 }
    ],
    'Limpeza de bicos injetores': [
      { item: 'Limpeza ultrassônica', quantidade: 1, valorUnitario: 200.00 },
      { item: 'Aditivo de limpeza', quantidade: 1, valorUnitario: 45.00 }
    ]
  };
  
  // Campo para nova observação (registrada no histórico de movimentações)
  novaObservacao: string = '';
  
  // Histórico de movimentações
  historico: any[] = [];
  historicoFiltrado: any[] = [];
  filtroDataInicio: string = '';
  filtroDataFim: string = '';

  private historicoFiltroRequestKey: string | null = null;

  private readonly userNameCache = new Map<number, string>();
  private readonly userNameInFlight = new Map<number, Observable<string | null>>();

  private readonly destroy$ = new Subject<void>();

  get activeTabId(): string {
    return String(this.currentStep);
  }

  get serviceOrderTabs(): FormShellTab[] {
    const tabs: FormShellTab[] = [];

    if (!this.osGerada) {
      tabs.push({
        id: '1',
        label: 'Abertura',
        icon: 'fas fa-play-circle'
      });
    }

    tabs.push({
      id: '2',
      label: 'Informações Gerais',
      icon: 'fas fa-info-circle',
      disabled: !this.osGerada
    });

    const vehicleLocked = !this.canVehicleSection();
    tabs.push({
      id: '3',
      label: 'Veículo',
      icon: 'fas fa-car',
      disabled: !this.osGerada || vehicleLocked,
      locked: vehicleLocked,
      lockedTitle: 'Bloqueado pela configuração da OS'
    });

    tabs.push({
      id: '4',
      label: 'Responsáveis',
      icon: 'fas fa-users',
      disabled: !this.osGerada
    });

    const servicesLocked = !this.canServicesSection();
    tabs.push({
      id: '5',
      label: 'Serviços',
      icon: 'fas fa-tools',
      disabled: !this.osGerada || servicesLocked,
      locked: servicesLocked,
      lockedTitle: 'Bloqueado pela configuração da OS'
    });

    tabs.push({
      id: '6',
      label: 'Anexos',
      icon: 'fas fa-paperclip',
      disabled: !this.osGerada
    });

    tabs.push({
      id: '7',
      label: 'Histórico',
      icon: 'fas fa-history',
      disabled: !this.osGerada
    });

    tabs.push({
      id: '8',
      label: 'CheckList',
      icon: 'fas fa-tasks',
      disabled: !this.osGerada
    });

    return tabs;
  }

  onServiceOrderTabChange(tabId: string): void {
    const parsed = Number(tabId);
    if (!Number.isFinite(parsed)) {
      return;
    }
    this.goToStep(parsed);
  }

  trackByOsTypeConfig(index: number, cfg: OsTypeConfig): number | string {
    return cfg?.id ?? cfg?.typeOS ?? index;
  }

  trackByPreventiveHistory(index: number, item: VehiclePreventiveMaintenanceHistoryItem): number {
    return item?.id ?? index;
  }

  trackByServico(index: number, servico: ServicoOS): number {
    return servico?.id ?? index;
  }

  trackByServiceType(index: number, tipo: ServiceType): number {
    return tipo?.id ?? index;
  }

  trackByItemDisponivel(index: number, item: Item): number {
    return item?.id ?? index;
  }

  trackByPeca(index: number, peca: any): number | string {
    return peca?.id ?? index;
  }

  trackBySessaoTrabalho(index: number, sessao: SessaoTrabalhoView): number | string {
    return sessao?.id ?? index;
  }

  trackBySessaoServico(index: number, servico: ServicoOS): number {
    return servico?.id ?? index;
  }

  trackBySessaoPeca(index: number, peca: any): number | string {
    return peca?.id ?? index;
  }

  trackByTipoAnexo(index: number, tipo: string): string {
    return tipo ?? String(index);
  }

  trackByAnexo(index: number, anexo: AnexoOS): number | string {
    return anexo?.id ?? anexo?.nomeArquivo ?? index;
  }

  trackByHistorico(index: number, evento: any): number | string {
    return evento?.id ?? `${evento?.tipo ?? 'evt'}-${evento?.dataHora ?? ''}-${index}`;
  }

  trackByVehicleChecklist(index: number, checklist: ChecklistItemDto): number {
    return checklist?.Id ?? index;
  }
  
  // Serviços prestados
  servicos: ServicoOS[] = [];
  
  // Peças
  pecas: any[] = [];
  
  // Peças e valores do serviço selecionado
  pecasServicoSelecionado: Array<{item: string, quantidade: number, valorUnitario: number, valorTotal: number}> = [];
  valorTotalServicoSelecionado: number = 0;
  valorUnitarioServico: number = 0;
  
  // Controle de detalhes expandidos das peças
  servicoDetalhesExpandidos: boolean[] = [];
  
  // Peças disponíveis no estoque
  pecasDisponiveisEstoque: Array<{id: string, nome: string, categoria: string, quantidadeDisponivel: number, valorUnitario: number}> = [
    { id: 'P001', nome: 'Óleo de motor 5W30', categoria: 'Fluidos', quantidadeDisponivel: 25, valorUnitario: 35.00 },
    { id: 'P002', nome: 'Filtro de óleo', categoria: 'Filtros', quantidadeDisponivel: 15, valorUnitario: 25.00 },
    { id: 'P003', nome: 'Filtro de ar', categoria: 'Filtros', quantidadeDisponivel: 12, valorUnitario: 45.00 },
    { id: 'P004', nome: 'Filtro de combustível', categoria: 'Filtros', quantidadeDisponivel: 8, valorUnitario: 55.00 },
    { id: 'P005', nome: 'Velas de ignição', categoria: 'Ignição', quantidadeDisponivel: 20, valorUnitario: 28.00 },
    { id: 'P006', nome: 'Jogo de pastilhas dianteiras', categoria: 'Freios', quantidadeDisponivel: 6, valorUnitario: 120.00 },
    { id: 'P007', nome: 'Fluido de freio', categoria: 'Fluidos', quantidadeDisponivel: 10, valorUnitario: 35.00 },
    { id: 'P008', nome: 'Par de discos dianteiros', categoria: 'Freios', quantidadeDisponivel: 4, valorUnitario: 280.00 },
    { id: 'P009', nome: 'Correia dentada', categoria: 'Motor', quantidadeDisponivel: 3, valorUnitario: 180.00 },
    { id: 'P010', nome: 'Tensor da correia', categoria: 'Motor', quantidadeDisponivel: 2, valorUnitario: 95.00 },
    { id: 'P011', nome: 'Rolamento', categoria: 'Motor', quantidadeDisponivel: 5, valorUnitario: 75.00 },
    { id: 'P012', nome: 'Bateria 60Ah', categoria: 'Elétrica', quantidadeDisponivel: 3, valorUnitario: 450.00 },
    { id: 'P013', nome: 'Pneu 195/65 R15', categoria: 'Pneus', quantidadeDisponivel: 8, valorUnitario: 350.00 },
    { id: 'P014', nome: 'Aditivo de limpeza', categoria: 'Fluidos', quantidadeDisponivel: 7, valorUnitario: 45.00 },
    { id: 'P015', nome: 'Anticongelante', categoria: 'Fluidos', quantidadeDisponivel: 12, valorUnitario: 25.00 }
  ];
  
  // Peças selecionadas para o serviço
  pecasSelecionadasEstoque: string[] = [];
  
  // Controle de Steps/Abas
  currentStep: number = 1;
  totalSteps: number = 8;
  
  // Controle de OS gerada
  osGerada: boolean = false;
  
  // Tipos de anexo disponíveis
  tiposAnexo = [
    'Avaria',
    'Documento Adicional',
    'Foto do Veículo',
    'Orçamento',
    'Nota Fiscal',
    'Laudo Técnico',
    'Checklist',
    'Contrato',
    'Autorização',
    'Outros'
  ];

  // Tipos de checklist disponíveis
  tiposChecklist = [
    'Pré-entrega',
    'Pós-entrega', 
    'Inspeção Técnica',
    'Verificação de Segurança',
    'Controle de Qualidade',
    'Vistoria Geral',
    'Outros'
  ];
  
  // Anexos
  anexos: AnexoOS[] = [];
  loadingAnexos: boolean = false;
  anexosLoadError: string | null = null;

  novoAnexo: Partial<AnexoOS> & { arquivo: File | null; nomeArquivo?: string; tamanho?: number } = {
    tipo: undefined,
    descricao: '',
    arquivo: null,
    nomeArquivo: undefined,
    tamanho: undefined
  };

  // CheckLists
  checklists: ChecklistOS[] = [];
  checklistSelecionado: ChecklistOS | null = null;

  // Checklists do veículo (API /checklist/list?plateNumber=...)
  vehicleChecklists: ChecklistItemDto[] = [];
  vehicleChecklistSelecionado: ChecklistItemDto | null = null;
  loadingVehicleChecklists: boolean = false;
  vehicleChecklistsLoadError: string | null = null;
  private vehicleChecklistsLastPlate: string | null = null;

  // Sessões de Trabalho
  sessoesTrabalho: SessaoTrabalhoView[] = [];

  // Seções de trabalho (ServiceWorkSection) carregadas da API
  // Usado para validações antes do direcionamento.
  serviceWorkSections: ServiceOrderWorkSection[] = [];

  constructor(
    private fb: FormBuilder,
    private serviceOrdersService: ServiceOrdersService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private sanitizer: DomSanitizer,
    private checklistService: ChecklistService,
    private serviceTypeService: ServiceTypeService,
    private itemService: ItemService,
    private confirmationService: ConfirmationService,
    private unsavedChangesService: UnsavedChangesService,
    private configOrdersService: ConfigOrdersService,
    private usersService: UsersService,
    private serviceOrderLogsService: ServiceOrderLogsService,
    private vehiclesService: VehiclesService,
    private serviceOrderWorkSectionsService: ServiceOrderWorkSectionsService,
    private contractsService: ContractsService,
    private customersService: CustomersService,
    private vehiclePreventiveHistoryService: VehiclePreventiveMaintenanceHistoryService,
    private departmentService: DepartmentService,
    private externalLinksService: ExternalLinksService,
    private stockMovementsService: StockMovementsService,
    private stockService: StockService,
    private accountsReceivableService: AccountsReceivableService,
    private externalServiceProvidersService: ExternalServiceProvidersService,
    private serviceOrderOpeningReasonsService: ServiceOrderOpeningReasonsService,
    private vehicleServiceOrderStatusChangesService: VehicleServiceOrderStatusChangesService,
    private budgetServiceOrdersService: BudgetServiceOrdersService,
    private budgetCollaboration: BudgetCollaborationService
  ) {
    this.serviceOrderForm = this.fb.group({
      // Campos Automáticos
      numeroOS: [{ value: '', disabled: true }, Validators.required],
      usuarioNome: [{ value: '', disabled: true }, Validators.required],
      unidadeNome: [{ value: '', disabled: true }, Validators.required],
      dataAbertura: [{ value: this.todayBrazilDateInput(), disabled: true }, Validators.required],
      horaAbertura: [{ value: '', disabled: true }, Validators.required],
      
      // Campos de Seleção
      tipoOS: ['', Validators.required],
      motivoAbertura: ['', Validators.required],
      tipo: ['', Validators.required],
      status: ['Aberta', Validators.required],
      
      // Setor de Abertura
      setorAbertura: [{ value: '', disabled: true }, Validators.required],
      
      // Status e Setor Atual
      statusAtual: ['Aberta', Validators.required],
      setorAtual: ['Recepção', Validators.required],
      motivoStatus: [''],
      
      // Veículo e Informações
      veiculoId: [''],
      veiculoPlaca: [''],
      km: [''],
      veiculoMarca: [''],
      veiculoModelo: [''],
      veiculoAnoModelo: [''],
      veiculoStatusDetalhes: [''],
      veiculoQuilometragemAtual: [''],
      veiculoUltimaManutencaoData: [''],
      veiculoUltimaManutencaoKm: [''],
      veiculoProximaManutencaoData: [''],
      veiculoRequerManutencao: [''],
      veiculoObservacoes: [''],
      
      // Veículo Adicional
      veiculoAdicionalId: [''],
      veiculoAdicionalPlaca: [''],
      
      // Cliente e Contrato
      clienteId: [''],
      clienteNome: [''],
      clienteDocumento: [''],
      clienteCategoria: [''],
      contratoId: [''],
      contratoNumero: [''],
      contratoStatus: [''],
      contratoDataInicio: [''],
      contratoDataFim: [''],
      contratoProdutoNome: [''],
      contratoProdutoAtivo: [''],

      // Contrato B2B / ciclo operacional (opcional)
      b2bContractId: [''],
      b2bContractItemId: [''],
      fuelLevel: [''],
      openingMileage: [''],
      entryDate: [''],
      startWorkDate: [''],
      deadlineDate: [''],
      endWorkDate: [''],
      pickupDate: [''],
      isScheduled: [false],
      
      // Mecânico
      mecanicoId: [''],
      mecanicoNome: [''],
      
      // Estoque
      estoqueId: [''],
      estoqueNome: [''],
      
      // Almoxarifado
      almoxarifadoId: [''],
      almoxarifadoNome: [''],

      // Prioridade da solicitação de peças ao estoque (service-order-stock-requests)
      stockRequestPriority: ['Normal'],

      // Descrição (não mais obrigatória na UI)
      descricao: [''],
      
      // Datas e Valores
      dataPrevistaConclusao: [''],
      dataConclusao: [''],
      // Hora do fechamento: preenchida no modal de encerramento e usada no closingDate.
      horaConclusao: [''],
      valorEstimado: [''],
      valorFinal: ['']
    });
  }

  ngOnInit(): void {
    // Inicializar histórico filtrado
    this.historicoFiltrado = [...this.historico];
    
    // Inicializar dados mockados
    this.initializeMockData();

    if (this.canStockSection()) {
      this.carregarStocks();
    }

    // Carregar tipos de OS da API
    this.carregarConfigOrders();
    
    // Carregar serviços da API
    this.carregarServicos();

    // Carregar todos os itens ativos globalmente (cada item já contém seu stockId/warehouseId).
    this.carregarItemsAtivos();
    
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
      if (params['id']) {
        this.serviceOrderId = +params['id'];
        this.isEditMode = this.router.url.includes('edit');
        this.isViewMode = this.router.url.includes('view');
        // Fora do fluxo de criação, o motivo de abertura não é obrigatório no formulário.
        this.setMotivoAberturaRequired(false);
        this.loadServiceOrder();
      } else {
        // Preencher dados automáticos iniciais da tela
        this.fillAutomaticFields();
        this.setMotivoAberturaRequired(true);
        // Carregar checklists mockados para demonstração
        this.checklists = this.getMockChecklists();
      }

      if (this.isViewMode) {
        this.serviceOrderForm.disable();
      }
    });
  }

  private setMotivoAberturaRequired(required: boolean): void {
    const ctrl = this.serviceOrderForm.get('motivoAbertura');
    if (!ctrl) return;

    if (required) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  /**
   * Preenche campos automáticos com dados do usuário logado
   */
  fillAutomaticFields(): void {
    // Pegar usuário do authService (mockado por enquanto)
    const currentUser = this.authService.currentUserValue;
    
    // Preencher nome do usuário
    const usuarioNome = currentUser?.username || 'Usuário Sistema';
    this.serviceOrderForm.patchValue({ 
      usuarioNome: usuarioNome
    });
    
    // Preencher unidade (ID vem do token, nome mockado)
    this.currentUserDepartmentId = this.authService.getDepartmentIdFromToken();
    this.serviceOrderForm.patchValue({ 
      unidadeNome: 'Unidade Central - SP'
    });
    
    // Preencher setor de abertura com o departamento do usuário logado
    if (this.currentUserDepartmentId) {
      this.departmentService.getDepartmentById(this.currentUserDepartmentId).subscribe({
        next: (dept) => {
          this.serviceOrderForm.patchValue({
            setorAbertura: dept.name,
            setorAtual: dept.name
          });
        },
        error: () => {
          this.serviceOrderForm.patchValue({ setorAbertura: 'Recepção', setorAtual: 'Recepção' });
        }
      });
    } else {
      this.serviceOrderForm.patchValue({ setorAbertura: 'Recepção', setorAtual: 'Recepção' });
    }
    
    // Preencher hora atual (Brasília)
    const wall = toBrazilWallClockString(new Date());
    const timePart = wall.includes('T') ? wall.split('T')[1].slice(0, 5) : '';
    this.serviceOrderForm.patchValue({
      horaAbertura: timePart || '00:00'
    });
    
  }

  /**
   * Gera um número de OS automático no formato OS-YYYYMMDD-XXXX
   */
  generateOSNumber(): void {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 9999) + 1;
    const randomPadded = String(random).padStart(4, '0');
    
    const osNumber = `OS-${year}${month}${day}-${randomPadded}`;
    this.serviceOrderForm.patchValue({ numeroOS: osNumber });
  }

  private extractHourFromDateTime(value: any): string {
    return this.extractBrazilHour(value);
  }

  private extractBrazilHour(value: any): string {
    if (!value) return '';
    const parsed = parseApiDateTime(String(value));
    if (!parsed) return '';
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(parsed);
    const get = (t: string) => parts.find(p => p.type === t)?.value ?? '00';
    return `${get('hour')}:${get('minute')}`;
  }

  private hasMeaningfulTime(value: any): boolean {
    if (!value) {
      return false;
    }

    const normalized = String(value);
    // Rejects T00:00:00 (midnight placeholder from backend)
    return /T(?!00:00(:00)?)\d{2}:\d{2}/.test(normalized);
  }

  getDisplayLoggedUser(): string {
    const formUser = (this.serviceOrderForm.get('usuarioNome')?.value || '').toString().trim();
    if (formUser) {
      return formUser;
    }

    return this.authService.currentUserValue?.username || 'Usuário Sistema';
  }

  getDisplayOpeningHour(): string {
    return (this.serviceOrderForm.get('horaAbertura')?.value || '').toString().trim() || '00:00';
  }

  getDisplayTipoOS(): string {
    return this.serviceOrderForm.get('tipoOS')?.value || this.configOrder?.typeOS || '';
  }

  loadServiceOrder(options?: { successMessage?: string }): void {
    if (!this.serviceOrderId) return;

    this.loading = true;
    forkJoin({
      data: this.serviceOrdersService.getById(this.serviceOrderId),
      logs: this.serviceOrderLogsService.getByServiceOrderId(this.serviceOrderId, { pageNumber: 1, pageSize: 100 }).pipe(
        catchError((error) => {
          console.error('Erro ao carregar logs da ordem de serviço:', error);
          return of(this.createEmptyLogsResponse());
        })
      ),
      sections: this.loadWorkSectionsWithDetails(this.serviceOrderId).pipe(
        catchError((error) => {
          console.error('Erro ao carregar seções de trabalho da ordem de serviço:', error);
          return of([] as ServiceOrderWorkSection[]);
        })
      )
    }).subscribe({
      next: ({ data, logs, sections }) => {
        this.applyServiceOrderData(data);

        const logItems = logs?.items || [];
        this.historico = logItems.length > 0
          ? this.mapLogsToHistorico(logItems)
          : ((data.historico && data.historico.length > 0) ? data.historico : this.historico);
        this.aplicarFiltroHistorico();

        if (logItems.length > 0) {
          this.mapLogsToHistoricoResolvedUsers(logItems)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
              next: (mapped) => {
                this.historico = mapped;
                this.aplicarFiltroHistorico();
              },
              error: () => {
                // Mantém o histórico já exibido.
              }
            });
        }

        const sectionsAfterMirror$ = this.isThirdPartyOS()
          ? this.tryMirrorApprovedBudget(sections || []).pipe(
              switchMap(() => this.loadWorkSectionsWithDetails(this.serviceOrderId!)),
              catchError(() => of(sections || []))
            )
          : of(sections || []);

        sectionsAfterMirror$.pipe(
          switchMap((loadedSections) => this.syncWorkSectionsStatusFromStockRequests(loadedSections)),
          take(1)
        ).subscribe({
            next: (synced) => {
              this.serviceWorkSections = synced;
              this.sessoesTrabalho = synced.length > 0
                ? this.mapWorkSectionsToSessions(synced)
                : ((data as any).sessoesTrabalho || []);
              this.finishLoadServiceOrder(options);
            },
            error: (syncError) => {
              console.error('Erro ao sincronizar status das seções com estoque:', syncError);
              this.serviceWorkSections = sections || [];
              this.sessoesTrabalho = sections.length > 0
                ? this.mapWorkSectionsToSessions(sections)
                : ((data as any).sessoesTrabalho || []);
              this.finishLoadServiceOrder(options);
            }
          });
      },
      error: (error) => {
        console.error('Erro ao carregar ordem de serviço:', error);
        this.openError('Erro ao carregar ordem de serviço.').subscribe(() => {
          this.router.navigate(['/service-orders']);
        });
        this.loading = false;
        this.submitting = false;
      }
    });
  }

  private finishLoadServiceOrder(options?: { successMessage?: string }): void {
    this.loading = false;
    this.submitting = false;
    if (options?.successMessage) {
      this.openSuccess(options.successMessage).subscribe(() => this.perguntarEncerramentoSugerido());
      return;
    }
    this.perguntarEncerramentoSugerido();
  }

  /**
   * O encerramento é irreversível, então o direcionamento apenas anota a intenção:
   * a pergunta só aparece depois que a OS recarrega com o status vindo da API.
   */
  private marcarSugestaoEncerramento(statusAnterior: string, statusNovo: string): void {
    this.sugerirEncerramentoAoCarregar = false;

    const allowedStatus = (this.configOrder?.statusClosedServiceOrder || '').toString().trim();
    if (!allowedStatus || this.configOrder?.canClose === false) return;

    const anterior = (statusAnterior || '').toString().trim();
    const novo = (statusNovo || '').toString().trim();
    this.sugerirEncerramentoAoCarregar = novo === allowedStatus && anterior !== allowedStatus;
  }

  private perguntarEncerramentoSugerido(): void {
    if (!this.sugerirEncerramentoAoCarregar) return;
    this.sugerirEncerramentoAoCarregar = false;

    const allowedStatus = (this.configOrder?.statusClosedServiceOrder || '').toString().trim();
    const currentStatus = (this.serviceOrderForm.get('statusAtual')?.value || '').toString().trim();
    if (!allowedStatus || currentStatus !== allowedStatus) return;

    this.encerrarOS('sugestao');
  }

  /** Garante work section de execução com itens aprovados antes de exibir a OS terceiros. */
  private tryMirrorApprovedBudget(sections: ServiceOrderWorkSection[]): Observable<void> {
    const hasExecution = sections.some(
      (section) => Number(section.sourceBudgetWorkSectionId ?? (section as any).SourceBudgetWorkSectionId ?? 0) > 0
    );
    if (hasExecution) return of(void 0);

    const checks = sections
      .filter((section) => section.id > 0)
      .map((section) =>
        this.budgetServiceOrdersService.getByWorkSectionId(section.id).pipe(
          catchError(() => of([]))
        )
      );

    if (checks.length === 0) return of(void 0);

    return forkJoin(checks).pipe(
      switchMap((lists) => {
        const budget = lists
          .flat()
          .find((b) => {
            if (!b?.id) return false;
            const status = (b.status || '').toLowerCase();
            return b.clientApproved || status.includes('aprov');
          });

        if (!budget?.id) return of(void 0);

        return this.budgetCollaboration.mirrorApproved(budget.id).pipe(
          map(() => void 0),
          catchError((err) => {
            console.warn('[OS] Espelho do orçamento aprovado não aplicado:', err);
            return of(void 0);
          })
        );
      })
    );
  }

  /** Recarrega OS na tela de edição e limpa buffers locais de serviços/peças. */
  private refreshServiceOrderPage(successMessage?: string): void {
    this.submitting = false;
    this.editingWorkSectionId = null;
    this.limparServicosEPecas();
    this.loadServiceOrder(successMessage ? { successMessage } : undefined);
  }

  private applyServiceOrderData(data: ServiceOrder): void {
    const rawData = data as any;
    const currentMotivo = (this.serviceOrderForm.get('motivoAbertura')?.value || '').toString().trim();
    const apiMotivo = (data.motivoAbertura || '').toString().trim();
    const resolvedMotivoAbertura = apiMotivo || currentMotivo || '';

    this.serviceOrderForm.patchValue({
      ...data,
      motivoAbertura: resolvedMotivoAbertura,
      usuarioNome: data.usuarioNome || rawData.userName || rawData.UserName || this.serviceOrderForm.get('usuarioNome')?.value || this.authService.currentUserValue?.username || 'Usuário Sistema',
      dataAbertura: this.formatDateForInput(data.dataAbertura),
      horaAbertura: data.horaAbertura
        || this.extractBrazilHour(data.dataAbertura)
        || '00:00',
      dataPrevistaConclusao: this.formatDateForInput(data.dataPrevistaConclusao),
      dataConclusao: this.formatDateForInput(data.dataConclusao),
      horaConclusao: this.extractBrazilHour(data.dataConclusao),
      tipoOS: data.tipoOS || data.configOrder?.typeOS || '',
      statusAtual: data.status || this.serviceOrderForm.get('statusAtual')?.value || 'Aberta',
      b2bContractId: data.b2bContractId ?? '',
      b2bContractItemId: data.b2bContractItemId ?? '',
      fuelLevel: data.fuelLevel || data.combustivel || '',
      openingMileage: data.openingMileage ?? data.km ?? '',
      entryDate: this.formatDateForInput(data.entryDate),
      startWorkDate: this.formatDateForInput(data.startWorkDate),
      deadlineDate: this.formatDateForInput(data.deadlineDate ?? data.dataPrevistaConclusao),
      endWorkDate: this.formatDateForInput(data.endWorkDate),
      pickupDate: this.formatDateForInput(data.pickupDate),
      isScheduled: !!data.isScheduled
    });

    // Resolver nome do departamento a partir do departmentId
    const deptId = data.unidadeId ?? (data as any).departmentId ?? (data as any).DepartmentId ?? this.currentUserDepartmentId;
    if (deptId) {
      this.departmentService.getDepartmentById(deptId).subscribe({
        next: (dept) => {
          this.serviceOrderForm.patchValue({
            setorAbertura: (data as any).setorAbertura || dept.name,
            setorAtual: (data as any).setorAtual || dept.name
          });
        },
        error: () => {
          this.serviceOrderForm.patchValue({
            setorAbertura: (data as any).setorAbertura || 'Recepção',
            setorAtual: (data as any).setorAtual || 'Recepção'
          });
        }
      });
    } else {
      this.serviceOrderForm.patchValue({
        setorAbertura: (data as any).setorAbertura || 'Recepção',
        setorAtual: (data as any).setorAtual || 'Recepção'
      });
    }

    this.configOrder = data.configOrder || null;

    const resolvedConfigOrderId =
      this.parsePositiveNumber(data.configOrderId)
      ?? this.parsePositiveNumber((data as any)?.configOrder?.id)
      ?? null;

    this.loadMotivoAberturaOptions(resolvedConfigOrderId);
    this.loadVehicleStatusChangeRules(resolvedConfigOrderId);

    if (resolvedConfigOrderId) {
      this.configOrdersService.getById(resolvedConfigOrderId).subscribe({
        next: (cfg) => {
          this.selectedOsTypeConfig = cfg;
          this.configOrder = {
            id: cfg.id,
            typeOS: cfg.typeOS,
            groupId: cfg.groupId ?? undefined,
            departmentId: cfg.departmentId ?? undefined,
            statusClosedServiceOrder: cfg.statusClosedServiceOrder ?? null,
            transaction: cfg.transaction === true,
            isThirdParty: cfg.isThirdParty === true,
            canVehicle: cfg.canVehicle !== false,
            canAditionalVehicle: cfg.canAditionalVehicle !== false,
            canClient: cfg.canClient !== false,
            canStock: cfg.canStock !== false,
            canServices: cfg.canServices !== false,
            canClose: cfg.canClose !== false,
            active: cfg.active !== false
          };

          this.loadMotivoAberturaOptions(cfg?.id ?? resolvedConfigOrderId);
          this.loadVehicleStatusChangeRules(cfg?.id ?? resolvedConfigOrderId);

          if (!this.isThirdPartyOS()) {
            this.selectedProvider = null;
          }
        },
        error: () => {}
      });
    }

    this.servicos = data.servicos || [];
    this.anexos = data.anexos || [];
    this.checklists = (data as any).checklists || this.getMockChecklists();
    this.osGerada = true;

    // Após gerar/carregar a OS, o motivo já foi informado ou persistido; não exige revalidação no submit.
    this.setMotivoAberturaRequired(false);

    // Ao carregar uma OS existente, ir direto para "Informações Gerais"
    if (this.currentStep === 1) {
      this.currentStep = 2;
    }

    const veiculoIdToLoad = data.veiculoId ?? this.toIntOrNull(this.serviceOrderForm.get('veiculoId')?.value);
    this.loadLinkedVehicleDetails(veiculoIdToLoad);
    if (veiculoIdToLoad) {
      this.loadPreventiveHistory(veiculoIdToLoad);
    }

    // Carregar detalhes do cliente e contrato vinculado (se houver)
    const clienteId = this.toIntOrNull(data.clienteId ?? this.serviceOrderForm.get('clienteId')?.value);
    if (clienteId) {
      this.loadCustomerDetails(clienteId);
      this.loadContractByCustomer(clienteId);
    }

    this.scheduleOsStateSnapshotReset();
  }

  /** Aguarda patchValue assíncronos (cliente/veículo) antes de gravar o baseline. */
  private scheduleOsStateSnapshotReset(): void {
    setTimeout(() => this.resetOsStateSnapshot(), 100);
  }

  private createEmptyLogsResponse(): PagedServiceOrderLogs {
    return {
      items: [],
      pageNumber: 1,
      pageSize: 0,
      totalCount: 0,
      totalPages: 1,
      hasPreviousPage: false,
      hasNextPage: false
    };
  }

  private loadWorkSectionsWithDetails(serviceOrderId: number): Observable<ServiceOrderWorkSection[]> {
    return this.serviceOrderWorkSectionsService.getByServiceOrderId(serviceOrderId).pipe(
      switchMap((sections) => {
        if (sections.length === 0) {
          return of([] as ServiceOrderWorkSection[]);
        }

        const requests = sections.map((section) => {
          const items$ = this.serviceOrderWorkSectionsService.getItemsByWorkSectionId(section.id).pipe(
            catchError(() => of([] as ServiceOrderWorkSectionItem[]))
          );
          const services$ = this.serviceOrderWorkSectionsService.getServicesByWorkSectionId(section.id).pipe(
            catchError(() => of([] as ServiceOrderWorkSectionService[]))
          );

          return forkJoin({ items: items$, services: services$ }).pipe(
            map(({ items, services }) => ({
              ...section,
              items,
              services
            }))
          );
        });

        return requests.length > 0 ? forkJoin(requests) : of([] as ServiceOrderWorkSection[]);
      })
    );
  }

  private readonly stockManagedWorkSectionStatusKeys = new Set([
    'pendente',
    'aguardando pecas',
    'em andamento',
    'cancelado'
  ]);

  private isStockManagedWorkSectionStatus(status: string | null | undefined): boolean {
    const key = normalizeWorkSectionStatusKey(status);
    return !key || this.stockManagedWorkSectionStatusKeys.has(key);
  }

  private syncWorkSectionsStatusFromStockRequests(
    sections: ServiceOrderWorkSection[]
  ): Observable<ServiceOrderWorkSection[]> {
    if (!this.canStockSection() || this.isThirdPartyOS() || !sections?.length) {
      return of(sections);
    }

    const linkedSections = sections.filter(
      (section) => Number(section.serviceOrderStockRequestsId) > 0
    );

    if (linkedSections.length === 0) {
      return of(sections);
    }

    const syncRequests = linkedSections.map((section) => {
      const stockRequestId = Number(section.serviceOrderStockRequestsId);

      if (!this.isStockManagedWorkSectionStatus(section.status)) {
        return of(section);
      }

      return this.stockMovementsService.getServiceOrderStockRequestById(stockRequestId).pipe(
        switchMap((detail) => {
          const mappedStatus = mapStockRequestStatusToWorkSectionStatus(detail?.status);
          const currentKey = normalizeWorkSectionStatusKey(section.status);
          const mappedKey = normalizeWorkSectionStatusKey(mappedStatus);

          if (currentKey === mappedKey) {
            return of({ ...section, status: mappedStatus });
          }

          return this.serviceOrderWorkSectionsService
            .update(
              section.id,
              {
                serviceOrderId: section.serviceOrderId ?? this.serviceOrderId!,
                description: section.description,
                status: mappedStatus,
                serviceOrderStockRequestsId: stockRequestId
              },
              false
            )
            .pipe(
              map((updated) => ({
                ...section,
                ...updated,
                status: mappedStatus,
                items: section.items,
                services: section.services
              })),
              catchError((err) => {
                console.error('[OS][sync] falha ao atualizar status da work section', section.id, err);
                return of(section);
              })
            );
        }),
        catchError((err) => {
          console.error('[OS][sync] falha ao consultar solicitação de estoque', stockRequestId, err);
          return of(section);
        })
      );
    });

    return forkJoin(syncRequests).pipe(
      map((syncedLinked) => {
        const syncedById = new Map(syncedLinked.map((item) => [item.id, item]));
        return sections.map((section) => syncedById.get(section.id) ?? section);
      })
    );
  }

  private mapLogsToHistorico(logs: ServiceOrderLog[]): any[] {
    return [...logs]
      .sort((left, right) => {
        const leftTime = new Date(left.createdAt || 0).getTime();
        const rightTime = new Date(right.createdAt || 0).getTime();
        return rightTime - leftTime;
      })
      .map((log) => ({
        tipo: this.resolveHistoricoTipo(log),
        acao: log.action || log.description || 'Movimentação da OS',
        usuarioNome: log.userName || 'Usuário Sistema',
        dataHora: log.createdAt || new Date().toISOString(),
        detalhes: log.details || log.description || '',
        statusAnterior: log.oldStatus || undefined,
        statusNovo: log.newStatus || undefined,
        setorAtual: log.department || undefined
      }));
  }

  private mapLogsToHistoricoResolvedUsers(logs: ServiceOrderLog[]): Observable<any[]> {
    return this.hydrateLogUserNames(logs).pipe(
      map((enriched) => this.mapLogsToHistorico(enriched))
    );
  }

  private hydrateLogUserNames(logs: ServiceOrderLog[]): Observable<ServiceOrderLog[]> {
    const missingUserIds = Array.from(
      new Set(
        (logs || [])
          .filter((log) => {
            const currentName = (log?.userName || '').toString().trim();
            return !currentName && log?.userId != null;
          })
          .map((log) => Number(log.userId))
          .filter((id) => Number.isFinite(id) && id > 0)
      )
    );

    if (missingUserIds.length === 0) {
      return of(logs);
    }

    return forkJoin(
      missingUserIds.map((userId) =>
        this.getUserNameByIdCached(userId).pipe(
          map((name) => ({ userId, name }))
        )
      )
    ).pipe(
      map((results) => {
        const idToName = new Map<number, string>();
        for (const result of results) {
          const normalized = (result?.name || '').toString().trim();
          if (normalized) {
            idToName.set(result.userId, normalized);
          }
        }

        return (logs || []).map((log) => {
          const currentName = (log?.userName || '').toString().trim();
          if (currentName) return log;

          const userId = log?.userId != null ? Number(log.userId) : null;
          if (!userId || !idToName.has(userId)) return log;

          return {
            ...log,
            userName: idToName.get(userId) || null
          };
        });
      })
    );
  }

  private getUserNameByIdCached(userId: number): Observable<string | null> {
    const normalizedUserId = Number(userId);
    if (!Number.isFinite(normalizedUserId) || normalizedUserId <= 0) {
      return of(null);
    }

    const cached = this.userNameCache.get(normalizedUserId);
    if (cached) {
      return of(cached);
    }

    const inFlight = this.userNameInFlight.get(normalizedUserId);
    if (inFlight) {
      return inFlight;
    }

    const request$ = this.usersService.getById(normalizedUserId).pipe(
      map((user: any) => {
        const name = (user?.name || user?.Name || '').toString().trim();
        return name || null;
      }),
      tap((name) => {
        if (name) {
          this.userNameCache.set(normalizedUserId, name);
        }
      }),
      catchError(() => of(null)),
      finalize(() => {
        this.userNameInFlight.delete(normalizedUserId);
      }),
      shareReplay(1)
    );

    this.userNameInFlight.set(normalizedUserId, request$);
    return request$;
  }

  private resolveHistoricoTipo(log: ServiceOrderLog): string {
    const content = `${log.action || ''} ${log.description || ''} ${log.details || ''}`.toLowerCase();

    if (log.oldStatus || log.newStatus || content.includes('status')) return 'mudanca_status';
    if (content.includes('direcion')) return 'direcionamento';
    if (content.includes('encerr') || content.includes('conclu')) return 'conclusao';
    if (content.includes('cancel')) return 'cancelamento';
    if (content.includes('edit') || content.includes('atualiz')) return 'edicao';
    if (content.includes('cria') || content.includes('abertura')) return 'criacao';

    return 'outros';
  }

  private mapWorkSectionsToSessions(sections: ServiceOrderWorkSection[]): SessaoTrabalhoView[] {
    const executionByProposalId = new Map<number, ServiceOrderWorkSection>();
    for (const section of sections) {
      const sourceId = Number(section.sourceBudgetWorkSectionId ?? (section as any).SourceBudgetWorkSectionId ?? 0);
      if (sourceId > 0) {
        executionByProposalId.set(sourceId, section);
      }
    }

    const visibleSections = sections.filter((section) => {
      const sourceId = Number(section.sourceBudgetWorkSectionId ?? (section as any).SourceBudgetWorkSectionId ?? 0);
      if (sourceId > 0) return true;

      const mirror = executionByProposalId.get(Number(section.id || 0));
      if (!mirror) return true;

      const mirrorLines = (mirror.services?.length ?? 0) + (mirror.items?.length ?? 0);
      return mirrorLines === 0;
    });

    return [...visibleSections]
      .sort((left, right) => {
        const leftTime = new Date(left.createdAt || 0).getTime();
        const rightTime = new Date(right.createdAt || 0).getTime();
        return rightTime - leftTime;
      })
      .map((section, index) => {
        const isExecutionMirror = Number(section.sourceBudgetWorkSectionId ?? (section as any).SourceBudgetWorkSectionId ?? 0) > 0;
        const servicos = (section.services || [])
          .filter((service) => this.isVisibleWorkSectionLine(service, isExecutionMirror))
          .map((service) => this.mapWorkSectionServiceToServico(service));
        const pecas = (section.items || [])
          .filter((item) => this.isVisibleWorkSectionLine(item, isExecutionMirror))
          .map((item) => this.mapWorkSectionItemToPeca(item));
        const valorTotal =
          servicos.reduce((total, servico) => total + this.valorLinhaCobranca(servico), 0) +
          pecas.reduce((total, peca) => total + this.valorLinhaCobranca(peca), 0);

        return {
          id: section.id,
          descricao: section.description,
          status: section.status ?? (section as any)?.Status ?? (section as any)?.status ?? null,
          dataSessao: section.createdAt || section.updatedAt || new Date().toISOString(),
          usuario: this.extractSectionUser(section),
          servicos,
          pecas,
          valorTotal: section.services?.length || section.items?.length ? valorTotal : 0
        };
      });
  }

  /** Na proposta de orçamento oculta linhas recusadas; na execução mostra tudo. */
  private isVisibleWorkSectionLine(
    line: { approvalStatus?: string | null },
    isExecutionMirror: boolean
  ): boolean {
    if (isExecutionMirror) return true;
    const status = (line.approvalStatus || '').trim().toLowerCase();
    return status !== 'recusado' && status !== 'reprovado' && status !== 'rejected';
  }

  getSessaoStatus(sessao: SessaoTrabalhoView): string {
    const fromSession = (sessao?.status ?? '').toString().trim();
    if (fromSession) return fromSession;

    // Fallback para compatibilidade com o comportamento antigo do front
    if (sessao?.aprovado === true) return 'Aprovado';
    return 'Pendente';
  }

  isSessaoCancelada(sessao: SessaoTrabalhoView): boolean {
    const status = (sessao?.status ?? '').toString().trim().toUpperCase();
    return status === 'CANCELADO' || status === 'CANCELADA';
  }

  isSessaoPendentePagamento(sessao: SessaoTrabalhoView): boolean {
    const status = (sessao?.status ?? '').toString().trim().toUpperCase();
    return status === 'PENDENTE PAGAMENTO' || status === 'PENDENTE_PAGAMENTO';
  }

  /** Indica se a seção está em revisão (cliente recusou o orçamento). */
  isSessaoEmRevisao(sessao: SessaoTrabalhoView): boolean {
    const normalized = (sessao?.status ?? '')
      .toString()
      .trim()
      .toUpperCase()
      .replace(/[-_]/g, ' ')
      .replace(/Ç/g, 'C')
      .replace(/Ã/g, 'A')
      .replace(/Õ/g, 'O')
      .replace(/Á/g, 'A')
      .replace(/É/g, 'E')
      .replace(/Í/g, 'I')
      .replace(/Ó/g, 'O')
      .replace(/Ú/g, 'U');
    return normalized.includes('ORCAMENTO EM REVISAO') || normalized === 'EM REVISAO';
  }

  /** True quando o usuário está editando uma seção em revisão. */
  isEditandoSessao(): boolean {
    return this.editingWorkSectionId !== null;
  }

  /**
   * Carrega os serviços/peças de uma seção em revisão para os arrays de
   * edição (servicos/pecas) e marca a seção como em edição. Ao salvar,
   * `salvarSessaoTrabalhoConfirmada()` chamará `atualizarSessaoEmRevisao()`.
   */
  editarSessaoEmRevisao(sessao: SessaoTrabalhoView, index: number): void {
    if (!sessao?.id) {
      this.openError('Seção sem identificador, não é possível editar.').subscribe();
      return;
    }
    if (!this.isSessaoEmRevisao(sessao)) {
      this.openWarning('Apenas orçamentos em revisão podem ser editados.').subscribe();
      return;
    }
    if (this.editingWorkSectionId && this.editingWorkSectionId !== sessao.id) {
      this.openWarning('Já existe outro orçamento em edição. Cancele a edição atual antes.').subscribe();
      return;
    }

    const hasUnsaved = this.servicos.length > 0 || this.pecas.length > 0;
    const message = hasUnsaved
      ? 'Os serviços/peças não salvos serão substituídos pelos itens deste orçamento. Deseja continuar?'
      : `Carregar os itens do Serviço #${index + 1} para edição?`;

    this.confirmationService
      .confirm({
        title: 'Editar orçamento em revisão',
        message,
        confirmText: 'Editar',
        cancelText: 'Cancelar',
        confirmButtonClass: 'btn-warning',
        cancelButtonClass: 'btn-secondary',
        type: 'warning'
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.servicos = (sessao.servicos || []).map((s) => ({ ...s }));
        this.pecas = (sessao.pecas || []).map((p) => ({ ...p }));
        this.editingWorkSectionId = sessao.id!;

        setTimeout(() => {
          const formSection = document.querySelector('.servicos-pecas-section, .servicos-section, .form-section');
          if (formSection) {
            formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 200);
      });
  }

  /** Cancela a edição em andamento e limpa os arrays de edição. */
  cancelarEdicaoSessao(): void {
    if (!this.isEditandoSessao()) return;
    this.confirmationService
      .confirm({
        title: 'Cancelar edição',
        message: 'Descartar as alterações e voltar ao estado anterior?',
        confirmText: 'Descartar',
        cancelText: 'Continuar editando',
        confirmButtonClass: 'btn-danger',
        cancelButtonClass: 'btn-secondary',
        type: 'warning'
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.editingWorkSectionId = null;
        this.limparServicosEPecas();
      });
  }

  private extractSectionUser(section: ServiceOrderWorkSection): string {
    const rawSection = section as any;
    return rawSection?.createdBy
      ?? rawSection?.CreatedBy
      ?? rawSection?.userName
      ?? rawSection?.UserName
      ?? rawSection?.usuarioNome
      ?? rawSection?.UsuarioNome
      ?? 'Usuário Sistema';
  }

  private mapWorkSectionServiceToServico(service: ServiceOrderWorkSectionService): ServicoOS {
    const maintenanceKind = this.normalizeMaintenanceKind(service.maintenanceKind);
    const valorTotal = Number(service.totalValue || 0);
    const isGuincho = !!service.isGuincho;
    const providerName = String(service.externalServiceProviderName || '').trim() || null;
    return {
      id: service.id,
      descricao: this.formatGuinchoServiceDescription(service.description, isGuincho, providerName),
      externalServiceProviderId: service.externalServiceProviderId ?? null,
      externalServiceProviderName: providerName,
      isGuincho,
      quantidade: service.quantity,
      valorUnitario: service.unitValue,
      valorTotal,
      maintenanceKind,
      chargePercent: service.chargePercent ?? null,
      // Orçamentos antigos vinham sem HasFault: corretiva cobrada conta como avaria.
      isAvaria: this.resolveLinhaAvaria(service.id, service.hasFault, maintenanceKind, valorTotal),
      statusAvaria: this.normalizePaymentStatus(service.paymentStatus),
      usuarioAdicionou: 'Usuário Sistema',
      dataAdicao: service.createdAt || new Date().toISOString(),
      status: 'Pendente'
    };
  }

  /** Exibe "Guincho — Fornecedor X" na OS e na impressão. */
  private formatGuinchoServiceDescription(
    description: string | null | undefined,
    isGuincho: boolean,
    providerName: string | null
  ): string {
    const raw = String(description || '').trim();
    if (!isGuincho) {
      return raw;
    }
    if (providerName) {
      if (raw.toLowerCase().includes('guincho')) {
        return raw;
      }
      return `Guincho — ${providerName}`;
    }
    return raw || 'Guincho';
  }

  private mapWorkSectionItemToPeca(item: ServiceOrderWorkSectionItem): any {
    const maintenanceKind = this.normalizeMaintenanceKind(item.maintenanceKind);
    const valorTotal = Number(item.totalValue || 0);
    return {
      id: item.id,
      descricao: item.description,
      quantidade: item.quantity,
      valorUnitario: item.unitValue,
      valorTotal,
      maintenanceKind,
      chargePercent: item.chargePercent ?? null,
      isAvaria: this.resolveLinhaAvaria(item.id, item.hasFault, maintenanceKind, valorTotal),
      statusAvaria: this.normalizePaymentStatus(item.paymentStatus),
      retiradaEstoque: item.withdrawnFromStock,
      emEstoque: item.inStock
    };
  }

  /**
   * Na OS impressa/histórico, só avaria cobra: o restante entra com total R$ 0,00.
   */
  valorLinhaCobranca(line: { isAvaria?: boolean; valorTotal?: number } | null | undefined): number {
    if (!line?.isAvaria) return 0;
    return Number(line.valorTotal || 0);
  }

  /**
   * HasFault no banco; se o orçamento veio sem a flag, corretiva com valor > 0 é avaria.
   * Linhas desmarcadas manualmente nesta tela ficam fora até recarregar a OS.
   */
  private resolveLinhaAvaria(
    lineId: number | null | undefined,
    hasFault: boolean | null | undefined,
    maintenanceKind: string,
    valorTotal: number
  ): boolean {
    const id = Number(lineId || 0);
    if (id > 0 && this.avariaDesmarcadaIds.has(id)) return false;
    if (hasFault) return true;
    return maintenanceKind === 'Corretiva' && valorTotal > 0;
  }

  private normalizeMaintenanceKind(value: string | null | undefined): 'Preventiva' | 'Corretiva' {
    return String(value || '').toLowerCase().startsWith('prevent') ? 'Preventiva' : 'Corretiva';
  }

  /** Total de cobrança da linha (serviço ou peça): preventiva = 0; corretiva com %. */
  private computeLineChargeTotal(
    quantity: number,
    unitValue: number,
    maintenanceKind?: string | null,
    chargePercent?: number | null
  ): number {
    if (this.normalizeMaintenanceKind(maintenanceKind) === 'Preventiva') {
      return 0;
    }
    const qty = Number(quantity || 0);
    const unit = Number(unitValue || 0);
    const percent = this.canViewChargePercent ? Number(chargePercent || 0) : 0;
    const factor = 1 + (percent > 0 ? percent / 100 : 0);
    return Math.round(qty * unit * factor * 100) / 100;
  }

  private normalizePaymentStatus(value: string | null | undefined): 'Pendente' | 'Pago' {
    const normalized = (value || '').toString().trim().toLowerCase();
    if (normalized === 'pago' || normalized === 'paid') return 'Pago';
    return 'Pendente';
  }

  formatDateForInput(date: any): string {
    if (!date) return '';
    if (typeof date === 'string') {
      const datePart = date.trim().split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
    }
    const parsed = parseApiDateTime(String(date));
    if (!parsed) return '';
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(parsed);
    const get = (t: string) => parts.find(p => p.type === t)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
  }

  private todayBrazilDateInput(): string {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date());
    const get = (t: string) => parts.find(p => p.type === t)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
  }

  formatDateForDisplay(date: any): string {
    if (!date) return '';
    const parsedDate = new Date(date);
    return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toLocaleDateString('pt-BR');
  }

  private formatMileageForDisplay(value: number | null | undefined): string {
    return value === null || value === undefined
      ? ''
      : Number(value).toLocaleString('pt-BR');
  }

  private formatBooleanForDisplay(value: boolean | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }

    return value ? 'Sim' : 'Não';
  }

  private buildVehicleDisplayLabel(plate?: string | null, brand?: string | null, model?: string | null): string {
    const description = [brand, model].filter(Boolean).join(' ').trim();
    if (plate && description) {
      return `${plate} - ${description}`;
    }

    return plate || description || '';
  }

  private mapVehicleToSelection(vehicle: Vehicle): VehicleSelection {
    return {
      id: vehicle.id,
      placa: vehicle.plateNumber,
      marca: vehicle.brand,
      modelo: vehicle.model,
      ano: vehicle.modelYear,
      cor: vehicle.color ?? 'N/A',
      chassi: vehicle.chassisNumber
    };
  }

  private clearLinkedVehicleDetails(resetMainVehicle: boolean = false): void {
    const values: any = {
      veiculoMarca: '',
      veiculoModelo: '',
      veiculoAnoModelo: '',
      veiculoStatusDetalhes: '',
      veiculoQuilometragemAtual: '',
      veiculoUltimaManutencaoData: '',
      veiculoUltimaManutencaoKm: '',
      veiculoProximaManutencaoData: '',
      veiculoRequerManutencao: '',
      veiculoObservacoes: ''
    };

    if (resetMainVehicle) {
      values.veiculoId = '';
      values.veiculoPlaca = '';
      values.km = '';
      this.selectedVehicle = null;
    }

    this.serviceOrderForm.patchValue(values);
  }

  private applyLinkedVehicleDetails(vehicle: Vehicle): void {
    this.selectedVehicle = this.mapVehicleToSelection(vehicle);
    const computedLabel = this.buildVehicleDisplayLabel(vehicle.plateNumber, vehicle.brand, vehicle.model);
    const existingLabel = this.serviceOrderForm.get('veiculoPlaca')?.value;
    const existingVehicleId = this.toIntOrNull(this.serviceOrderForm.get('veiculoId')?.value) ?? 0;
    this.serviceOrderForm.patchValue({
      veiculoId: vehicle.id || existingVehicleId,
      veiculoPlaca: computedLabel || existingLabel || '',
      km: vehicle.currentMileage ?? '',
      veiculoMarca: vehicle.brand ?? '',
      veiculoModelo: vehicle.model ?? '',
      veiculoAnoModelo: vehicle.modelYear ? String(vehicle.modelYear) : '',
      veiculoStatusDetalhes: vehicle.status ?? '',
      veiculoQuilometragemAtual: this.formatMileageForDisplay(vehicle.currentMileage),
      veiculoUltimaManutencaoData: this.formatDateForDisplay(vehicle.lastMaintenanceDate),
      veiculoUltimaManutencaoKm: this.formatMileageForDisplay(vehicle.lastMaintenanceMileage),
      veiculoProximaManutencaoData: this.formatDateForDisplay(vehicle.nextMaintenanceDate),
      veiculoRequerManutencao: this.formatBooleanForDisplay(vehicle.requiresMaintenance),
      veiculoObservacoes: vehicle.notes ?? ''
    });
  }

  private loadLinkedVehicleDetails(vehicleId: number | null | undefined): void {
    if (!vehicleId) {
      this.clearLinkedVehicleDetails(true);
      return;
    }

    this.vehiclesService.getById(vehicleId).pipe(take(1)).subscribe({
      next: (vehicle: Vehicle) => this.applyLinkedVehicleDetails(vehicle),
      error: (error: any) => {
        console.error('Erro ao carregar detalhes do veículo vinculado:', error);
        this.clearLinkedVehicleDetails(false);
      }
    });
  }

  private loadPreventiveHistory(vehicleId: number): void {
    this.loadingPreventiveHistory = true;
    this.preventiveHistory = [];
    this.vehiclePreventiveHistoryService.getSummaryByVehicle(vehicleId).pipe(take(1)).subscribe({
      next: (summary) => {
        this.preventiveHistory = summary.history;
        this.loadingPreventiveHistory = false;
      },
      error: () => {
        this.preventiveHistory = [];
        this.loadingPreventiveHistory = false;
      }
    });
  }

  private toIntOrNull(value: any): number | null {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  private toIsoDateTime(dateValue: string | null | undefined, timeValue?: string | null): string | null {
    if (!dateValue) return null;

    // Abertura/fechamento: data + hora reais.
    if (timeValue != null) {
      const combined = combineBrazilDateAndTime(dateValue, timeValue);
      if (combined) return combined;
    }

    if (dateValue.includes('T') && !/T00:00:00/.test(dateValue.split('.')[0])) {
      return dateValue.replace(/Z$/i, '').replace(/[+-]\d{2}:\d{2}$/, '');
    }

    // Campos só-data (entrada, prazo etc.): meia-noite BR sem Z.
    const datePart = dateValue.trim().split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return `${datePart}T00:00:00`;
    }
    return null;
  }

  /**
   * Com hora escolhida no encerramento, envia data + hora e o backend respeita, inclusive
   * retroativa. Sem hora, envia só a data: aí o backend resolve o instante, e o snapshot de
   * alterações não salvas não muda a cada chamada.
   */
  private resolveClosingIso(dateValue: string | null | undefined): string | null {
    const date = (dateValue || '').toString().trim().split('T')[0];
    if (!date) return null;

    const time = (this.serviceOrderForm.get('horaConclusao')?.value || '').toString().trim();
    return time ? combineBrazilDateAndTime(date, time) : date;
  }

  private toOpeningClosingIso(dateValue: string | null | undefined, timeValue?: string | null): string | null {
    if (!dateValue) return null;
    const time = (timeValue || '').trim();
    if (time && time !== '00:00') {
      return combineBrazilDateAndTime(dateValue, time);
    }
    // Sem hora útil → instante atual de Brasília.
    return toBrazilWallClockString(new Date());
  }

  private parsePositiveNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private extractDepartmentIdFromProfile(profile: any): number | null {
    return this.parsePositiveNumber(
      profile?.DepartmentId
      ?? profile?.departmentId
      ?? profile?.UnidadeId
      ?? profile?.unidadeId
      ?? profile?.Staff?.DepartmentId
      ?? profile?.Staff?.departmentId
      ?? profile?.Staff?.Staff?.DepartmentId
      ?? profile?.Staff?.Staff?.departmentId
      ?? profile?.staff?.DepartmentId
      ?? profile?.staff?.departmentId
      ?? profile?.staff?.staff?.DepartmentId
      ?? profile?.staff?.staff?.departmentId
    );
  }

  private extractStaffIdFromProfile(profile: any): number | null {
    return this.parsePositiveNumber(
      profile?.StaffId
      ?? profile?.staffId
      ?? profile?.Staff?.Id
      ?? profile?.Staff?.StaffId
      ?? profile?.Staff?.Staff?.Id
      ?? profile?.Staff?.Staff?.StaffId
      ?? profile?.staff?.Id
      ?? profile?.staff?.StaffId
      ?? profile?.staff?.staff?.Id
      ?? profile?.staff?.staff?.StaffId
      ?? (this.authService.currentUserValue as any)?.staffId
      ?? (this.authService.currentUserValue as any)?.StaffId
    );
  }

  private resolveCurrentUserDepartmentId() {
    const tokenDepartmentId = this.authService.getDepartmentIdFromToken();
    if (tokenDepartmentId) {
      return of(tokenDepartmentId);
    }

    const userId = this.parsePositiveNumber(this.authService.getUserIdFromToken());
    if (!userId) {
      return of(null);
    }

    return this.usersService.getUserById(userId).pipe(
      switchMap((profile) => {
        const departmentId = this.extractDepartmentIdFromProfile(profile);
        if (departmentId) {
          return of(departmentId);
        }

        const staffId = this.extractStaffIdFromProfile(profile);
        if (!staffId || !this.usersService.staffService) {
          return of(null);
        }

        return this.usersService.staffService.getStaffById(staffId).pipe(
          map((staff) => this.extractDepartmentIdFromProfile(staff)),
          catchError((error) => {
            console.error('Erro ao buscar staff do usuário logado:', error);
            return of(null);
          })
        );
      }),
      catchError((error) => {
        console.error('Erro ao buscar perfil do usuário logado:', error);
        return of(null);
      })
    );
  }

  private buildServiceOrderPayload(departmentId: number): any {
    const rawOpeningDate = this.serviceOrderForm.get('dataAbertura')?.value as string;
    const rawOpeningTime = this.serviceOrderForm.get('horaAbertura')?.value as string;
    const rawClosingDate = this.serviceOrderForm.get('dataConclusao')?.value as string;
    const motivo = (this.serviceOrderForm.get('motivoAbertura')?.value || '').toString().trim();

    const payload: any = {
      openingDate: this.toOpeningClosingIso(rawOpeningDate, rawOpeningTime) ?? toBrazilWallClockString(new Date()),
      closingDate: this.resolveClosingIso(rawClosingDate),
      orderNumber: this.serviceOrderForm.get('numeroOS')?.value || null,
      description: this.serviceOrderForm.get('descricao')?.value || null,
      typeOS: this.serviceOrderForm.get('tipoOS')?.value || null,
      // Enviado em create e update (incl. direcionamento). Se a listagem voltar vazio após PUT,
      // verificar persistência de OpeningReason no microserviço order.
      openingReason: motivo || null,
      configOrderId: this.configOrder?.id ?? null,
      departmentId: departmentId,
      DepartmentId: departmentId,
      vehicleId: this.toIntOrNull(this.serviceOrderForm.get('veiculoId')?.value),
      additionalVehicleId: this.toIntOrNull(this.serviceOrderForm.get('veiculoAdicionalId')?.value),
      customerId: this.toIntOrNull(this.serviceOrderForm.get('clienteId')?.value),
      staffId: this.toIntOrNull(this.serviceOrderForm.get('mecanicoId')?.value),
      stockId: this.toIntOrNull(this.serviceOrderForm.get('almoxarifadoId')?.value),
      status: this.serviceOrderForm.get('status')?.value || 'Aberta'
    };

    // Campos B2B opcionais — só envia se preenchidos (Order preserva no update se omitidos)
    const b2bContractId = this.toIntOrNull(this.serviceOrderForm.get('b2bContractId')?.value);
    const b2bContractItemId = this.toIntOrNull(this.serviceOrderForm.get('b2bContractItemId')?.value);
    const fuelLevel = (this.serviceOrderForm.get('fuelLevel')?.value || '').toString().trim();
    const openingMileage =
      this.toIntOrNull(this.serviceOrderForm.get('openingMileage')?.value)
      ?? this.toIntOrNull(this.serviceOrderForm.get('km')?.value);
    const entryDate = this.toIsoDateTime(this.serviceOrderForm.get('entryDate')?.value as string);
    const startWorkDate = this.toIsoDateTime(this.serviceOrderForm.get('startWorkDate')?.value as string);
    const deadlineDate = this.toIsoDateTime(this.serviceOrderForm.get('deadlineDate')?.value as string);
    const endWorkDate = this.toIsoDateTime(this.serviceOrderForm.get('endWorkDate')?.value as string);
    const pickupDate = this.toIsoDateTime(this.serviceOrderForm.get('pickupDate')?.value as string);
    const isScheduled = !!this.serviceOrderForm.get('isScheduled')?.value;

    if (b2bContractId) payload.b2bContractId = b2bContractId;
    if (b2bContractItemId) payload.b2bContractItemId = b2bContractItemId;
    if (fuelLevel) payload.fuelLevel = fuelLevel;
    if (openingMileage != null) payload.openingMileage = openingMileage;
    if (entryDate) payload.entryDate = entryDate;
    if (startWorkDate) payload.startWorkDate = startWorkDate;
    if (deadlineDate) payload.deadlineDate = deadlineDate;
    if (endWorkDate) payload.endWorkDate = endWorkDate;
    if (pickupDate) payload.pickupDate = pickupDate;
    if (isScheduled) payload.isScheduled = true;

    return payload;
  }

  private captureOsPersistedState(): Record<string, unknown> {
    const rawOpeningDate = this.serviceOrderForm.get('dataAbertura')?.value as string;
    const rawOpeningTime = this.serviceOrderForm.get('horaAbertura')?.value as string;
    const rawClosingDate = this.serviceOrderForm.get('dataConclusao')?.value as string;
    const rawPrevista = this.serviceOrderForm.get('dataPrevistaConclusao')?.value as string;
    const motivo = (this.serviceOrderForm.get('motivoAbertura')?.value || '').toString().trim();

    return {
      openingDate: this.toOpeningClosingIso(rawOpeningDate, rawOpeningTime),
      closingDate: this.resolveClosingIso(rawClosingDate),
      dataPrevistaConclusao: this.toIsoDateTime(rawPrevista),
      description: (this.serviceOrderForm.get('descricao')?.value || '').toString().trim() || null,
      typeOS: this.serviceOrderForm.get('tipoOS')?.value || null,
      openingReason: motivo || null,
      configOrderId: this.configOrder?.id ?? null,
      vehicleId: this.toIntOrNull(this.serviceOrderForm.get('veiculoId')?.value),
      additionalVehicleId: this.toIntOrNull(this.serviceOrderForm.get('veiculoAdicionalId')?.value),
      customerId: this.toIntOrNull(this.serviceOrderForm.get('clienteId')?.value),
      staffId: this.toIntOrNull(this.serviceOrderForm.get('mecanicoId')?.value),
      stockId: this.toIntOrNull(this.serviceOrderForm.get('almoxarifadoId')?.value),
      stockRequestPriority: this.serviceOrderForm.get('stockRequestPriority')?.value || 'Normal',
      status: this.serviceOrderForm.get('status')?.value || 'Aberta'
    };
  }

  private captureOsPersistedStateSnapshot(): string {
    return JSON.stringify(this.captureOsPersistedState());
  }

  private resetOsStateSnapshot(): void {
    this.savedOsStateSnapshot = this.captureOsPersistedStateSnapshot();
  }

  hasUnsavedOsChanges(): boolean {
    if (this.bypassUnsavedGuard || this.isViewMode) {
      return false;
    }
    if (!this.osGerada && !this.serviceOrderId) {
      return false;
    }
    if (this.savedOsStateSnapshot === null) {
      this.resetOsStateSnapshot();
    }
    return this.savedOsStateSnapshot !== this.captureOsPersistedStateSnapshot();
  }

  canDeactivate(): Observable<boolean> {
    return this.confirmLeave();
  }

  confirmLeave(): Observable<boolean> {
    if (this.bypassUnsavedGuard || !this.hasUnsavedOsChanges()) {
      return of(true);
    }

    return this.unsavedChangesService.prompt({
      message: 'Existem alterações na ordem de serviço que ainda não foram salvas. Deseja salvar antes de sair?'
    }).pipe(
      take(1),
      switchMap((action) => {
        if (action === 'stay') {
          return of(false);
        }
        if (action === 'discard') {
          this.bypassUnsavedGuard = true;
          return of(true);
        }
        return this.saveBeforeLeave();
      })
    );
  }

  private saveBeforeLeave(): Observable<boolean> {
    if (!this.osGerada && !this.serviceOrderId) {
      this.openWarning('Clique em "Gerar OS" para criar a ordem de serviço antes de salvar alterações.').subscribe();
      return of(false);
    }

    if (this.serviceOrderForm.invalid) {
      Object.keys(this.serviceOrderForm.controls).forEach(key => {
        this.serviceOrderForm.get(key)?.markAsTouched();
      });
      return of(false);
    }

    return this.resolveCurrentUserDepartmentId().pipe(
      take(1),
      switchMap((departmentId) => {
        if (!departmentId) {
          this.openError('Não foi possível identificar o departamento do usuário logado. Verifique se o usuário possui Staff vinculado com DepartmentId.').subscribe();
          return of(false);
        }
        return this.submitServiceOrderForLeave(departmentId);
      })
    );
  }

  private submitServiceOrderForLeave(departmentId: number): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.currentUserDepartmentId = departmentId;
      this.submitting = true;
      const payload = this.buildServiceOrderPayload(departmentId);

      const handleSuccess = (order: ServiceOrder) => {
        if (!this.serviceOrderId && order.id) {
          this.serviceOrderId = order.id;
          this.isEditMode = true;
        }
        this.applyServiceOrderData(order);
        this.bypassUnsavedGuard = true;
        this.submitting = false;
        observer.next(true);
        observer.complete();
      };

      const handleError = (error: unknown, message: string) => {
        console.error(message, error);
        this.openError(message).subscribe();
        this.submitting = false;
        observer.next(false);
        observer.complete();
      };

      if (this.serviceOrderId) {
        this.serviceOrdersService.update(this.serviceOrderId, payload).subscribe({
          next: handleSuccess,
          error: (err) => handleError(err, 'Erro ao atualizar ordem de serviço.')
        });
      } else {
        this.serviceOrdersService.create(payload).subscribe({
          next: handleSuccess,
          error: (err) => handleError(err, 'Erro ao criar ordem de serviço.')
        });
      }
    });
  }

  private submitServiceOrder(departmentId: number): void {
    this.currentUserDepartmentId = departmentId;
    this.submitting = true;

    const payload = this.buildServiceOrderPayload(departmentId);
    const successMessage = 'Ordem de serviço atualizada com sucesso!';

    const onSuccess = (order: ServiceOrder) => {
      const createdId = order.id;
      if (!this.serviceOrderId && createdId) {
        this.serviceOrderId = createdId;
        this.isEditMode = true;
        this.router.navigate(['/service-orders', createdId, 'edit'], { replaceUrl: true });
      }
      this.refreshServiceOrderPage(successMessage);
    };

    if (this.serviceOrderId) {
      this.serviceOrdersService.update(this.serviceOrderId, payload).subscribe({
        next: (updatedOrder) => onSuccess(updatedOrder),
        error: (error) => {
          console.error('Erro ao atualizar ordem de serviço:', error);
          this.openError('Erro ao atualizar ordem de serviço.').subscribe();
          this.submitting = false;
        }
      });
    } else {
      this.serviceOrdersService.create(payload).subscribe({
        next: (createdOrder) => onSuccess(createdOrder),
        error: (error) => {
          console.error('Erro ao criar ordem de serviço:', error);
          this.openError('Erro ao criar ordem de serviço.').subscribe();
          this.submitting = false;
        }
      });
    }
  }

  onSubmit(): void {
    if (!this.osGerada && !this.serviceOrderId) {
      this.openWarning('Clique em "Gerar OS" para criar a ordem de serviço antes de salvar alterações.').subscribe();
      return;
    }

    if (this.serviceOrderForm.invalid) {
      Object.keys(this.serviceOrderForm.controls).forEach(key => {
        this.serviceOrderForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.resolveCurrentUserDepartmentId()
      .pipe(take(1))
      .subscribe((departmentId) => {
        if (!departmentId) {
          this.openError('Não foi possível identificar o departamento do usuário logado. Verifique se o usuário possui Staff vinculado com DepartmentId.').subscribe();
          return;
        }

        this.submitServiceOrder(departmentId);
      });
  }

  onCancel(): void {
    this.confirmLeave()
      .pipe(take(1))
      .subscribe((canLeave) => {
        if (canLeave) {
          this.router.navigate(['/service-orders']);
        }
      });
  }

  enableEdit(): void {
    this.isViewMode = false;
    this.isEditMode = true;
    this.serviceOrderForm.enable();
    this.router.navigate(['/service-orders', this.serviceOrderId, 'edit']);
  }

  getFormTitle(): string {
    if (this.isViewMode) return 'Visualizar Ordem de Serviço';
    if (this.isEditMode) return 'Editar Ordem de Serviço';
    return 'Nova Ordem de Serviço';
  }

  /**
   * Abre o modal de seleção de cliente
   */
  openCustomerModal(): void {
    if (!this.isViewMode && this.configOrder?.canClient !== false) {
      this.customerModal.open();
    }
  }

  /**
   * Abre o modal de seleção de veículo
   */
  openVehicleModal(): void {
    if (!this.isViewMode && this.configOrder?.canVehicle !== false) {
      this.vehicleModal.open();
    }
  }

  /**
   * Abre o modal de seleção de almoxarifado
   */
  openWarehouseModal(): void {
    if (!this.isViewMode && this.configOrder?.canStock !== false) {
      this.warehouseModal.open();
    }
  }

  // ─── ConfigOrder permission helpers ───────────────────────────────────────

  canVehicleSection(): boolean {
    return this.configOrder?.canVehicle !== false;
  }

  canAditionalVehicleSection(): boolean {
    return this.configOrder?.canAditionalVehicle !== false;
  }

  canClientSection(): boolean {
    return this.configOrder?.canClient !== false;
  }

  canStockSection(): boolean {
    // Peças entram pelo orçamento. Estoque fica oculto na OS até existir baixa real.
    return false;
  }

  canServicesSection(): boolean {
    return this.configOrder?.canServices !== false;
  }

  canCloseSection(): boolean {
    return this.configOrder?.canClose !== false;
  }

  isTransactionOS(): boolean {
    return this.configOrder?.transaction === true;
  }

  isThirdPartyOS(): boolean {
    if (this.selectedOsTypeConfig?.isThirdParty != null) {
      return this.selectedOsTypeConfig.isThirdParty === true;
    }
    if (this.configOrder?.isThirdParty != null) {
      return this.configOrder.isThirdParty === true;
    }
    return false;
  }

  /** Exibe o campo "% a mais" (acréscimo) nas linhas de serviço/peça. */
  get canViewChargePercent(): boolean {
    return true;
  }

  openProviderModal(): void {
    if (!this.isThirdPartyOS()) return;
    if (this.providerModal) {
      this.providerModal.open();
    }
  }

  onProviderSelected(provider: ProviderSelection): void {
    if (!this.isThirdPartyOS()) return;
    this.selectedProvider = provider;

    // Propaga o prestador para os serviços já adicionados na sessão atual
    const providerId = provider?.id ?? null;
    this.servicos = this.servicos.map((servico) => ({
      ...servico,
      externalServiceProviderId: providerId
    }));
  }

  removerPrestador(): void {
    this.selectedProvider = null;

    // Remove associação do prestador dos serviços em edição
    this.servicos = this.servicos.map((servico) => ({
      ...servico,
      externalServiceProviderId: null
    }));
  }

  /**
   * Inicializa dados mockados
   */
  initializeMockData(): void {
    // Lista de estoques mockados
    this.stocksList = [
      { id: 1, name: 'Estoque Principal', warehouseId: 1, warehouseName: 'Almoxarifado Central' },
      { id: 2, name: 'Estoque Peças', warehouseId: 2, warehouseName: 'Almoxarifado Peças' },
      { id: 3, name: 'Estoque Ferramentas', warehouseId: 3, warehouseName: 'Almoxarifado Ferramentas' },
      { id: 4, name: 'Estoque Químicos', warehouseId: 4, warehouseName: 'Almoxarifado Químicos' },
      { id: 5, name: 'Estoque Emergência', warehouseId: 5, warehouseName: 'Almoxarifado Emergência' }
    ];

    // Lista de almoxarifados mockados
    this.warehousesList = [
      { id: 1, nome: 'Almoxarifado Central', responsavel: 'João Silva' },
      { id: 2, nome: 'Almoxarifado Peças', responsavel: 'Maria Santos' },
      { id: 3, nome: 'Almoxarifado Ferramentas', responsavel: 'Pedro Oliveira' },
      { id: 4, nome: 'Almoxarifado Químicos', responsavel: 'Ana Costa' },
      { id: 5, nome: 'Almoxarifado Emergência', responsavel: 'Carlos Mendes' }
    ];

  }

  carregarStocks(): void {
    this.loadingStocks = true;
    this.stockService.getActive({ page: 1, pageSize: 1000 }).subscribe({
      next: (result) => {
        const items = result.items || [];
        this.stocksList = items.map((s) => ({
          id: s.id,
          name: s.name,
          warehouseId: s.warehouseId,
          warehouseName: s.warehouseName
        }));
        this.loadingStocks = false;
      },
      error: (error) => {
        console.error('Erro ao carregar estoques:', error);
        this.loadingStocks = false;
      }
    });
  }

  /**
   * Carrega todos os serviços da API
   */
  carregarServicos(): void {
    this.loadingServicos = true;
    // Carregar todos os serviços ativos (usando pageSize grande para obter todos)
    this.serviceTypeService.getActive({ page: 1, pageSize: 1000 }).subscribe({
      next: (result) => {
        this.tiposServicos = result.items || [];
        this.loadingServicos = false;
      },
      error: (error) => {
        console.error('Erro ao carregar serviços:', error);
        this.loadingServicos = false;
        // Fallback para lista vazia caso haja erro
        this.tiposServicos = [];
      }
    });
  }

  /**
   * Carrega todos os itens disponíveis da API
   */
  carregarItemsByStock(stockId: number): void {
    this.loadingItems = true;
    this.itemService.getItemsByStock(stockId, { page: 1, pageSize: 1000 }).subscribe({
      next: (result) => {
        this.itemsDisponiveis = result.items || [];
        this.loadingItems = false;
      },
      error: (error) => {
        console.error('Erro ao carregar itens por estoque:', error);
        this.loadingItems = false;
        this.itemsDisponiveis = [];
      }
    });
  }

  /**
   * Carrega todos os itens ativos (sem dependência de estoque selecionado).
   * Cada Item retornado já contém stockId/warehouseId, que são usados ao montar
   * o payload de `service-order-stock-requests` por linha de peça.
   */
  carregarItemsAtivos(): void {
    this.loadingItems = true;
    this.itemService.getActiveItems({ page: 1, pageSize: 1000 }).subscribe({
      next: (result) => {
        this.itemsDisponiveis = result.items || [];
        this.loadingItems = false;
      },
      error: (error) => {
        console.error('Erro ao carregar itens ativos:', error);
        this.loadingItems = false;
        this.itemsDisponiveis = [];
      }
    });
  }

  /**
   * Callback quando um cliente é selecionado
   */
  onCustomerSelected(customer: CustomerSelection): void {
    this.serviceOrderForm.patchValue({
      clienteId: customer.id,
      clienteNome: customer.nome
    });

    // Carregar detalhes do cliente (documentNumber, customerCategory)
    this.loadCustomerDetails(customer.id);

    // Carregar contrato do cliente e vincular veículo automaticamente
    this.loadContractByCustomer(customer.id);
  }

  /**
   * Callback quando um veículo é selecionado
   */
  onVehicleSelected(vehicle: VehicleSelection): void {
    this.selectedVehicle = vehicle;
    this.serviceOrderForm.patchValue({
      veiculoId: vehicle.id,
      veiculoPlaca: `${vehicle.placa} - ${vehicle.marca} ${vehicle.modelo}`
    });

    this.loadLinkedVehicleDetails(vehicle.id);
    this.loadPreventiveHistory(vehicle.id);

    // Verificar se há contrato atrelado ao veículo e auto-linkar cliente
    this.loadContractByVehicle(vehicle.id);
  }

  /**
   * Abre o modal de seleção de veículo adicional
   */
  openVehicleAdditionalModal(): void {
    if (!this.isViewMode && this.configOrder?.canAditionalVehicle !== false) {
      this.vehicleAdditionalModal.open();
    }
  }

  /**
   * Callback quando um veículo adicional é selecionado
   */
  onVehicleAdditionalSelected(vehicle: VehicleSelection): void {
    this.selectedVehicleAdditional = vehicle;
    this.serviceOrderForm.patchValue({
      veiculoAdicionalId: vehicle.id,
      veiculoAdicionalPlaca: `${vehicle.placa} - ${vehicle.marca} ${vehicle.modelo}`
    });
  }

  /**
   * Remove o veículo adicional selecionado
   */
  removerVeiculoAdicional(): void {
    this.selectedVehicleAdditional = null;
    this.serviceOrderForm.patchValue({
      veiculoAdicionalId: '',
      veiculoAdicionalPlaca: ''
    });
  }

  // ===== Auto-link: Cliente ↔ Veículo via Contrato =====

  /**
   * Carrega detalhes adicionais do cliente (documento, categoria)
   */
  private loadCustomerDetails(customerId: number): void {
    this.customersService.getById(customerId).pipe(take(1)).subscribe({
      next: (customer: Customer) => {
        this.serviceOrderForm.patchValue({
          clienteDocumento: customer.documentNumber ?? '',
          clienteCategoria: customer.customerCategory ?? ''
        });
      },
      error: (err) => console.error('Erro ao carregar detalhes do cliente:', err)
    });
  }

  /**
   * Carrega contrato ativo do cliente e aplica dados na OS.
   * Se encontrar contrato com veículo, propõe auto-link.
   */
  private loadContractByCustomer(customerId: number): void {
    this.contractsService.getContractsByCustomerId(customerId).pipe(take(1)).subscribe({
      next: (contracts: Contract[]) => {
        // Buscar contrato ativo/assinado (prioridade: Active > Signed > Pending)
        const activeContract = this.findBestContract(contracts);
        if (!activeContract) {
          this.clearContractFields();
          return;
        }

        this.applyContractToForm(activeContract);

        // Se já tem veículo na OS, não propõe substituição
        const currentVehicleId = this.serviceOrderForm.get('veiculoId')?.value;
        if (currentVehicleId) return;
        console.log('Contrato encontrado para cliente:', activeContract);
        // Se contrato tem veículo, propor auto-link
        if (activeContract.vehicleId) {
          this.confirmAutoLinkVehicleFromContract(activeContract);
        }
      },
      error: (err) => {
        console.error('Erro ao carregar contrato do cliente:', err);
        this.clearContractFields();
      }
    });
  }

  /**
   * Busca contrato por veículo via searchContracts e propõe auto-link de cliente.
   */
  private loadContractByVehicle(vehicleId: number): void {
    this.contractsService.searchContracts({ vehicleId }).pipe(take(1)).subscribe({
      next: (contracts: Contract[]) => {
        const activeContract = this.findBestContract(contracts);
        if (!activeContract) {
          this.clearContractFields();
          return;
        }

        this.applyContractToForm(activeContract);

        // Se já tem cliente na OS, não propõe substituição
        const currentClienteId = this.serviceOrderForm.get('clienteId')?.value;
        if (currentClienteId) return;

        // Se contrato tem cliente, propor auto-link
        if (activeContract.customerId) {
          this.confirmAutoLinkCustomerFromContract(activeContract);
        }
      },
      error: (err) => {
        console.error('Erro ao buscar contrato por veículo:', err);
        this.clearContractFields();
      }
    });
  }

  /**
   * Retorna o melhor contrato da lista (Vigente/Active > Signed > Renovado > Pending/Draft)
   * Suporta status em PT e EN retornados pela API.
   */
  private findBestContract(contracts: Contract[]): Contract | null {
    if (!contracts || contracts.length === 0) return null;
    const priority: Record<string, number> = {
      'Vigente': 1, 'Active': 1,
      'Signed': 2, 'Assinado': 2,
      'Renovado': 3, 'Renewed': 3,
      'Pending': 4, 'Pendente': 4,
      'Draft': 5, 'Rascunho': 5
    };
    const allowedStatuses = new Set(Object.keys(priority));
    const sorted = contracts
      .filter(c => allowedStatuses.has(c.status))
      .sort((a, b) => (priority[a.status] ?? 99) - (priority[b.status] ?? 99));
    return sorted[0] ?? null;
  }

  /**
   * Aplica dados do contrato nos campos do formulário e carrega produto
   */
  private applyContractToForm(contract: Contract): void {
    this.serviceOrderForm.patchValue({
      contratoId: contract.id,
      contratoNumero: contract.contractNumber,
      contratoStatus: contract.status,
      contratoDataInicio: this.formatDateForDisplay(contract.startDate),
      contratoDataFim: this.formatDateForDisplay(contract.endDate),
      contratoProdutoNome: '',
      contratoProdutoAtivo: ''
    });

    // Carregar dados do produto do contrato
    if (contract.productId) {
      this.contractsService.getContractComplete(contract.id).pipe(take(1)).subscribe({
        next: (response) => {
          if (response.product) {
            this.serviceOrderForm.patchValue({
              contratoProdutoNome: response.product.name ?? response.product.Name ?? '',
              contratoProdutoAtivo: (response.product.isActive ?? response.product.IsActive) ? 'Sim' : 'Não'
            });
          }
        },
        error: () => {} // silencioso, produto é informativo
      });
    }
  }

  /**
   * Limpa campos de contrato no formulário
   */
  private clearContractFields(): void {
    this.serviceOrderForm.patchValue({
      contratoId: '',
      contratoNumero: '',
      contratoStatus: '',
      contratoDataInicio: '',
      contratoDataFim: '',
      contratoProdutoNome: '',
      contratoProdutoAtivo: ''
    });
  }

  /**
   * Confirma auto-link de veículo a partir de contrato do cliente
   */
  private confirmAutoLinkVehicleFromContract(contract: Contract): void {
    // Buscar detalhes do veículo para exibir a descrição correta no modal
    this.vehiclesService.getById(contract.vehicleId).pipe(take(1)).subscribe({
      next: (vehicle: Vehicle) => {
        const descFromVehicle = this.buildVehicleDisplayLabel(vehicle.plateNumber, vehicle.brand, vehicle.model);
        const descFromSnapshot = this.buildVehicleDisplayLabel(contract.vehicleSnapshot?.plate, contract.vehicleSnapshot?.brand, contract.vehicleSnapshot?.model);
        const desc = descFromVehicle || descFromSnapshot || '';
        this.confirmationService.confirm({
          title: 'Veículo encontrado no contrato',
          message: `O cliente selecionado possui um contrato (Nº ${contract.contractNumber} - ${contract.status}) com o veículo "${desc}" vinculado. Deseja atrelar este veículo à OS automaticamente?`,
          confirmText: 'Sim, atrelar veículo',
          cancelText: 'Não',
          type: 'info',
          icon: 'fas fa-car'
        }).subscribe(confirmed => {
          if (confirmed) {
            this.selectedVehicle = this.mapVehicleToSelection(vehicle);
            const vehicleId = vehicle.id || contract.vehicleId;
            this.serviceOrderForm.patchValue({
              veiculoId: vehicleId,
              veiculoPlaca: desc
            });
            this.applyLinkedVehicleDetails(vehicle);
            if (vehicleId) {
              this.loadPreventiveHistory(vehicleId);
            }
          }
        });
      },
      error: () => console.error('Erro ao carregar veículo do contrato')
    });
  }

  /**
   * Confirma auto-link de cliente a partir de contrato do veículo
   */
  private confirmAutoLinkCustomerFromContract(contract: Contract): void {
    this.customersService.getById(contract.customerId).pipe(take(1)).subscribe({
      next: (customer: Customer) => {
        const desc = [customer.name, customer.documentNumber].filter(Boolean).join(' - ');

        this.confirmationService.confirm({
          title: 'Cliente encontrado no contrato',
          message: `O veículo selecionado possui um contrato (Nº ${contract.contractNumber} - ${contract.status}) com o cliente "${desc}" vinculado. Deseja atrelar este cliente à OS automaticamente?`,
          confirmText: 'Sim, atrelar cliente',
          cancelText: 'Não',
          type: 'info',
          icon: 'fas fa-user'
        }).subscribe(confirmed => {
          if (confirmed) {
            this.serviceOrderForm.patchValue({
              clienteId: customer.id,
              clienteNome: customer.name,
              clienteDocumento: customer.documentNumber ?? '',
              clienteCategoria: customer.customerCategory ?? ''
            });
          }
        });
      },
      error: () => console.error('Erro ao carregar cliente do contrato')
    });
  }

  /**
   * Callback quando um almoxarifado é selecionado
   */
  onWarehouseSelected(warehouse: WarehouseSelection): void {
    this.selectedWarehouse = warehouse;
    this.serviceOrderForm.patchValue({
      almoxarifadoId: warehouse.id,
      almoxarifadoNome: `${warehouse.nome} - ${warehouse.responsavel}`
    });

    // Carrega os itens disponíveis filtrando pelo almoxarifado escolhido,
    // garantindo que cada peça selecionada na lista pertence ao warehouse atual.
    this.carregarItemsByWarehouse(warehouse.id);

    if (this.pendingAddPecaAfterWarehouse) {
      this.pendingAddPecaAfterWarehouse = false;
      this.pushPecaLinha(warehouse.id);
    }
  }

  /**
   * Carrega os itens disponíveis para um almoxarifado específico.
   */
  private carregarItemsByWarehouse(warehouseId: number): void {
    if (!warehouseId || warehouseId <= 0) {
      return;
    }
    this.loadingItems = true;
    this.itemService.getItemsByWarehouse(warehouseId, { page: 1, pageSize: 1000 }).subscribe({
      next: (result) => {
        this.itemsDisponiveis = result.items || [];
        this.loadingItems = false;
      },
      error: (error) => {
        console.error('Erro ao carregar itens por almoxarifado:', error);
        this.loadingItems = false;
        this.itemsDisponiveis = [];
      }
    });
  }

  /**
   * Callback quando um estoque é selecionado
   */
  onStockChange(event: any): void {
    const stockId = event.target.value;
    if (stockId) {
      const parsedStockId = Number(stockId);
      const stock = this.stocksList.find(s => s.id === parsedStockId);
      if (stock) {
        this.selectedStockId = parsedStockId;
        this.serviceOrderForm.patchValue({
          estoqueId: parsedStockId,
          estoqueNome: stock.name,
          almoxarifadoId: stock.warehouseId ?? '',
          almoxarifadoNome: stock.warehouseName ?? ''
        });

        // Se a listagem não trouxe o warehouseId, buscar via detalhe do estoque
        if (!stock.warehouseId) {
          this.stockService.getById(parsedStockId).pipe(take(1)).subscribe({
            next: (detail) => {
              if (detail?.warehouseId) {
                stock.warehouseId = detail.warehouseId;
                stock.warehouseName = (detail as any).warehouseName ?? stock.warehouseName;
                this.serviceOrderForm.patchValue({
                  almoxarifadoId: detail.warehouseId,
                  almoxarifadoNome: stock.warehouseName ?? ''
                });
              }
            },
            error: (err) => console.error('Erro ao buscar detalhes do estoque:', err)
          });
        }

        this.carregarItemsByStock(parsedStockId);
      }
    } else {
      this.selectedStockId = null;
      this.serviceOrderForm.patchValue({
        estoqueId: '',
        estoqueNome: '',
        almoxarifadoId: '',
        almoxarifadoNome: ''
      });

      this.itemsDisponiveis = [];
    }
  }

  trackByStock(index: number, stock: StockSelection): number {
    return stock.id;
  }
  
  /**
   * Adiciona uma nova observação ao histórico
   */
  adicionarObservacao(): void {
    if (!this.novaObservacao || this.novaObservacao.trim() === '') {
      return;
    }
    
    // Registra a observação como um evento no histórico de movimentações
    this.adicionarEventoHistorico(
      'observacao',
      'Observação adicionada',
      this.novaObservacao.trim()
    );

    this.novaObservacao = '';
  }
  
  /**
   * Formata data e hora para exibição
   */
  formatarDataHora(dataHora: any): string {
    const date = new Date(dataHora);
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const ano = date.getFullYear();
    const hora = String(date.getHours()).padStart(2, '0');
    const minuto = String(date.getMinutes()).padStart(2, '0');
    
    return `${dia}/${mes}/${ano} às ${hora}:${minuto}`;
  }
  
  /**
   * Adiciona um evento ao histórico da OS
   */
  adicionarEventoHistorico(tipo: string, acao: string, detalhes?: string, dadosExtras?: any): void {
    const currentUser = this.authService.currentUserValue;
    const now = new Date();
    
    // Captura o setor atual no momento da criação do evento
    const setorAtual = this.getSetorAtual();
    
    const evento = {
      tipo: tipo,
      acao: acao,
      usuarioNome: currentUser?.username || 'Usuário Sistema',
      dataHora: now.toISOString(),
      detalhes: detalhes,
      setorAtual: setorAtual, // Sempre captura o setor atual
      ...dadosExtras
    };
    
    this.historico.push(evento);
    this.aplicarFiltroHistorico();
  }
  
  /**
   * Aplica o filtro de data ao histórico
   */
  aplicarFiltroHistorico(): void {
    if (!this.filtroDataInicio && !this.filtroDataFim) {
      this.historicoFiltrado = [...this.historico];
      return;
    }

    this.historicoFiltrado = this.historico.filter(evento => {
      const dataEvento = new Date(evento.dataHora);
      
      // Remove a parte de hora para comparar apenas as datas
      const dataEventoSemHora = new Date(dataEvento.getFullYear(), dataEvento.getMonth(), dataEvento.getDate());
      
      let passaFiltroInicio = true;
      let passaFiltroFim = true;

      if (this.filtroDataInicio) {
        const dataInicio = this.parseDateInputToLocalDate(this.filtroDataInicio);
        if (dataInicio) {
          passaFiltroInicio = dataEventoSemHora >= dataInicio;
        }
      }

      if (this.filtroDataFim) {
        const dataFim = this.parseDateInputToLocalDate(this.filtroDataFim);
        if (dataFim) {
          passaFiltroFim = dataEventoSemHora <= dataFim;
        }
      }

      return passaFiltroInicio && passaFiltroFim;
    });
  }

  private parseDateInputToLocalDate(value: string): Date | null {
    const match = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec((value || '').trim());
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);

    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return null;
    }

    return new Date(year, month, day);
  }

  /**
   * Limpa os filtros de data do histórico
   */
  limparFiltroHistorico(): void {
    this.filtroDataInicio = '';
    this.filtroDataFim = '';
    this.historicoFiltrado = [...this.historico];
  }

  onHistoricoFiltroChange(): void {
    // Se ainda não existe OS persistida no backend, só filtra o array local.
    if (!this.serviceOrderId) {
      this.aplicarFiltroHistorico();
      return;
    }

    this.reloadHistoricoFromApi();
  }

  onHistoricoFiltroClear(): void {
    this.filtroDataInicio = '';
    this.filtroDataFim = '';

    if (!this.serviceOrderId) {
      this.historicoFiltrado = [...this.historico];
      return;
    }

    this.reloadHistoricoFromApi();
  }

  private reloadHistoricoFromApi(): void {
    if (!this.serviceOrderId) return;

    const startDate = (this.filtroDataInicio || '').trim() || undefined;
    const endDate = (this.filtroDataFim || '').trim() || undefined;

    const requestKey = `${this.serviceOrderId}|${startDate ?? ''}|${endDate ?? ''}`;
    this.historicoFiltroRequestKey = requestKey;

    const hasFilter = Boolean(startDate || endDate);
    const logs$ = hasFilter
      ? this.fetchAllServiceOrderLogs(this.serviceOrderId, { startDate, endDate })
      : this.serviceOrderLogsService
          .getByServiceOrderId(this.serviceOrderId, { pageNumber: 1, pageSize: 100 })
          .pipe(map((response) => response.items));

    logs$.pipe(take(1)).subscribe({
      next: (items) => {
        if (this.historicoFiltroRequestKey !== requestKey) return;

        const mapped = items.length > 0 ? this.mapLogsToHistorico(items) : [];
        this.historico = mapped;
        // Mantém a compatibilidade: caso o backend não aplique o filtro, o front continua filtrando.
        this.aplicarFiltroHistorico();

        if (items.length > 0) {
          this.mapLogsToHistoricoResolvedUsers(items)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
              next: (enrichedMapped) => {
                if (this.historicoFiltroRequestKey !== requestKey) return;
                this.historico = enrichedMapped;
                this.aplicarFiltroHistorico();
              },
              error: () => {
                // Mantém o histórico já exibido.
              }
            });
        }
      },
      error: (error) => {
        if (this.historicoFiltroRequestKey !== requestKey) return;
        console.error('Erro ao recarregar logs da ordem de serviço com filtro:', error);
        // Fallback: mantém o histórico atual e aplica o filtro local.
        this.aplicarFiltroHistorico();
      }
    });
  }

  private fetchAllServiceOrderLogs(
    serviceOrderId: number,
    filters: Omit<ServiceOrderLogFilters, 'serviceOrderId'>
  ): Observable<ServiceOrderLog[]> {
    const pageSize = 200;

    return this.serviceOrderLogsService.getByServiceOrderId(serviceOrderId, { ...filters, pageNumber: 1, pageSize }).pipe(
      expand((page) => {
        if (!page?.hasNextPage) {
          return EMPTY;
        }

        return this.serviceOrderLogsService.getByServiceOrderId(serviceOrderId, {
          ...filters,
          pageNumber: (page.pageNumber || 1) + 1,
          pageSize
        });
      }),
      map((page) => page.items || []),
      reduce((acc, items) => acc.concat(items), [] as ServiceOrderLog[])
    );
  }
  
  /**
   * Retorna o ícone apropriado para cada tipo de evento
   */
  getIconeEvento(tipo: string): string {
    const icones: any = {
      'criacao': 'fas fa-plus-circle',
      'edicao': 'fas fa-edit',
      'mudanca_status': 'fas fa-exchange-alt',
      'direcionamento': 'fas fa-share',
      'atribuicao': 'fas fa-user-tag',
      'conclusao': 'fas fa-check-circle',
      'cancelamento': 'fas fa-times-circle',
      'outros': 'fas fa-info-circle'
    };
    
    return icones[tipo] || 'fas fa-circle';
  }
  
  /**
   * Retorna a cor apropriada para cada tipo de evento
   */
  getCorEvento(tipo: string): string {
    const cores: any = {
      'criacao': '#28a745',
      'edicao': '#ffc107',
      'mudanca_status': '#17a2b8',
      'direcionamento': '#007bff',
      'atribuicao': '#fd7e14',
      'conclusao': '#28a745',
      'cancelamento': '#dc3545',
      'outros': '#6c757d'
    };
    
    return cores[tipo] || '#6c757d';
  }
  
  /**
   * Abre modal de direcionamento
   */
  direcionarParaSetor(): void {
    // Orçamento B2B: work section "Pendente" é fase normal — não bloqueia transitar a OS.
    if (!isB2bConfigOrder(this.configOrder)) {
      const sections = this.serviceWorkSections || [];
      if (sections.length > 0) {
        const hasPending = sections.some((section) => {
          const sectionStatus = (section?.status ?? '').toString().trim().toUpperCase();
          if (sectionStatus === 'PENDENTE') return true;

          const services = section?.services || [];
          return services.some((service: any) => {
            const status = (service?.status ?? service?.Status ?? '').toString().trim().toUpperCase();
            return status === 'PENDENTE';
          });
        });

        if (hasPending) {
          this.confirmationService
            .confirm({
              title: 'Serviços Pendentes',
              message: 'Há Serviços Pendentes aguardando atualização.',
              confirmText: 'OK',
              hideCancelButton: true,
              type: 'warning',
              icon: 'fas fa-exclamation-triangle'
            })
            .pipe(take(1))
            .subscribe();
          return;
        }
      }
    }

    const ordemServicoAtual = this.configOrder?.typeOS || this.serviceOrderForm.get('tipoOS')?.value || 'Não definida';
    const groupId = this.configOrder?.groupId ?? null;
    const configOrderId = this.configOrder?.id ?? null;
    const currentStatusNome = this.serviceOrderForm.get('statusAtual')?.value || '';
    this.direcionamentoModal.open(ordemServicoAtual, groupId, currentStatusNome, configOrderId);
  }

  /**
   * Encerra a OS
   */
  /**
   * Abre o modal de confirmação de encerramento
   */
  encerrarOS(origem: 'manual' | 'sugestao' = 'manual'): void {
    const allowedStatus = (this.configOrder?.statusClosedServiceOrder || '').toString();
    const currentStatus = (this.serviceOrderForm.get('statusAtual')?.value || '').toString();

    // Sugestão automática não deve gerar aviso: quem direcionou a OS não pediu para encerrar.
    if (origem === 'sugestao' && (!allowedStatus.trim() || currentStatus.trim() !== allowedStatus.trim())) {
      return;
    }

    // Regra solicitada: somente permite encerrar se status(string===string) for o permitido.
    if (!allowedStatus.trim()) {
      this.openWarning('Configuração da OS não possui "Status que permite encerramento" definido.').subscribe();
      return;
    }

    if (currentStatus.trim() !== allowedStatus.trim()) {
      this.openWarning(
        `Não é permitido encerrar a OS no status atual ("${currentStatus}"). ` +
        `Status permitido para encerramento: "${allowedStatus}".`
      ).subscribe();
      return;
    }

    // O fechamento pode ser retroativo, então quem confirma é o modal com data e hora.
    this.encerramentoModal.open({
      ordemServico: this.serviceOrderForm.get('numeroOS')?.value,
      statusAtual: currentStatus.trim(),
      dataAbertura: this.serviceOrderForm.get('dataAbertura')?.value,
      horaAbertura: this.serviceOrderForm.get('horaAbertura')?.value,
      sugerido: origem === 'sugestao'
    });
  }

  onEncerramentoConfirmado(data: ServiceOrderCloseData): void {
    this.encerrarOSDefinitivamente(data.dataFechamento, data.horaFechamento);
  }

  private encerrarOSDefinitivamente(dataFechamento: string, horaFechamento: string): void {
    const previousStatus = String(this.serviceOrderForm.get('statusAtual')?.value || 'Em Andamento');

    this.serviceOrderForm.patchValue({
      statusAtual: 'Encerrada',
      status: 'Encerrada',
      dataConclusao: dataFechamento,
      horaConclusao: horaFechamento
    });

    this.adicionarEventoHistorico(
      'conclusao',
      'OS Encerrada Definitivamente',
      'Ordem de serviço encerrada pelo usuário. Esta OS não poderá ser reaberta. Orçamentos abertos vinculados foram cancelados.',
      {
        statusAnterior: previousStatus,
        statusNovo: 'Encerrada'
      }
    );

    const osId = Number(this.serviceOrderId || 0);
    if (osId > 0) {
      this.serviceOrdersService.cancelLinkedBudgets(osId).subscribe({
        next: (res) => {
          if (res.cancelled > 0) {
            this.openSuccess(
              `${res.cancelled} orçamento(s) vinculado(s) cancelado(s) automaticamente.`
            ).subscribe();
          }
          this.onSubmit();
        },
        error: () => this.onSubmit()
      });
      return;
    }

    this.onSubmit();
  }
  
  /**
   * Gera HTML para impressão da OS
   */
  imprimirOS(): void {
    const formValues = this.serviceOrderForm.getRawValue();
    
    // Obter o caminho base da aplicação para o logo
    const baseUrl = window.location.origin;
    const logoUrl = `${baseUrl}/assets/branding/flit-core-logo-light.svg`;
    
    // Gerar o HTML formatado para impressão
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>OS ${formValues.numeroOS || 'N/A'} - Impressão</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            font-size: 11pt;
            line-height: 1.4;
            color: #333;
            padding: 20px;
            background: white;
          }
          
          .print-container {
            max-width: 210mm;
            margin: 0 auto;
          }
          
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #3A4D56;
          }
          
          .header-logo {
            max-width: 300px;
            height: auto;
            margin-bottom: 15px;
          }
          
          .header h1 {
            color: #3A4D56;
            font-size: 24pt;
            margin-bottom: 5px;
          }
          
          .header h2 {
            color: #666;
            font-size: 14pt;
            font-weight: normal;
          }
          
          .os-number {
            font-size: 18pt;
            font-weight: bold;
            color: #3A4D56;
            margin: 10px 0;
          }
          
          .section {
            margin-bottom: 25px;
            page-break-inside: avoid;
          }
          
          .section-title {
            background: #3A4D56;
            color: white;
            padding: 8px 12px;
            font-size: 13pt;
            font-weight: bold;
            margin-bottom: 12px;
            border-radius: 4px;
          }
          
          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 15px;
          }
          
          .info-item {
            padding: 8px;
            background: #f8f9fa;
            border-left: 3px solid #3A4D56;
          }
          
          .info-item label {
            font-weight: bold;
            color: #495057;
            display: block;
            margin-bottom: 3px;
            font-size: 10pt;
          }
          
          .info-item .value {
            color: #212529;
            font-size: 11pt;
          }
          
          .full-width {
            grid-column: 1 / -1;
          }
          
          .checkbox-list {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }
          
          .checkbox-item {
            display: flex;
            align-items: center;
            padding: 4px;
          }
          
          .checkbox-item input {
            margin-right: 8px;
          }
          
          .table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          
          .table th {
            background: #e9ecef;
            padding: 8px;
            text-align: left;
            font-weight: bold;
            border: 1px solid #dee2e6;
            font-size: 10pt;
          }
          
          .table td {
            padding: 6px 8px;
            border: 1px solid #dee2e6;
            font-size: 10pt;
          }
          
          .table tr:nth-child(even) {
            background: #f8f9fa;
          }
          
          .table tfoot td {
            font-weight: bold;
            background: #e9ecef;
          }
          
          .text-right {
            text-align: right;
          }
          
          .text-center {
            text-align: center;
          }
          
          .badge {
            display: inline-block;
            padding: 3px 8px;
            font-size: 9pt;
            font-weight: bold;
            border-radius: 3px;
          }
          
          .badge-success {
            background: #28a745;
            color: white;
          }
          
          .badge-warning {
            background: #ffc107;
            color: #212529;
          }
          
          .badge-danger {
            background: #dc3545;
            color: white;
          }
          
          .badge-info {
            background: #17a2b8;
            color: white;
          }
          
          .observacao-box {
            padding: 10px;
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            margin-top: 8px;
            white-space: pre-wrap;
            font-size: 10pt;
          }
          
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #dee2e6;
            text-align: center;
            font-size: 9pt;
            color: #6c757d;
          }
          
          .signature-area {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 40px;
            margin-top: 50px;
            page-break-inside: avoid;
          }
          
          .signature-box {
            text-align: center;
            padding-top: 40px;
            border-top: 1px solid #333;
          }
          
          @media print {
            body {
              padding: 0;
            }
            
            .section {
              page-break-inside: avoid;
            }
            
            @page {
              margin: 15mm;
              size: A4;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          <!-- CABEÇALHO -->
          <div class="header">
            <img src="${logoUrl}" alt="Flit Core" class="header-logo" onerror="this.style.display='none'">
            <h1>ORDEM DE SERVIÇO</h1>
            <h2>Sistema de Gestão de Manutenção</h2>
            <div class="os-number">OS Nº ${formValues.numeroOS || 'N/A'}</div>
          </div>
          
          <!-- DADOS GERAIS -->
          <div class="section">
            <div class="section-title">📋 DADOS GERAIS</div>
            <div class="info-grid">
              <div class="info-item">
                <label>Tipo de OS:</label>
                <div class="value">${formValues.tipoOS || 'N/A'}</div>
              </div>
              <div class="info-item">
                <label>Status:</label>
                <div class="value">${formValues.statusAtual || 'N/A'}</div>
              </div>
              <div class="info-item">
                <label>Data de Abertura:</label>
                <div class="value">${this.formatarDataParaImpressao(formValues.dataAbertura)} às ${formValues.horaAbertura || 'N/A'}</div>
              </div>
              <div class="info-item">
                <label>Setor Atual:</label>
                <div class="value">${formValues.setorAtual || 'N/A'}</div>
              </div>
              <div class="info-item">
                <label>Usuário Abertura:</label>
                <div class="value">${formValues.usuarioNome || 'N/A'}</div>
              </div>
              <div class="info-item">
                <label>Unidade:</label>
                <div class="value">${formValues.unidadeNome || 'N/A'}</div>
              </div>
            </div>
          </div>
          
          <!-- VEÍCULO -->
          ${formValues.veiculoPlaca ? `
          <div class="section">
            <div class="section-title">🚗 INFORMAÇÕES DO VEÍCULO</div>
            <div class="info-grid">
              <div class="info-item">
                <label>Veículo:</label>
                <div class="value">${formValues.veiculoPlaca}</div>
              </div>
              <div class="info-item">
                <label>KM:</label>
                <div class="value">${formValues.km || 'N/A'}</div>
              </div>
            </div>
            
          </div>
          ` : ''}
          
          <!-- RESPONSÁVEIS -->
          <div class="section">
            <div class="section-title">👥 RESPONSÁVEIS</div>
            <div class="info-grid">
              ${formValues.clienteNome ? `
              <div class="info-item">
                <label>Cliente:</label>
                <div class="value">${formValues.clienteNome}</div>
              </div>
              ` : ''}
              ${formValues.contratoNumero ? `
              <div class="info-item">
                <label>Contrato:</label>
                <div class="value">${formValues.contratoNumero}</div>
              </div>
              ` : ''}
              ${formValues.mecanicoNome ? `
              <div class="info-item">
                <label>Mecânico:</label>
                <div class="value">${formValues.mecanicoNome}</div>
              </div>
              ` : ''}
              ${this.canStockSection() && formValues.almoxarifadoNome ? `
              <div class="info-item">
                <label>Almoxarifado:</label>
                <div class="value">${formValues.almoxarifadoNome}</div>
              </div>
              ` : ''}
              ${formValues.descricao ? `
              <div class="info-item full-width">
                <label>Descrição do Serviço:</label>
                <div class="observacao-box">${formValues.descricao}</div>
              </div>
              ` : ''}
            </div>
          </div>
          
          <!-- SERVIÇOS -->
          ${this.servicos.length > 0 ? `
          <div class="section">
            <div class="section-title">🔧 SERVIÇOS REALIZADOS</div>
            <table class="table">
              <thead>
                <tr>
                  <th width="5%">#</th>
                  <th width="55%">Descrição</th>
                  <th width="10%" class="text-center">Qtd</th>
                  <th width="20%" class="text-right">Total</th>
                  <th width="10%" class="text-center">Avaria</th>
                </tr>
              </thead>
              <tbody>
                ${this.servicos.map((servico, index) => `
                  <tr>
                    <td class="text-center">${index + 1}</td>
                    <td>${servico.descricao || 'N/A'}</td>
                    <td class="text-center">${servico.quantidade}</td>
                    <td class="text-right">${this.formatarValor(this.valorLinhaCobranca(servico))}</td>
                    <td class="text-center">${servico.isAvaria ? '⚠️ Sim' : 'Não'}</td>
                  </tr>
                `).join('')}
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3" class="text-right">SUBTOTAL SERVIÇOS:</td>
                  <td class="text-right">${this.formatarValor(this.servicos.reduce((t, s) => t + this.valorLinhaCobranca(s), 0))}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          ` : ''}
          
          <!-- PEÇAS -->
          ${this.pecas.length > 0 ? `
          <div class="section">
            <div class="section-title">⚙️ PEÇAS UTILIZADAS</div>
            <table class="table">
              <thead>
                <tr>
                  <th width="5%">#</th>
                  <th width="55%">Descrição</th>
                  <th width="10%" class="text-center">Qtd</th>
                  <th width="20%" class="text-right">Total</th>
                  <th width="10%" class="text-center">Avaria</th>
                </tr>
              </thead>
              <tbody>
                ${this.pecas.map((peca, index) => `
                  <tr>
                    <td class="text-center">${index + 1}</td>
                    <td>${peca.descricao || 'N/A'}</td>
                    <td class="text-center">${peca.quantidade}</td>
                    <td class="text-right">${this.formatarValor(this.valorLinhaCobranca(peca))}</td>
                    <td class="text-center">${peca.isAvaria ? '⚠️ Sim' : 'Não'}</td>
                  </tr>
                `).join('')}
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3" class="text-right">SUBTOTAL PEÇAS:</td>
                  <td class="text-right">${this.formatarValor(this.pecas.reduce((t, p) => t + this.valorLinhaCobranca(p), 0))}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          ` : ''}
          
          <!-- TOTAIS -->
          ${(this.servicos.length > 0 || this.pecas.length > 0) ? `
          <div class="section">
            <div class="section-title">💰 RESUMO FINANCEIRO</div>
            <div class="info-grid">
              <div class="info-item">
                <label>Subtotal Serviços:</label>
                <div class="value">${this.formatarValor(this.servicos.reduce((t, s) => t + this.valorLinhaCobranca(s), 0))}</div>
              </div>
              <div class="info-item">
                <label>Subtotal Peças:</label>
                <div class="value">${this.formatarValor(this.pecas.reduce((t, p) => t + this.valorLinhaCobranca(p), 0))}</div>
              </div>
              <div class="info-item">
                <label>TOTAL GERAL:</label>
                <div class="value" style="font-size: 14pt; font-weight: bold; color: #3A4D56;">
                  ${this.formatarValor(
                    this.servicos.reduce((t, s) => t + this.valorLinhaCobranca(s), 0)
                    + this.pecas.reduce((t, p) => t + this.valorLinhaCobranca(p), 0)
                  )}
                </div>
              </div>
              ${this.calcularQuantidadeAvarias() > 0 ? `
              <div class="info-item">
                <label>Total Avarias:</label>
                <div class="value" style="color: #dc3545; font-weight: bold;">
                  ${this.formatarValor(this.calcularValorTotalAvarias())}
                </div>
              </div>
              ` : ''}
            </div>
          </div>
          ` : ''}
          
          <!-- SESSÕES DE TRABALHO -->
          ${this.sessoesTrabalho.length > 0 ? `
          <div class="section">
            <div class="section-title">📋 HISTÓRICO DE SESSÕES DE TRABALHO</div>
            ${this.sessoesTrabalho.map((sessao, sessaoIndex) => `
              <div style="margin-bottom: 30px; padding: 15px; border: 1px solid #dee2e6; border-radius: 4px; background: #f8f9fa;">
                <div style="margin-bottom: 15px; padding-bottom: 10px; border-bottom: 2px solid #3A4D56;">
                  <h4 style="color: #3A4D56; margin-bottom: 5px;">Sessão #${sessaoIndex + 1}</h4>
                  <div style="font-size: 9pt; color: #6c757d;">
                    <strong>Data/Hora:</strong> ${this.formatarDataHora(sessao.dataSessao)} | 
                    <strong>Usuário:</strong> ${sessao.usuario}
                  </div>
                </div>
                
                ${sessao.servicos.length > 0 ? `
                <div style="margin-bottom: 15px;">
                  <h5 style="color: #495057; margin-bottom: 8px; font-size: 11pt;">🔧 Serviços (${sessao.servicos.length})</h5>
                  <table class="table" style="margin-bottom: 10px;">
                    <thead>
                      <tr>
                        <th width="5%">#</th>
                        <th width="55%">Descrição</th>
                        <th width="10%" class="text-center">Qtd</th>
                        <th width="20%" class="text-right">Total</th>
                        <th width="10%" class="text-center">Avaria</th>
                        <th width="10%" class="text-center">Status Pagamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${sessao.servicos.map((servico, idx) => `
                        <tr style="${servico.isAvaria ? 'background-color: #fff3cd;' : ''}">
                          <td class="text-center">${idx + 1}</td>
                          <td>${servico.descricao || 'N/A'}</td>
                          <td class="text-center">${servico.quantidade}</td>
                          <td class="text-right" style="${servico.isAvaria ? 'color: #dc3545; font-weight: bold;' : ''}">${this.formatarValor(this.valorLinhaCobranca(servico))}</td>
                          <td class="text-center">${servico.isAvaria ? '⚠️ Sim' : 'Não'}</td>
                          <td class="text-center">
                            ${servico.isAvaria ? `
                              <span style="padding: 2px 6px; border-radius: 3px; font-size: 8pt; font-weight: bold; ${servico.statusAvaria === 'Pago' ? 'background: #28a745; color: white;' : 'background: #ffc107; color: #212529;'}">
                                ${servico.statusAvaria || 'Pendente'}
                              </span>
                            ` : '-'}
                          </td>
                        </tr>
                      `).join('')}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colspan="3" class="text-right"><strong>Subtotal:</strong></td>
                        <td class="text-right"><strong>${this.formatarValor(this.calcularSubtotalServicos(sessao.servicos))}</strong></td>
                        <td colspan="2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                ` : ''}
                
                ${sessao.pecas.length > 0 ? `
                <div style="margin-bottom: 15px;">
                  <h5 style="color: #495057; margin-bottom: 8px; font-size: 11pt;">⚙️ Peças (${sessao.pecas.length})</h5>
                  <table class="table" style="margin-bottom: 10px;">
                    <thead>
                      <tr>
                        <th width="5%">#</th>
                        <th width="52%">Descrição</th>
                        <th width="8%">Qtd</th>
                        <th width="19%" class="text-right">Total</th>
                        <th width="8%" class="text-center">Avaria</th>
                        <th width="8%" class="text-center">Status Pagamento</th>
                        
                      </tr>
                    </thead>
                    <tbody>
                      ${sessao.pecas.map((peca, idx) => `
                        <tr style="${peca.isAvaria ? 'background-color: #fff3cd;' : ''}">
                          <td class="text-center">${idx + 1}</td>
                          <td>${peca.descricao || 'N/A'}</td>
                          <td class="text-center">${peca.quantidade}</td>
                          <td class="text-right" style="${peca.isAvaria ? 'color: #dc3545; font-weight: bold;' : ''}">${this.formatarValor(this.valorLinhaCobranca(peca))}</td>
                          <td class="text-center">${peca.isAvaria ? '⚠️ Sim' : 'Não'}</td>
                          <td class="text-center">
                            ${peca.isAvaria ? `
                              <span style="padding: 2px 6px; border-radius: 3px; font-size: 8pt; font-weight: bold; ${peca.statusAvaria === 'Pago' ? 'background: #28a745; color: white;' : 'background: #ffc107; color: #212529;'}">
                                ${peca.statusAvaria || 'Pendente'}
                              </span>
                            ` : '-'}
                          </td>
                          
                        </tr>
                      `).join('')}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colspan="3" class="text-right"><strong>Subtotal:</strong></td>
                        <td class="text-right"><strong>${this.formatarValor(this.calcularSubtotalPecas(sessao.pecas))}</strong></td>
                        <td colspan="2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                ` : ''}
                
                <div style="margin-top: 15px; padding: 10px; background: #e9ecef; border-radius: 4px; text-align: right;">
                  <strong style="font-size: 12pt; color: #3A4D56;">
                    Total da Sessão: ${this.formatarValor(sessao.valorTotal)}
                  </strong>
                </div>
              </div>
            `).join('')}
            
            <div style="margin-top: 20px; padding: 15px; background: #3A4D56; color: white; border-radius: 4px; text-align: center;">
              <strong style="font-size: 13pt;">
                Total Geral das Sessões: ${this.formatarValor(this.sessoesTrabalho.reduce((total, sessao) => total + sessao.valorTotal, 0))}
              </strong>
            </div>
          </div>
          ` : ''}
          
          <!-- PRAZOS -->
          ${(formValues.dataPrevistaConclusao || formValues.dataConclusao) ? `
          <div class="section">
            <div class="section-title">📅 PRAZOS</div>
            <div class="info-grid">
              ${formValues.dataPrevistaConclusao ? `
              <div class="info-item">
                <label>Data Prevista Conclusão:</label>
                <div class="value">${this.formatarDataParaImpressao(formValues.dataPrevistaConclusao)}</div>
              </div>
              ` : ''}
              ${formValues.dataConclusao ? `
              <div class="info-item">
                <label>Data de Conclusão:</label>
                <div class="value">${this.formatarDataParaImpressao(formValues.dataConclusao)}</div>
              </div>
              ` : ''}
              ${formValues.valorEstimado ? `
              <div class="info-item">
                <label>Valor Estimado:</label>
                <div class="value">${this.formatarValor(formValues.valorEstimado)}</div>
              </div>
              ` : ''}
            </div>
          </div>
          ` : ''}
          
          <!-- ASSINATURAS -->
          <div class="section">
            <div class="signature-area">
              <div class="signature-box">
                <strong>Responsável Técnico</strong>
                <div style="margin-top: 5px; font-size: 9pt; color: #6c757d;">
                  ${formValues.mecanicoNome || '_________________________'}
                </div>
              </div>
              <div class="signature-box">
                <strong>Cliente/Autorização</strong>
                <div style="margin-top: 5px; font-size: 9pt; color: #6c757d;">
                  ${formValues.clienteNome || '_________________________'}
                </div>
              </div>
            </div>
          </div>
          
          <!-- RODAPÉ -->
          <div class="footer">
            <p>Documento gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
            <p>Sistema de Gestão de Ordens de Serviço - ${formValues.unidadeNome || 'Flit Core'}</p>
          </div>
        </div>
      </body>
      </html>
    `;
    
    // Abrir em nova janela e imprimir
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Aguardar o carregamento antes de imprimir
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 250);
      };
      
      // Adicionar ao histórico
      this.adicionarEventoHistorico(
        'outros',
        'OS impressa',
        `Ordem de serviço ${formValues.numeroOS} gerada para impressão`
      );
    } else {
      this.openWarning('Por favor, permita pop-ups para imprimir a OS.').subscribe();
    }
  }
  
  /**
   * Formata data para impressão
   */
  formatarDataParaImpressao(data: any): string {
    if (!data) return 'N/A';
    const d = new Date(data);
    return d.toLocaleDateString('pt-BR');
  }
  
  
  /**
   * Retorna o setor atual da OS
   */
  getSetorAtual(): string {
    // Primeiro tenta pegar do formulário
    const setorFormulario = this.serviceOrderForm.get('setorAtual')?.value;
    if (setorFormulario) {
      return setorFormulario;
    }
    
    // Se não tiver no formulário, pega o último direcionamento do histórico
    const ultimoDirecionamento = [...this.historico]
      .reverse()
      .find(h => h.tipo === 'direcionamento' && h.setorDestino);
    
    return ultimoDirecionamento?.setorDestino || 'Recepção';
  }
  
  /**
   * Processa o direcionamento confirmado
   */
  onDirecionamentoConfirmado(data: DirecionamentoData): void {
    // Regra solicitada: informar ao usuário se o novo status afeta o status do veículo.
    const novoStatusNome = (data?.novoStatusNome || '').toString();
    const rule = this.vehicleStatusChangeRules
      .find((r) => (r?.initialStatusName || '').toString().trim() === novoStatusNome.trim());

    if (rule?.newStatusVeichile) {
      this.openWarning(
        `Atenção: ao mudar o status da OS para "${novoStatusNome}", o status do veículo será alterado para "${rule.newStatusVeichile}".`
      ).subscribe();
    }

    const setorOrigem = this.getSetorAtual();
    const statusAnterior = this.serviceOrderForm.get('statusAtual')?.value || '';

    // Atualiza setor, status e motivo no formulário
    this.serviceOrderForm.patchValue({
      setorAtual: data.setorDestino,
      statusAtual: data.novoStatusNome,
      motivoStatus: data.observacao || ''
    });

    // Monta detalhes para o histórico
    let detalhes = `Novo status: ${data.novoStatusNome}`;
    if (data.observacao) {
      detalhes += `\n${data.observacao}`;
    }

    // Adiciona ao histórico
    this.adicionarEventoHistorico(
      'direcionamento',
      `OS direcionada para ${data.setorDestino}`,
      detalhes,
      {
        setorOrigem: setorOrigem,
        setorDestino: data.setorDestino,
        statusAnterior: statusAnterior,
        statusNovo: data.novoStatusNome
      }
    );

    // Persiste o novo status na API.
    // IMPORTANTE: O backend pode tratar PUT como replace. Então precisamos enviar um payload completo
    // (preservando vehicleId/customerId/etc.), e apenas sobrescrever status/configOrderId.
    if (!this.serviceOrderId) {
      this.resetOsStateSnapshot();
      this.bypassUnsavedGuard = true;
      this.openSuccess(`Direcionamento realizado com sucesso. Novo status: ${data.novoStatusNome}`).subscribe(() => {
        this.router.navigate(['/service-orders']);
      });
      return;
    }

    this.resolveCurrentUserDepartmentId()
      .pipe(take(1))
      .subscribe((departmentId) => {
        const resolvedDepartmentId = departmentId ?? this.currentUserDepartmentId ?? this.configOrder?.departmentId ?? null;
        if (!resolvedDepartmentId) {
          this.openError('Não foi possível identificar o departamento para salvar o direcionamento.').subscribe();
          return;
        }

        const payload: any = this.buildServiceOrderPayload(resolvedDepartmentId);

        const preservedOpeningReason = (this.serviceOrderForm.get('motivoAbertura')?.value || '').toString().trim();
        const preservedOpeningDate = this.serviceOrderForm.get('dataAbertura')?.value;
        const newConfigOrderId = this.parsePositiveNumber(data.configOrderId) ?? this.parsePositiveNumber(payload.configOrderId);
        const currentConfigOrderId = this.parsePositiveNumber(this.configOrder?.id);
        const configOrderChanged = !!newConfigOrderId && newConfigOrderId !== currentConfigOrderId;

        payload.status = data.novoStatusNome;
        payload.configOrderId = newConfigOrderId ?? payload.configOrderId;
        payload.openingReason = preservedOpeningReason || null;
        payload.openingDate = this.toOpeningClosingIso(
          preservedOpeningDate,
          this.serviceOrderForm.get('horaAbertura')?.value as string,
        ) ?? payload.openingDate;
        payload.departmentId = resolvedDepartmentId;
        payload.DepartmentId = resolvedDepartmentId;

        if (configOrderChanged && data.destinoTypeOS) {
          payload.typeOS = data.destinoTypeOS;
          this.serviceOrderForm.patchValue({ tipoOS: data.destinoTypeOS });

          if (this.configOrder) {
            this.configOrder = {
              ...this.configOrder,
              id: newConfigOrderId!,
              typeOS: data.destinoTypeOS
            };
          } else {
            this.configOrder = {
              id: newConfigOrderId!,
              typeOS: data.destinoTypeOS,
              canVehicle: true,
              canAditionalVehicle: true,
              canClient: true,
              canStock: true,
              canServices: true,
              canClose: true,
              active: true
            };
          }

          const matchedConfig = this.osTypeConfigs.find((c) => c.id === newConfigOrderId);
          if (matchedConfig) {
            this.selectedOsTypeConfig = matchedConfig;
          }

          this.loadMotivoAberturaOptions(newConfigOrderId);
        }

        const direcionamentoSuccessMessage = `Direcionamento realizado com sucesso. Novo status: ${data.novoStatusNome}`;

        this.serviceOrdersService.update(this.serviceOrderId!, payload).subscribe({
          next: () => {
            this.resetOsStateSnapshot();
            this.bypassUnsavedGuard = true;
            this.applyVehicleStatusChangeRule(rule)
              .pipe(take(1))
              .subscribe(() => {
                if (configOrderChanged) {
                  this.openSuccess(direcionamentoSuccessMessage).subscribe(() => {
                    this.router.navigate(['/service-orders']);
                  });
                } else {
                  this.marcarSugestaoEncerramento(statusAnterior, data.novoStatusNome);
                  this.refreshServiceOrderPage(direcionamentoSuccessMessage);
                }
              });
          },
          error: (err) => {
            console.error('Erro ao atualizar status da OS:', err);
            this.openError('Erro ao atualizar status da OS no servidor.').subscribe();
          }
        });
      });
  }

  /** Só há o que atualizar quando a OS tem veículo da frota vinculado. */
  canEditVehicleKm(): boolean {
    return !this.isViewMode && !!this.toIntOrNull(this.serviceOrderForm.get('veiculoId')?.value);
  }

  /**
   * O KM exibido é o do cadastro do veículo, então a edição grava no cadastro —
   * o "KM abertura" da OS continua sendo campo próprio da ordem de serviço.
   */
  salvarKmVeiculo(): void {
    const vehicleId = this.toIntOrNull(this.serviceOrderForm.get('veiculoId')?.value);
    const km = this.toIntOrNull(this.serviceOrderForm.get('km')?.value);
    if (!vehicleId || this.salvandoKmVeiculo) return;

    if (km == null || km < 0) {
      this.openWarning('Informe um KM válido para atualizar o cadastro do veículo.').subscribe();
      return;
    }

    this.salvandoKmVeiculo = true;
    this.vehiclesService
      .updateMileage(vehicleId, km)
      .pipe(take(1), finalize(() => (this.salvandoKmVeiculo = false)))
      .subscribe({
        next: () => {
          this.adicionarEventoHistorico(
            'outros',
            'KM do veículo atualizado',
            `Novo KM do cadastro: ${km.toLocaleString('pt-BR')}`
          );
          this.openSuccess('KM atualizado no cadastro do veículo.').subscribe();
        },
        error: (err) => {
          console.error('Erro ao atualizar KM do veículo:', err);
          this.openError('Não foi possível atualizar o KM no cadastro do veículo.').subscribe();
        }
      });
  }

  private applyVehicleStatusChangeRule(rule: VehicleServiceOrderStatusChange | undefined): Observable<void> {
    const newVehicleStatus = (rule?.newStatusVeichile || '').toString().trim();
    if (!newVehicleStatus) return of(void 0);

    const vehicleId = this.toIntOrNull(this.serviceOrderForm.get('veiculoId')?.value);
    if (!vehicleId) return of(void 0);

    return this.vehiclesService.getById(vehicleId).pipe(
      take(1),
      switchMap((currentVehicle: Vehicle) => {
        const currentStatus = (currentVehicle?.status || '').toString().trim();
        if (currentStatus === newVehicleStatus) {
          return of(currentVehicle);
        }

        // Importante: não chamar update com payload parcial aqui, pois o mapToDto do VehiclesService
        // preenche renavamNumber como string vazia quando ausente.
        return this.vehiclesService.update(vehicleId, {
          ...currentVehicle,
          status: newVehicleStatus
        });
      }),
      tap((updatedVehicle: Vehicle) => {
        // Reflete na UI imediatamente (status/label/etc.)
        if (updatedVehicle?.id) {
          this.applyLinkedVehicleDetails(updatedVehicle);
        }
      }),
      map(() => void 0),
      catchError((err) => {
        console.error('Erro ao atualizar status do veículo após direcionamento:', err);
        this.openWarning('Direcionamento salvo, mas não foi possível atualizar o status do veículo automaticamente.').subscribe();
        return of(void 0);
      })
    );
  }
  
  /**
   * Navega para o step anterior
   */
  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.scrollToTop();
    }
  }
  
  /**
   * Navega para o próximo step
   */
  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.scrollToTop();

      if (this.currentStep === 6) {
        this.onEnterAttachmentsTab();
      }

      if (this.currentStep === 8) {
        this.onEnterChecklistTab();
      }
    }
  }
  
  /**
   * Vai para um step específico
   */
  goToStep(step: number): void {
    // Se não for a aba de abertura (step 1) e a OS não foi gerada, não permite navegar
    if (step > 1 && !this.osGerada) {
      this.openWarning('Por favor, selecione o tipo de OS e clique em "Gerar OS" para continuar.').subscribe();
      return;
    }
    
    // Se a OS já foi gerada, não permite voltar para a aba de abertura (step 1)
    if (step === 1 && this.osGerada) {
      this.openWarning('Não é possível retornar à aba de Abertura após a OS ter sido gerada.').subscribe();
      return;
    }
    
    if (step >= 1 && step <= this.totalSteps) {
      this.currentStep = step;
      this.scrollToTop();

      // Performance: só busca checklists quando o usuário entra na aba
      if (step === 8) {
        this.onEnterChecklistTab();
      }

      // Anexos: buscar no backend quando entrar na aba
      if (step === 6) {
        this.onEnterAttachmentsTab();
      }
    }
  }

  private parseAttachmentDescription(raw: any): { tipo: AnexoOS['tipo']; descricao: string } {
    const fallback: { tipo: AnexoOS['tipo']; descricao: string } = {
      tipo: 'Outros',
      descricao: raw != null ? String(raw) : ''
    };

    if (raw == null) {
      return fallback;
    }

    const value = String(raw).trim();
    if (!value) {
      return { tipo: 'Outros', descricao: '' };
    }

    const match = value.match(/^\[(.+?)\]\s*(.*)$/);
    if (!match) {
      return { tipo: 'Outros', descricao: value };
    }

    const bracketType = (match[1] ?? '').trim();
    const rest = (match[2] ?? '').trim();

    if (this.tiposAnexo.includes(bracketType)) {
      return {
        tipo: bracketType as AnexoOS['tipo'],
        descricao: rest
      };
    }

    return { tipo: 'Outros', descricao: value };
  }

  private mapAttachmentDtoToAnexo(dto: ServiceOrderAttachmentDto): AnexoOS {
    const parsedId = (dto as any)?.id ?? (dto as any)?.Id;
    const id = typeof parsedId === 'number' ? parsedId : (parsedId != null ? Number(parsedId) : undefined);
    const fileName = (dto as any)?.fileName ?? (dto as any)?.FileName ?? '';
    const fileSize = (dto as any)?.fileSize ?? (dto as any)?.FileSize;
    const filePath = (dto as any)?.filePath ?? (dto as any)?.FilePath;
    const userId = (dto as any)?.userId ?? (dto as any)?.UserId;
    const createdAt = (dto as any)?.createdAt ?? (dto as any)?.CreatedAt ?? (dto as any)?.updatedAt ?? (dto as any)?.UpdatedAt;

    const { tipo, descricao } = this.parseAttachmentDescription((dto as any)?.description ?? (dto as any)?.Description);

    return {
      id: Number.isFinite(id as any) ? (id as number) : undefined,
      tipo,
      descricao: descricao || '',
      nomeArquivo: String(fileName || ''),
      tamanho: Number(fileSize) || 0,
      // Guardamos o caminho do blob para permitir gerar download-url por blobName se necessário.
      arquivo: filePath ? { filePath: String(filePath) } : undefined,
      usuarioAdicionou: userId != null ? String(userId) : 'Sistema',
      dataAdicao: createdAt ? String(createdAt) : new Date().toISOString()
    };
  }

  private onEnterAttachmentsTab(): void {
    if (!this.osGerada) {
      return;
    }

    if (!this.serviceOrderId) {
      this.anexosLoadError = 'OS ainda não possui ID para buscar anexos.';
      return;
    }

    if (this.loadingAnexos) {
      return;
    }

    this.loadingAnexos = true;
    this.anexosLoadError = null;

    this.serviceOrdersService
      .listAttachments(this.serviceOrderId)
      .pipe(take(1))
      .subscribe({
        next: (attachments) => {
          this.anexos = (attachments || []).map(dto => this.mapAttachmentDtoToAnexo(dto));
          this.loadingAnexos = false;
        },
        error: (err) => {
          console.error('Erro ao buscar anexos da OS:', err);
          this.loadingAnexos = false;
          this.anexosLoadError = 'Erro ao buscar anexos da OS.';
          this.openError('Erro ao buscar anexos da OS.').subscribe();
        }
      });
  }

  private getLinkedVehiclePlateNumber(): string | null {
    const selectedPlate = (this.selectedVehicle as any)?.placa;
    if (selectedPlate) {
      const normalized = String(selectedPlate).trim();
      return normalized ? normalized.toUpperCase() : null;
    }

    const formPlateLabel = this.serviceOrderForm.get('veiculoPlaca')?.value;
    if (!formPlateLabel) {
      return null;
    }

    const raw = String(formPlateLabel);
    const plate = raw.split(' - ')[0]?.trim();
    return plate ? plate.toUpperCase() : null;
  }

  abrirNovoChecklist(): void {
    this.openChecklistTypeModal();
  }

  /**
   * Abre modal para seleção do tipo de checklist antes de criar a partir da OS.
   */
  openChecklistTypeModal(): void {
    const checklistTypes = [
      { value: 'Inspeção', label: 'Inspeção', icon: 'fa-search', description: 'Checklist de inspeção geral do veículo' },
      { value: 'Preventiva', label: 'Preventiva', icon: 'fa-shield-alt', description: 'Manutenção preventiva do veículo' },
      { value: 'Recolhimento', label: 'Recolhimento', icon: 'fa-undo', description: 'Checklist de recolhimento de veículo' },
      { value: 'Corretiva', label: 'Corretiva', icon: 'fa-tools', description: 'Manutenção corretiva do veículo' },
      { value: 'Qualidade', label: 'Qualidade', icon: 'fa-award', description: 'Checklist de controle de qualidade' },
      { value: 'Comercial', label: 'Comercial', icon: 'fa-handshake', description: 'Checklist comercial do veículo' }
    ];

    const root = getComputedStyle(document.documentElement);
    const primaryColor = root.getPropertyValue('--primary-color').trim() || '#3A4D56';
    const primaryDark = root.getPropertyValue('--primary-dark').trim() || '#2A3D46';
    const borderColor = root.getPropertyValue('--border-color').trim() || '#e9ecef';
    const textDark = root.getPropertyValue('--text-dark').trim() || '#2c3e50';
    const textLight = root.getPropertyValue('--text-light').trim() || '#6c757d';
    const backgroundLight = root.getPropertyValue('--background-light').trim() || '#f8f9fa';
    const secondaryColor = root.getPropertyValue('--secondary-color').trim() || '#6C7B7F';
    const shadowMedium = root.getPropertyValue('--shadow-medium').trim() || 'rgba(0, 0, 0, 0.15)';

    const modalHTML = `
      <div id="checklistTypeModalBackdrop" style="
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        background: rgba(0, 0, 0, 0.5) !important;
        z-index: 10000 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        padding: 20px !important;
      ">
        <div style="
          width: 100% !important;
          max-width: 600px !important;
          background: white !important;
          border-radius: 12px !important;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2) !important;
          overflow: hidden !important;
          animation: slideDown 0.3s ease-out !important;
        ">
          <div style="
            background: linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%) !important;
            color: white !important;
            padding: 24px !important;
            text-align: center !important;
          ">
            <h2 style="margin: 0 0 8px 0 !important; font-size: 24px !important; font-weight: 600 !important;">
              <i class="fas fa-clipboard-check" style="margin-right: 8px !important;"></i>
              Novo Checklist
            </h2>
            <p style="margin: 0 !important; opacity: 0.9 !important; font-size: 14px !important;">
              Selecione o tipo de checklist que deseja criar
            </p>
          </div>
          <div style="padding: 24px !important; max-height: 60vh !important; overflow-y: auto !important;">
            <div style="display: grid !important; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)) !important; gap: 16px !important;">
              ${checklistTypes.map(type => `
                <div 
                  class="checklist-type-option" 
                  data-type="${type.value}"
                  style="
                    border: 2px solid ${borderColor} !important;
                    border-radius: 8px !important;
                    padding: 20px !important;
                    cursor: pointer !important;
                    transition: all 0.2s ease !important;
                    background: white !important;
                  "
                  onmouseover="this.style.borderColor='${primaryColor}'; this.style.transform='translateY(-2px)'; this.style.boxShadow='0 4px 12px ${shadowMedium}'"
                  onmouseout="this.style.borderColor='${borderColor}'; this.style.transform='translateY(0)'; this.style.boxShadow='none'"
                >
                  <div style="text-align: center !important;">
                    <div style="
                      width: 60px !important;
                      height: 60px !important;
                      margin: 0 auto 12px auto !important;
                      background: linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%) !important;
                      border-radius: 50% !important;
                      display: flex !important;
                      align-items: center !important;
                      justify-content: center !important;
                      color: white !important;
                      font-size: 24px !important;
                    ">
                      <i class="fas ${type.icon}"></i>
                    </div>
                    <h3 style="
                      margin: 0 0 8px 0 !important;
                      font-size: 18px !important;
                      font-weight: 600 !important;
                      color: ${textDark} !important;
                    ">${type.label}</h3>
                    <p style="
                      margin: 0 !important;
                      font-size: 13px !important;
                      color: ${textLight} !important;
                      line-height: 1.4 !important;
                    ">${type.description}</p>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
          <div style="
            padding: 16px 24px !important;
            background: ${backgroundLight} !important;
            border-top: 1px solid ${borderColor} !important;
            display: flex !important;
            justify-content: flex-end !important;
            gap: 12px !important;
          ">
            <button 
              id="cancelChecklistTypeBtn"
              style="
                padding: 10px 20px !important;
                background: ${borderColor} !important;
                color: ${textDark} !important;
                border: none !important;
                border-radius: 6px !important;
                cursor: pointer !important;
                font-size: 14px !important;
                font-weight: 500 !important;
                transition: background 0.2s ease !important;
              "
              onmouseover="this.style.background='${secondaryColor}'"
              onmouseout="this.style.background='${borderColor}'"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
      <style>
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      </style>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);

    const backdrop = document.getElementById('checklistTypeModalBackdrop');
    const cancelBtn = document.getElementById('cancelChecklistTypeBtn');
    const typeOptions = document.querySelectorAll('.checklist-type-option');

    backdrop?.addEventListener('click', (e) => {
      if (e.target === backdrop) {
        this.closeChecklistTypeModal();
      }
    });

    cancelBtn?.addEventListener('click', () => {
      this.closeChecklistTypeModal();
    });

    typeOptions.forEach(option => {
      option.addEventListener('click', () => {
        const type = (option as HTMLElement).getAttribute('data-type');
        if (type) {
          this.closeChecklistTypeModal();
          this.navigateToNewChecklist(type);
        }
      });
    });
  }

  closeChecklistTypeModal(): void {
    const backdrop = document.getElementById('checklistTypeModalBackdrop');
    if (backdrop) {
      backdrop.remove();
    }
  }

  private navigateToNewChecklist(type: string): void {
    this.confirmLeave()
      .pipe(take(1))
      .subscribe((canLeave) => {
        if (!canLeave) {
          return;
        }

        const plateNumber = this.getLinkedVehiclePlateNumber();
        const queryParams: { type: string; plate?: string } = { type };

        if (plateNumber) {
          queryParams.plate = plateNumber;
        }

        this.router.navigate(['/checklist/new'], { queryParams });
      });
  }

  private onEnterChecklistTab(): void {
    const plateNumber = this.getLinkedVehiclePlateNumber();
    if (!plateNumber) {
      this.vehicleChecklists = [];
      this.vehicleChecklistSelecionado = null;
      this.vehicleChecklistsLoadError = 'Nenhuma placa de veículo vinculada para buscar checklists.';
      this.vehicleChecklistsLastPlate = null;
      return;
    }

    if (this.loadingVehicleChecklists) {
      return;
    }

    // Cache simples por placa: evita re-fetch ao voltar para a aba
    if (this.vehicleChecklistsLastPlate === plateNumber) {
      return;
    }

    this.loadingVehicleChecklists = true;
    this.vehicleChecklistsLoadError = null;
    this.vehicleChecklists = [];
    this.vehicleChecklistSelecionado = null;

    this.checklistService
      .getAllByPlateNumber(plateNumber, 1, 10)
      .pipe(
        take(1),
        catchError((err) => {
          console.error('Erro ao buscar checklists por placa:', err);
          this.vehicleChecklistsLoadError = 'Erro ao carregar checklists do veículo.';
          return of({ success: false, message: 'Erro', data: [], timestamp: new Date().toISOString() } as any);
        })
      )
      .subscribe((response: any) => {
        const success = response?.success === true;
        this.vehicleChecklists = response?.data ?? [];
        this.loadingVehicleChecklists = false;

        if (success) {
          this.vehicleChecklistsLastPlate = plateNumber;
        } else {
          this.vehicleChecklistsLastPlate = null;
          this.vehicleChecklistsLoadError = response?.message || 'Não foi possível carregar os checklists.';
        }
      });
  }

  getChecklistTypeDisplay(checklist: ChecklistItemDto): string {
    const type = checklist?.ChecklistType || '';
    const sub = checklist?.ChecklistSubType || '';
    return sub ? `${type} - ${sub}` : type;
  }

  getChecklistStatusBadgeClass(status: string | null | undefined): string {
    const normalized = (status || '').toLowerCase();
    if (normalized.includes('vigente') || normalized.includes('completed') || normalized.includes('conclu')) {
      return 'badge-success';
    }
    if (normalized.includes('cancel')) {
      return 'badge-danger';
    }
    if (normalized.includes('draft') || normalized.includes('pend') || normalized.includes('progress') || normalized.includes('andamento')) {
      return 'badge-warning';
    }
    return 'badge-secondary';
  }

  private extractPdfUrlFromChecklist(checklist: ChecklistItemDto): string | null {
    const sections: any[] = (checklist as any)?.Sections ?? [];
    for (const section of sections) {
      const items: any[] = section?.Items ?? [];
      for (const item of items) {
        const photoUrl = item?.PhotoUrl ?? item?.photoUrl;
        const itemType = item?.ItemType ?? item?.itemType;
        if (typeof photoUrl === 'string' && photoUrl) {
          const lower = photoUrl.toLowerCase();
          if (itemType === 'Document' || lower.includes('.pdf')) {
            return photoUrl;
          }
        }
      }
    }
    return null;
  }

  getVehicleChecklistPdfUrlRaw(checklist: ChecklistItemDto | null): string | null {
    if (!checklist) return null;
    return this.extractPdfUrlFromChecklist(checklist);
  }

  getVehicleChecklistPdfUrl(checklist: ChecklistItemDto | null): SafeResourceUrl | null {
    const url = checklist ? this.extractPdfUrlFromChecklist(checklist) : null;
    if (!url) return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  visualizarVehicleChecklist(checklist: ChecklistItemDto): void {
    this.vehicleChecklistSelecionado = checklist;
  }

  fecharVehicleChecklistVisualizadorPDF(): void {
    this.vehicleChecklistSelecionado = null;
  }

  baixarVehicleChecklistPdf(checklist: ChecklistItemDto): void {
    const url = this.extractPdfUrlFromChecklist(checklist);
    if (!url) {
      this.openError('PDF não disponível para este checklist.').subscribe();
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';
    link.click();
  }
  
  /**
   * Verifica se está no primeiro step
   */
  isFirstStep(): boolean {
    return this.currentStep === 1;
  }
  
  /**
   * Verifica se está no último step
   */
  isLastStep(): boolean {
    return this.currentStep === this.totalSteps;
  }
  
  /**
   * Gera a OS e habilita as outras abas
   */
  /**
   * Carrega todos os tipos de OS (ConfigOrders) disponíveis
   */
  carregarConfigOrders(): void {
    this.loadingOsTypeConfigs = true;
    
    // Carregar IDs permitidos do token (+ tipos B2B ativos)
    const tokenIds = this.authService.getAllowedConfigOrderIdsFromToken();

    this.configOrdersService.getAll().subscribe({
      next: (configs) => {
        this.allowedConfigOrderIds = mergeAccessibleConfigOrderIds(tokenIds, configs);
        this.osTypeConfigs = filterAccessibleConfigOrders(configs, this.allowedConfigOrderIds);
        this.loadingOsTypeConfigs = false;
      },
      error: () => {
        this.loadingOsTypeConfigs = false;
      }
    });
  }

  /**
   * Chamado quando o usuário muda o Tipo de OS no select.
   * Aplica as permissões da configuração selecionada.
   */
  onTipoOSChange(): void {
    const typeOS = this.serviceOrderForm.get('tipoOS')?.value;
    if (!typeOS) {
      this.configOrder = null;
      this.selectedOsTypeConfig = null;
      this.selectedProvider = null;
      this.loadMotivoAberturaOptions(null);
      this.loadVehicleStatusChangeRules(null);
      return;
    }
    const found = this.osTypeConfigs.find(c => c.typeOS === typeOS);
    if (found) {
      this.selectedOsTypeConfig = found;
      this.configOrder = {
        id: found.id,
        typeOS: found.typeOS,
        groupId: found.groupId ?? undefined,
        departmentId: found.departmentId ?? undefined,
        statusClosedServiceOrder: found.statusClosedServiceOrder ?? null,
        transaction: found.transaction === true,
        isThirdParty: found.isThirdParty === true,
        canVehicle: found.canVehicle !== false,
        canAditionalVehicle: found.canAditionalVehicle !== false,
        canClient: found.canClient !== false,
        canStock: found.canStock !== false,
        canServices: found.canServices !== false,
        canClose: found.canClose !== false,
        active: found.active !== false
      };

      this.loadMotivoAberturaOptions(found.id);
      this.loadVehicleStatusChangeRules(found.id);

      if (!this.isThirdPartyOS()) {
        this.selectedProvider = null;
      }
    } else {
      this.configOrder = null;
      this.selectedOsTypeConfig = null;
      this.selectedProvider = null;
      this.loadMotivoAberturaOptions(null);
      this.loadVehicleStatusChangeRules(null);
    }
  }

  private ensureLegacyMotivoInOptions(): void {
    const currentValue = (this.serviceOrderForm.get('motivoAbertura')?.value || '').toString().trim();
    if (currentValue && !this.motivoAberturaOptions.includes(currentValue)) {
      this.motivoAberturaOptions = [currentValue, ...this.motivoAberturaOptions];
    }
  }

  private loadMotivoAberturaOptions(configOrderId: number | null): void {
    const normalizedId = this.parsePositiveNumber(configOrderId);
    if (!normalizedId) {
      this.motivoAberturaConfigOrderId = null;
      // Fallback padrão quando não há ConfigOrder selecionada.
      this.motivoAberturaOptions = ['Avaria', 'Recolhimento', 'Furto', 'Outros'];
      this.ensureLegacyMotivoInOptions();
      return;
    }

    if (this.motivoAberturaConfigOrderId === normalizedId && this.motivoAberturaOptions.length) {
      this.ensureLegacyMotivoInOptions();
      return;
    }

    this.motivoAberturaConfigOrderId = normalizedId;
    this.loadingMotivoAberturaOptions = true;

    this.serviceOrderOpeningReasonsService.getByConfigOrderId(normalizedId).pipe(take(1)).subscribe({
      next: (items: ServiceOrderOpeningReason[]) => {
        const options = (items || [])
          .map((r) => ((r as any)?.statusName ?? (r as any)?.openingReasonName ?? '').toString().trim())
          .filter((name) => !!name);

        const currentValue = (this.serviceOrderForm.get('motivoAbertura')?.value || '').toString().trim();
        this.motivoAberturaOptions = currentValue && !options.includes(currentValue)
          ? [currentValue, ...options]
          : options;

        this.loadingMotivoAberturaOptions = false;
      },
      error: () => {
        this.ensureLegacyMotivoInOptions();
        this.loadingMotivoAberturaOptions = false;
      }
    });
  }

  private loadVehicleStatusChangeRules(configOrderId: number | null): void {
    const normalizedId = this.parsePositiveNumber(configOrderId);
    if (!normalizedId) {
      this.vehicleStatusChangeRulesConfigOrderId = null;
      this.vehicleStatusChangeRules = [];
      return;
    }

    if (this.vehicleStatusChangeRulesConfigOrderId === normalizedId) {
      return;
    }

    this.vehicleStatusChangeRulesConfigOrderId = normalizedId;
    this.loadingVehicleStatusChangeRules = true;

    this.vehicleServiceOrderStatusChangesService.getByConfigOrderId(normalizedId).pipe(take(1)).subscribe({
      next: (items) => {
        this.vehicleStatusChangeRules = items || [];
        this.loadingVehicleStatusChangeRules = false;
      },
      error: () => {
        this.vehicleStatusChangeRules = [];
        this.loadingVehicleStatusChangeRules = false;
      }
    });
  }

  gerarOS(): void {
    const tipoOSCtrl = this.serviceOrderForm.get('tipoOS');
    const motivoAberturaCtrl = this.serviceOrderForm.get('motivoAbertura');

    // No fluxo de criação, o motivo de abertura é obrigatório.
    this.setMotivoAberturaRequired(true);
    
    // Validar se há tipos de OS permitidos
    if (this.allowedConfigOrderIds.length === 0) {
      this.openWarning('Você não tem permissão para criar ordens de serviço. Nenhum tipo de OS está autorizado para seu perfil.').subscribe();
      return;
    }

    if (!tipoOSCtrl?.value) {
      tipoOSCtrl?.markAsTouched();
      this.openWarning('Por favor, selecione o Tipo de OS antes de continuar.').subscribe();
      return;
    }

    if (!motivoAberturaCtrl?.value) {
      motivoAberturaCtrl?.markAsTouched();
      this.openWarning('Por favor, selecione o Motivo da Abertura antes de continuar.').subscribe();
      return;
    }

    // Aplica a configuração do tipo selecionado (caso não tenha sido aplicada ainda)
    if (!this.configOrder) {
      this.onTipoOSChange();
    }

    if (!this.configOrder?.id) {
      this.openWarning('Não foi possível identificar a configuração da OS selecionada.').subscribe();
      return;
    }
    
    // Validar se o tipo selecionado está nos permitidos
    if (!this.allowedConfigOrderIds.includes(this.configOrder.id)) {
      this.openWarning('O tipo de OS selecionado não está autorizado para seu perfil.').subscribe();
      return;
    }

    this.gerandoOS = true;

    this.resolveCurrentUserDepartmentId()
      .pipe(take(1))
      .subscribe((departmentId) => {
        if (!departmentId) {
          this.gerandoOS = false;
          this.openError('Não foi possível identificar o departamento do usuário logado. Verifique se o usuário possui Staff vinculado com DepartmentId.').subscribe();
          return;
        }

        this.currentUserDepartmentId = departmentId;

        const payload = this.buildServiceOrderPayload(departmentId);
        payload.status = payload.status || 'Aberta';

        this.serviceOrdersService.create(payload).subscribe({
          next: (createdOrder) => {
            const createdId = createdOrder.id;
            const numeroOS = createdOrder.numeroOS || 'N/A';

            this.serviceOrderId = createdId;
            this.isEditMode = true;
            this.osGerada = true;
            this.applyServiceOrderData(createdOrder);

            this.adicionarEventoHistorico(
              'criacao',
              'OS gerada',
              `Ordem de serviço do tipo "${tipoOSCtrl.value}" criada com sucesso`
            );

            this.currentStep = 2;
            this.gerandoOS = false;

            // Após gerar a OS, o motivo já foi persistido; não exige revalidação no submit.
            this.setMotivoAberturaRequired(false);

            this.router.navigate(['/service-orders', createdId, 'edit'], { replaceUrl: true });

            this.openSuccess(
              `Ordem de Serviço gerada com sucesso! Tipo: ${tipoOSCtrl.value}. Número: ${numeroOS}. Agora você pode preencher as informações necessárias.`
            ).subscribe();
          },
          error: (error) => {
            console.error('Erro ao gerar ordem de serviço:', error);
            this.openError('Erro ao gerar ordem de serviço.').subscribe();
            this.gerandoOS = false;
          }
        });
      });
  }

  /**
   * Formata a data de conclusão para exibição
   */
  formatarDataConclusao(data: any): string {
    if (!data) return '';
    const d = new Date(data);
    if (isNaN(d.getTime())) return data; // Retorna como está se não for uma data válida
    return d.toLocaleDateString('pt-BR');
  }
  
  /**
   * Retorna o nome da aba atual
   */
  getCurrentTabName(): string {
    const tabNames = {
      1: 'Abertura',
      2: 'Informações Gerais',
      3: 'Veículo',
      4: 'Responsáveis',
      5: 'Serviços',
      6: 'Anexos',
      7: 'Histórico',
      8: 'CheckList'
    };
    return tabNames[this.currentStep as keyof typeof tabNames] || 'Ordem de Serviço';
  }
  
  /**
   * Rola para o topo do formulário
   */
  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  
  /**
   * Carrega histórico mockado para demonstração
   */
  carregarHistoricoMockado(): void {
    const now = new Date();
    const currentUser = this.authService.currentUserValue;
    const usuarioNome = currentUser?.username || 'Usuário Sistema';
    
    // Evento 1: Criação
    this.historico.push({
      tipo: 'criacao',
      acao: 'OS criada',
      usuarioNome: usuarioNome,
      dataHora: new Date(now.getTime() - 7200000).toISOString(), // 2 horas atrás
      detalhes: 'Ordem de serviço aberta no sistema',
      setorAtual: 'Recepção'
    });
    
    // Evento 2: Direcionamento
    this.historico.push({
      tipo: 'direcionamento',
      acao: 'OS direcionada para Mecânica',
      usuarioNome: usuarioNome,
      dataHora: new Date(now.getTime() - 5400000).toISOString(), // 1h30 atrás
      detalhes: 'Encaminhada para análise técnica',
      setorOrigem: 'Recepção',
      setorDestino: 'Mecânica',
      setorAtual: 'Mecânica'
    });
    
    // Evento 3: Mudança de Status
    this.historico.push({
      tipo: 'mudanca_status',
      acao: 'Status alterado',
      usuarioNome: 'João Mecânico',
      dataHora: new Date(now.getTime() - 3600000).toISOString(), // 1 hora atrás
      detalhes: 'Status atualizado de "Aberta" para "Em Andamento"',
      statusAnterior: 'Aberta',
      statusNovo: 'Em Andamento',
      setorAtual: 'Mecânica'
    });
    
    // Evento 4: Atribuição
    this.historico.push({
      tipo: 'atribuicao',
      acao: 'Mecânico atribuído',
      usuarioNome: 'Supervisor',
      dataHora: new Date(now.getTime() - 2700000).toISOString(), // 45 min atrás
      detalhes: 'João Mecânico foi designado como responsável pela OS',
      setorAtual: 'Mecânica'
    });
    
    // Evento 5: Direcionamento para outro setor
    this.historico.push({
      tipo: 'direcionamento',
      acao: 'OS direcionada para Almoxarifado',
      usuarioNome: 'João Mecânico',
      dataHora: new Date(now.getTime() - 1800000).toISOString(), // 30 min atrás
      detalhes: 'Solicitação de peças necessárias',
      setorOrigem: 'Mecânica',
      setorDestino: 'Almoxarifado',
      setorAtual: 'Almoxarifado'
    });
    
    // Evento 6: Retorno
    this.historico.push({
      tipo: 'direcionamento',
      acao: 'OS retornou para Mecânica',
      usuarioNome: 'Almoxarife',
      dataHora: new Date(now.getTime() - 900000).toISOString(), // 15 min atrás
      detalhes: 'Peças separadas e disponibilizadas',
      setorOrigem: 'Almoxarifado',
      setorDestino: 'Mecânica',
      setorAtual: 'Mecânica'
    });
    
    // Atualizar histórico filtrado
    this.historicoFiltrado = [...this.historico];
  }
  
  /**
   * Adiciona uma nova linha de serviço
   */
  adicionarServicoLinha(): void {
    const novoServico: ServicoOS = {
      id: this.servicos.length + 1,
      descricao: this.selectedProvider ? this.selectedProvider.serviceType : '',
      externalServiceProviderId: this.selectedProvider?.id ?? null,
      quantidade: 1,
      valorUnitario: 0,
      valorTotal: 0,
      maintenanceKind: 'Corretiva',
      chargePercent: null,
      isAvaria: false,
      statusAvaria: 'Pendente',
      status: 'Pendente' as 'Pendente' | 'Em Andamento' | 'Concluído' | 'Cancelado',
      usuarioAdicionou: this.authService.currentUserValue?.username || 'Usuário Sistema',
      dataAdicao: new Date().toISOString()
    };
    
    this.servicos.push(novoServico);
  }
  
  /**
   * Adiciona uma nova linha de peça.
   * Antes de criar a linha exigimos a seleção de um almoxarifado, pois o
   * payload de `service-order-stock-requests` exige `sourceWarehouseId` por item
   * e a listagem de itens disponíveis deve ser filtrada pelo almoxarifado.
   */
  adicionarPecaLinha(): void {
    if (this.isThirdPartyOS() || !this.canStockSection()) {
      this.pushPecaLinha();
      return;
    }

    const warehouseId = this.toIntOrNull(this.serviceOrderForm.get('almoxarifadoId')?.value);
    if (warehouseId && warehouseId > 0) {
      this.pushPecaLinha(warehouseId);
      return;
    }

    // Sem almoxarifado: abre modal e adia a inclusão da peça até a confirmação.
    this.pendingAddPecaAfterWarehouse = true;
    if (this.warehouseModal) {
      this.warehouseModal.open();
    } else {
      this.openWarning('Selecione um almoxarifado para adicionar peças.').subscribe();
      this.pendingAddPecaAfterWarehouse = false;
    }
  }

  /**
   * Cria a linha de peça em si, opcionalmente já vinculando o almoxarifado.
   */
  private pushPecaLinha(warehouseId?: number): void {
    const novaPeca: any = {
      id: this.pecas.length + 1,
      descricao: '',
      quantidade: 1,
      valorUnitario: 0,
      valorTotal: 0,
      maintenanceKind: 'Corretiva',
      chargePercent: null,
      isAvaria: false,
      statusAvaria: 'Pendente',
      retiradaEstoque: this.canStockSection() && !this.isThirdPartyOS(),
      emEstoque: this.canStockSection() && !this.isThirdPartyOS(),
      warehouseId: warehouseId && warehouseId > 0 ? warehouseId : null
    };

    this.pecas.push(novaPeca);
  }
  
  /**
   * Adiciona um novo serviço
   */
  adicionarServico(servico: any): void {
    const currentUser = this.authService.currentUserValue;
    const now = new Date();
    
    // Usa o valor unitário do campo do formulário ou o valor calculado
    const valorUnitario = this.valorUnitarioServico || servico.valorUnitario || 0;
    
    // Monta string das peças selecionadas do estoque
    const pecasEstoqueSelecionadas = this.pecasSelecionadasEstoque.length > 0 
      ? this.pecasSelecionadasEstoque.map(id => {
          const peca = this.pecasDisponiveisEstoque.find(p => p.id === id);
          return peca ? `${peca.nome} (${peca.categoria})` : '';
        }).filter(nome => nome).join(', ')
      : undefined;
    
    const novoServico: ServicoOS = {
      id: this.servicos.length + 1,
      descricao: servico.descricao,
      externalServiceProviderId: this.selectedProvider?.id ?? null,
      pecasEstoque: pecasEstoqueSelecionadas,
      quantidade: servico.quantidade,
      valorUnitario: valorUnitario,
      valorTotal: servico.quantidade * valorUnitario,
      isAvaria: false,
      statusAvaria: 'Pendente',
      usuarioAdicionou: currentUser?.username || 'Usuário Sistema',
      dataAdicao: now.toISOString(),
      status: 'Pendente' as 'Pendente' | 'Em Andamento' | 'Concluído' | 'Cancelado'
    };
    
    this.servicos.push(novoServico);
    
    // Adicionar ao histórico
    let detalhesServico = `Serviço: ${servico.descricao} (Qtd: ${servico.quantidade}, Valor: R$ ${valorUnitario.toFixed(2)})`;
    if (pecasEstoqueSelecionadas) {
      detalhesServico += ` - Peças: ${pecasEstoqueSelecionadas}`;
    }
    
    this.adicionarEventoHistorico(
      'outros',
      'Serviço adicionado',
      detalhesServico
    );
  }
  
  /**
   * Remove um serviço por índice
   */
  removerServico(index: number): void {
    if (index >= 0 && index < this.servicos.length) {
      const servico = this.servicos[index];
      this.confirmationService
        .confirm({
          title: 'Confirmar remoção',
          message: `Tem certeza que deseja remover o serviço "${servico.descricao || 'selecionado'}"?`,
          confirmText: 'Remover',
          cancelText: 'Cancelar',
          confirmButtonClass: 'btn-danger',
          cancelButtonClass: 'btn-secondary',
          type: 'warning'
        })
        .pipe(take(1))
        .subscribe((confirmed) => {
          if (!confirmed) return;

          this.servicos.splice(index, 1);

          this.adicionarEventoHistorico(
            'outros',
            'Serviço removido',
            `Serviço removido: ${servico.descricao || 'item sem descrição'}`
          );
        });
    }
  }
  
  /**
   * Remove uma peça por índice
   */
  removerPeca(index: number): void {
    if (index >= 0 && index < this.pecas.length) {
      const peca = this.pecas[index];
      this.confirmationService
        .confirm({
          title: 'Confirmar remoção',
          message: `Tem certeza que deseja remover a peça "${peca.descricao || 'selecionada'}"?`,
          confirmText: 'Remover',
          cancelText: 'Cancelar',
          confirmButtonClass: 'btn-danger',
          cancelButtonClass: 'btn-secondary',
          type: 'warning'
        })
        .pipe(take(1))
        .subscribe((confirmed) => {
          if (!confirmed) return;

          this.pecas.splice(index, 1);

          this.adicionarEventoHistorico(
            'outros',
            'Peça removida',
            `Peça removida: ${peca.descricao || 'item sem descrição'}`
          );
        });
    }
  }
  
  /**
   * Calcula o valor total dos serviços
   */
  calcularValorTotalServicos(): number {
    return this.servicos.reduce((total, servico) => total + (servico.valorTotal || 0), 0);
  }
  
  /**
   * Calcula o valor total das peças
   */
  calcularValorTotalPecas(): number {
    return this.pecas.reduce((total, peca) => total + (peca.valorTotal || 0), 0);
  }
  
  /**
   * Calcula o valor total geral (serviços + peças)
   * Para OS de Terceiros, aplica acréscimo percentual quando habilitado.
   */
  calcularValorTotalGeral(): number {
    const subtotal = this.calcularValorTotalServicos() + this.calcularValorTotalPecas();
    return subtotal + this.calcularValorAcrescimo();
  }

  /**
   * Calcula o valor do acréscimo (%) sobre o subtotal (serviços + peças).
   * Aplicável apenas para OS de Terceiros com toggle ativo e percentual válido.
   */
  calcularValorAcrescimo(): number {
    if (!this.isThirdPartyOS() || !this.aplicarAcrescimo) {
      return 0;
    }
    const percentual = Number(this.acrescimoPercentual);
    if (!percentual || isNaN(percentual) || percentual <= 0) {
      return 0;
    }
    const subtotal = this.calcularValorTotalServicos() + this.calcularValorTotalPecas();
    return subtotal * (percentual / 100);
  }

  /**
   * Chamado quando o toggle de acréscimo é alterado.
   */
  onAplicarAcrescimoChange(): void {
    if (!this.aplicarAcrescimo) {
      this.acrescimoPercentual = 0;
    }
  }
  
  /**
   * Chamado quando um serviço é alterado
   */
  onServicoChange(index: number): void {
    if (index >= 0 && index < this.servicos.length) {
      const servico = this.servicos[index];

      // Modo prestador: cálculo livre (valor editável manualmente)
      if (this.selectedProvider) {
        servico.valorTotal = this.computeLineChargeTotal(
          servico.quantidade,
          servico.valorUnitario,
          servico.maintenanceKind,
          servico.chargePercent
        );
        return;
      }

      // Buscar o serviço selecionado da lista
      const servicoSelecionado = this.tiposServicos.find(s => s.name === servico.descricao);
      
      if (servicoSelecionado) {
        // Aplicar valores do serviço da API
        servico.valorUnitario = servicoSelecionado.currentCost || servicoSelecionado.baseCost || 0;
      }
      
      // Fallback: Se o serviço está no mapeamento hardcoded, usar esses valores
      if (servico.descricao && this.servicosPecasValores[servico.descricao]) {
        const pecasValores = this.servicosPecasValores[servico.descricao];
        const valorTotal = pecasValores.reduce((total, peca) => 
          total + (peca.quantidade * peca.valorUnitario), 0);
        servico.valorUnitario = valorTotal;
      }

      servico.valorTotal = this.computeLineChargeTotal(
        servico.quantidade,
        servico.valorUnitario,
        servico.maintenanceKind,
        servico.chargePercent
      );
    }
  }
  
  /**
   * Chamado quando uma peça é alterada
   */
  onPecaChange(index: number): void {
    if (index >= 0 && index < this.pecas.length) {
      const peca = this.pecas[index];
      
      // Modo prestador: cálculo livre (valor editável manualmente)
      if (this.selectedProvider) {
        peca.valorTotal = this.computeLineChargeTotal(
          peca.quantidade,
          peca.valorUnitario,
          peca.maintenanceKind,
          peca.chargePercent
        );
        return;
      }

      // Se a peça foi selecionada, buscar o item da API e aplicar o preço de venda
      if (peca.descricao) {
        const itemSelecionado = this.itemsDisponiveis.find(item => item.name === peca.descricao);
        if (itemSelecionado && itemSelecionado.salePrice !== undefined) {
          peca.valorUnitario = itemSelecionado.salePrice;
        }
        // Atualiza visualmente o almoxarifado da sessão com base no item escolhido,
        // apenas se ainda não houver um almoxarifado definido no form.
        if (itemSelecionado && !this.serviceOrderForm.get('almoxarifadoId')?.value) {
          if ((itemSelecionado as any).warehouseId) {
            this.serviceOrderForm.patchValue({
              almoxarifadoId: (itemSelecionado as any).warehouseId,
              almoxarifadoNome: (itemSelecionado as any).warehouseName ?? this.serviceOrderForm.get('almoxarifadoNome')?.value ?? ''
            });
          }
        }
      }

      peca.valorTotal = this.computeLineChargeTotal(
        peca.quantidade,
        peca.valorUnitario,
        peca.maintenanceKind,
        peca.chargePercent
      );

      peca.retiradaEstoque = this.canStockSection() && !this.isThirdPartyOS();
      peca.emEstoque = this.canStockSection() && !this.isThirdPartyOS();
    }
  }
  
  /**
   * Avaria de linha já salva precisa ir ao banco na hora: o PDF e a cobrança leem
   * a flag persistida, não o estado da tela.
   */
  alternarAvariaSessaoServico(servico: any): void {
    const id = Number(servico?.id || 0);
    if (!id || this.atualizandoAvariaSessao) return;

    const novoValor = !servico.isAvaria;
    this.atualizandoAvariaSessao = true;
    this.serviceOrderWorkSectionsService
      .setServiceBilling(id, { hasFault: novoValor })
      .pipe(finalize(() => (this.atualizandoAvariaSessao = false)))
      .subscribe({
        next: (atualizado) => {
          if (novoValor) this.avariaDesmarcadaIds.delete(id);
          else this.avariaDesmarcadaIds.add(id);
          servico.isAvaria = novoValor;
          servico.statusAvaria = this.normalizePaymentStatus(atualizado.paymentStatus);
        },
        error: () => {}
      });
  }

  alternarAvariaSessaoPeca(peca: any): void {
    const id = Number(peca?.id || 0);
    if (!id || this.atualizandoAvariaSessao) return;

    const novoValor = !peca.isAvaria;
    this.atualizandoAvariaSessao = true;
    this.serviceOrderWorkSectionsService
      .setItemBilling(id, { hasFault: novoValor })
      .pipe(finalize(() => (this.atualizandoAvariaSessao = false)))
      .subscribe({
        next: (atualizado) => {
          if (novoValor) this.avariaDesmarcadaIds.delete(id);
          else this.avariaDesmarcadaIds.add(id);
          peca.isAvaria = novoValor;
          peca.statusAvaria = this.normalizePaymentStatus(atualizado.paymentStatus);
        },
        error: () => {}
      });
  }

  /**
   * Chamado quando o status de avaria é alterado
   */
  onAvariaChange(): void {
    // Método para recalcular quando uma avaria é marcada/desmarcada
    // Força a atualização da view
  }

  /**
   * Calcula o valor total de avarias em serviços
   */
  calcularValorAvariasServicos(): number {
    return this.servicos
      .filter(s => s.isAvaria)
      .reduce((total, servico) => total + (servico.valorTotal || 0), 0);
  }
  
  /**
   * Calcula o valor total de avarias em peças
   */
  calcularValorAvariasPecas(): number {
    return this.pecas
      .filter(p => p.isAvaria)
      .reduce((total, peca) => total + (peca.valorTotal || 0), 0);
  }
  
  /**
   * Calcula o valor total de todas as avarias
   */
  calcularValorTotalAvarias(): number {
    return this.calcularValorAvariasServicos() + this.calcularValorAvariasPecas();
  }
  
  /**
   * Calcula a quantidade total de itens marcados como avaria
   */
  calcularQuantidadeAvarias(): number {
    const servicosAvaria = this.servicos.filter(s => s.isAvaria).length;
    const pecasAvaria = this.pecas.filter(p => p.isAvaria).length;
    return servicosAvaria + pecasAvaria;
  }
  
  /**
   * Gera boleto para itens marcados como avaria
   */
  gerarBoletoAvarias(): void {
    if (this.gerandoBoletoAvarias || this.boletoAvariasGerado) {
      return;
    }

    const clienteId = this.toIntOrNull(this.serviceOrderForm.get('clienteId')?.value);
    if (!clienteId) {
      this.goToStep(4);
      this.openWarning('Selecione o Cliente (aba Responsáveis) antes de gerar o boleto de avarias.').subscribe();
      return;
    }

    // Filtra serviços e peças marcados como avaria
    const servicosAvaria = this.servicos.filter(s => s.isAvaria);
    const pecasAvaria = this.pecas.filter(p => p.isAvaria);

    if (servicosAvaria.length === 0 && pecasAvaria.length === 0) {
      this.openWarning('Nenhum item marcado como avaria para gerar boleto.').subscribe();
      return;
    }

    const cliente = this.serviceOrderForm.get('clienteNome')?.value || 'Cliente não informado';
    const veiculo = this.serviceOrderForm.get('veiculoPlaca')?.value || 'Veículo não informado';
    const numeroOS = (this.serviceOrderForm.get('numeroOS')?.value || '').toString().trim();
    const contratoNumero = (this.serviceOrderForm.get('contratoNumero')?.value || '').toString().trim();

    const valorTotalRaw = this.calcularValorTotalAvarias();
    const valorTotal = Math.round((valorTotalRaw || 0) * 100) / 100;

    const issueDate = new Date();
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 1);

    const documentNumber = numeroOS !== '' ? numeroOS : `OS-${this.serviceOrderId ?? 'N/A'}`;
    const documentType = 'Ordem de Serviço';
    const category = 'Serviços';
    const status = 'Pendente';

    const staffId = this.authService.getStaffIdFromToken();

    const payload: AccountsReceivableCreate = {
      ...(contratoNumero !== '' && { contractNumber: contratoNumero }),
      documentNumber,
      documentType,
      issueDate: this.formatDateForInput(issueDate),
      dueDate: this.formatDateForInput(dueDate),
      originalAmount: valorTotal,
      amountToReceive: valorTotal,
      status,
      category,
      customerId: clienteId,
      ...(staffId != null && { issuedByStaffId: staffId }),
      description: `OS ${documentNumber}${cliente ? ` - ${cliente}` : ''}`
    };

    this.gerandoBoletoAvarias = true;
    this.confirmationService
      .confirm({
        title: 'Gerar boleto de avarias',
        message:
          `OS: ${documentNumber}. Cliente: ${cliente}. Veículo: ${veiculo}. ` +
          `Serviços: ${servicosAvaria.length}. Peças: ${pecasAvaria.length}. ` +
          `Valor total: ${this.formatarValor(valorTotal)}. ` +
          `Vencimento: ${new Date(payload.dueDate as any).toLocaleDateString('pt-BR')}.`,
        confirmText: 'Gerar boleto',
        cancelText: 'Cancelar',
        confirmButtonClass: 'btn-danger',
        cancelButtonClass: 'btn-secondary',
        type: 'warning'
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          this.gerandoBoletoAvarias = false;
          return;
        }

        this.accountsReceivableService.createAccountsReceivable(payload).pipe(take(1)).subscribe({
          next: () => {
            this.openSuccess('Conta a receber criada para cobrança de avarias.').subscribe();
            this.adicionarEventoHistorico(
              'financeiro',
              'Boleto de Avarias Gerado',
              `Conta a receber criada - OS: ${documentNumber} - Valor: ${this.formatarValor(valorTotal)} - Vencimento: ${this.formatDateForDisplay(payload.dueDate)}`
            );
            this.boletoAvariasGerado = true;
            this.gerandoBoletoAvarias = false;
          },
          error: (error) => {
            console.error('Erro ao criar conta a receber para avarias:', error);
            this.gerandoBoletoAvarias = false;
          }
        });
      });
  }
  
  /**
   * Formata valor para exibição
   */
  formatarValor(valor: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  }
  
  /**
   * Chamado quando um tipo de serviço é selecionado no dropdown
   */
  onServicoSelecionado(tipoServico: string): void {
    // Limpa os dados anteriores
    this.pecasServicoSelecionado = [];
    this.valorTotalServicoSelecionado = 0;
    this.valorUnitarioServico = 0;
    
    // Busca as peças e valores do serviço selecionado
    const pecasValores = this.servicosPecasValores[tipoServico];
    
    if (pecasValores && pecasValores.length > 0) {
      // Calcula o valor total de cada item
      this.pecasServicoSelecionado = pecasValores.map(peca => ({
        item: peca.item,
        quantidade: peca.quantidade,
        valorUnitario: peca.valorUnitario,
        valorTotal: peca.quantidade * peca.valorUnitario
      }));
      
      // Calcula o valor total do serviço
      this.valorTotalServicoSelecionado = this.pecasServicoSelecionado.reduce(
        (total, peca) => total + peca.valorTotal, 
        0
      );
      
      // Define o valor unitário como o valor total calculado
      this.valorUnitarioServico = this.valorTotalServicoSelecionado;
    }
  }
  
  /**
   * Chamado quando um arquivo é selecionado
   */
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validar tamanho (máx 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB em bytes
      if (file.size > maxSize) {
        this.openError('O arquivo excede o tamanho máximo permitido de 10MB.').subscribe();
        event.target.value = '';
        return;
      }
      
      this.novoAnexo.arquivo = file;
      this.novoAnexo.nomeArquivo = file.name;
      this.novoAnexo.tamanho = file.size;
    }
  }
  
  /**
   * Adiciona um novo anexo à lista
   */
  adicionarAnexo(): void {
    if (!this.novoAnexo.tipo || !this.novoAnexo.arquivo || !this.novoAnexo.nomeArquivo || !this.novoAnexo.tamanho) {
      this.openWarning('Por favor, selecione o tipo do documento e o arquivo.').subscribe();
      return;
    }

    if (!this.serviceOrderId) {
      this.openError('A OS precisa estar salva para enviar anexos.').subscribe();
      return;
    }
    
    const currentUser = this.authService.currentUserValue;

    const userId =
      (currentUser?.userId != null ? String(currentUser.userId) : null) ??
      localStorage.getItem('lastLoggedUserId') ??
      undefined;

    const fileToSend = this.novoAnexo.arquivo;
    const tipo = this.novoAnexo.tipo as AnexoOS['tipo'];
    const descricao = (this.novoAnexo.descricao || '').toString();

    this.serviceOrdersService
      .uploadAndRegisterAttachment(this.serviceOrderId, fileToSend, { type: tipo, description: descricao, userId })
      .pipe(take(1))
      .subscribe({
        next: (created) => {
          const createdView = this.mapAttachmentDtoToAnexo(created);
          this.anexos.push(createdView);

          this.adicionarEventoHistorico(
            'outros',
            'Anexo adicionado',
            `Arquivo: ${createdView.nomeArquivo} (Tipo: ${createdView.tipo})`
          );

          // Limpar formulário
          this.novoAnexo = {
            tipo: undefined,
            descricao: '',
            arquivo: null
          };

          // Limpar input file
          const fileInput = document.getElementById('fileInput') as HTMLInputElement;
          if (fileInput) {
            fileInput.value = '';
          }

          this.openSuccess('Anexo enviado com sucesso!').subscribe();
        },
        error: (err) => {
          console.error('Erro ao enviar anexo:', err);
          this.openError('Erro ao enviar anexo.').subscribe();
        }
      });
  }
  
  /**
   * Remove um anexo da lista
   */
  removerAnexo(anexoId: number): void {
    const anexo = this.anexos.find(a => a.id === anexoId);
    if (!anexo) return;

    this.confirmationService
      .confirm({
        title: 'Confirmar remoção',
        message: `Tem certeza que deseja remover o anexo "${anexo.nomeArquivo}"?`,
        confirmText: 'Remover',
        cancelText: 'Cancelar',
        confirmButtonClass: 'btn-danger',
        cancelButtonClass: 'btn-secondary',
        type: 'warning'
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;

        // Se já existe no backend (tem id + serviceOrderId), remove via API
        if (this.serviceOrderId && anexoId) {
          const currentUser = this.authService.currentUserValue;
          const userId =
            (currentUser?.userId != null ? String(currentUser.userId) : null) ??
            localStorage.getItem('lastLoggedUserId') ??
            undefined;

          this.serviceOrdersService
            .deleteAttachment(this.serviceOrderId, anexoId, userId)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.anexos = this.anexos.filter(a => a.id !== anexoId);
                this.adicionarEventoHistorico(
                  'outros',
                  'Anexo removido',
                  `Arquivo removido: ${anexo.nomeArquivo}`
                );
                this.openSuccess('Anexo removido com sucesso!').subscribe();
              },
              error: (err) => {
                console.error('Erro ao remover anexo:', err);
                this.openError('Erro ao remover anexo.').subscribe();
              }
            });
          return;
        }

        // Fallback local
        this.anexos = this.anexos.filter(a => a.id !== anexoId);
        this.openSuccess('Anexo removido com sucesso!').subscribe();
      });
  }
  
  /**
   * Visualiza um anexo (abre em nova aba)
   */
  visualizarAnexo(anexo: AnexoOS): void {
    // Evita bloqueio de popup: abre uma aba imediatamente no clique.
    const popup = window.open('', '_blank');

    const tryOpenUrl = (url: string) => {
      if (popup && !popup.closed) {
        popup.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    };

    const closePopup = () => {
      try {
        if (popup && !popup.closed) {
          popup.close();
        }
      } catch {
        // ignore
      }
    };

    if (this.serviceOrderId) {
      const blobName =
        (anexo as any)?.filePath ??
        (typeof (anexo as any)?.arquivo === 'string' ? (anexo as any).arquivo : null) ??
        (anexo as any)?.arquivo?.filePath ??
        (anexo as any)?.arquivo?.blobName;

      const request$ = anexo.id
        ? this.serviceOrdersService.getAttachmentDownloadUrlById(this.serviceOrderId, anexo.id)
        : (blobName ? this.serviceOrdersService.getAttachmentDownloadUrlByBlobName(this.serviceOrderId, String(blobName)) : null);

      if (!request$) {
        closePopup();
        this.openError('Arquivo não disponível para visualização.').subscribe();
        return;
      }

      request$
        .pipe(take(1))
        .subscribe({
          next: (url) => tryOpenUrl(url),
          error: (err) => {
            console.error('Erro ao gerar URL de visualização:', err);
            closePopup();
            this.openError('Arquivo não disponível para visualização.').subscribe();
          }
        });
      return;
    }

    // Fallback: anexos ainda locais
    if (anexo.arquivo && anexo.arquivo instanceof File) {
      const fileURL = URL.createObjectURL(anexo.arquivo);
      tryOpenUrl(fileURL);
      return;
    }

    closePopup();
    this.openError('Arquivo não disponível para visualização.').subscribe();
  }
  
  /**
   * Faz download de um anexo
   */
  baixarAnexo(anexo: AnexoOS): void {
    if (this.serviceOrderId && anexo.id) {
      this.serviceOrdersService
        .getAttachmentDownloadUrlById(this.serviceOrderId, anexo.id)
        .pipe(take(1))
        .subscribe({
          next: (url) => {
            const link = document.createElement('a');
            link.href = url;
            link.download = anexo.nomeArquivo;
            link.rel = 'noopener';
            link.target = '_blank';
            link.click();
          },
          error: (err) => {
            console.error('Erro ao gerar URL de download:', err);
            this.openError('Arquivo não disponível para download.').subscribe();
          }
        });
      return;
    }

    if (this.serviceOrderId && !anexo.id) {
      const blobName =
        (anexo as any)?.filePath ??
        (typeof (anexo as any)?.arquivo === 'string' ? (anexo as any).arquivo : null) ??
        (anexo as any)?.arquivo?.filePath ??
        (anexo as any)?.arquivo?.blobName;

      if (blobName) {
        this.serviceOrdersService
          .getAttachmentDownloadUrlByBlobName(this.serviceOrderId, String(blobName))
          .pipe(take(1))
          .subscribe({
            next: (url) => {
              const link = document.createElement('a');
              link.href = url;
              link.download = anexo.nomeArquivo;
              link.rel = 'noopener';
              link.target = '_blank';
              link.click();
            },
            error: (err) => {
              console.error('Erro ao gerar URL de download (blobName):', err);
              this.openError('Arquivo não disponível para download.').subscribe();
            }
          });
        return;
      }
    }

    // Fallback: anexos ainda locais
    if (anexo.arquivo && anexo.arquivo instanceof File) {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(anexo.arquivo);
      link.download = anexo.nomeArquivo;
      link.click();
      return;
    }

    this.openError('Arquivo não disponível para download.').subscribe();
  }
  
  /**
   * Formata o tamanho do arquivo para exibição
   */
  formatarTamanhoArquivo(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
  
  /**
   * Calcula o tamanho total de todos os anexos
   */
  calcularTamanhoTotalAnexos(): number {
    return this.anexos.reduce((total, anexo) => total + anexo.tamanho, 0);
  }
  
  /**
   * Retorna o ícone apropriado para cada tipo de anexo
   */
  getIconeAnexo(tipo: string): string {
    const icones: any = {
      'Avaria': 'fas fa-exclamation-triangle',
      'Documento Adicional': 'fas fa-file-alt',
      'Foto do Veículo': 'fas fa-camera',
      'Orçamento': 'fas fa-file-invoice-dollar',
      'Nota Fiscal': 'fas fa-receipt',
      'Laudo Técnico': 'fas fa-file-medical',
      'Checklist': 'fas fa-tasks',
      'Contrato': 'fas fa-file-contract',
      'Autorização': 'fas fa-file-signature',
      'Outros': 'fas fa-file'
    };
    
    return icones[tipo] || 'fas fa-file';
  }
  
  /**
   * Retorna a classe do badge para cada tipo de anexo
   */
  getBadgeClassAnexo(tipo: string): string {
    const classes: any = {
      'Avaria': 'badge-danger',
      'Documento Adicional': 'badge-info',
      'Foto do Veículo': 'badge-primary',
      'Orçamento': 'badge-warning',
      'Nota Fiscal': 'badge-success',
      'Laudo Técnico': 'badge-secondary',
      'Checklist': 'badge-info',
      'Contrato': 'badge-dark',
      'Autorização': 'badge-warning',
      'Outros': 'badge-secondary'
    };
    
    return classes[tipo] || 'badge-secondary';
  }

  /**
   * Alterna a seleção de uma peça do estoque
   */
  togglePecaSelecionada(pecaId: string): void {
    const index = this.pecasSelecionadasEstoque.indexOf(pecaId);
    if (index > -1) {
      this.pecasSelecionadasEstoque.splice(index, 1);
    } else {
      this.pecasSelecionadasEstoque.push(pecaId);
    }
  }

  /**
   * Remove uma peça específica da seleção
   */
  removerPecaSelecionada(pecaId: string): void {
    const index = this.pecasSelecionadasEstoque.indexOf(pecaId);
    if (index > -1) {
      this.pecasSelecionadasEstoque.splice(index, 1);
    }
  }

  /**
   * Limpa todas as peças selecionadas
   */
  limparPecasSelecionadas(): void {
    this.pecasSelecionadasEstoque = [];
  }

  /**
   * Obtém o nome de uma peça pelo ID
   */
  getPecaNome(pecaId: string): string {
    const peca = this.pecasDisponiveisEstoque.find(p => p.id === pecaId);
    return peca ? peca.nome : 'Peça não encontrada';
  }

  /**
   * Obtém peças agrupadas por categoria
   */
  getPecasPorCategoria(): { [categoria: string]: Array<{id: string, nome: string, categoria: string, quantidadeDisponivel: number, valorUnitario: number}> } {
    return this.pecasDisponiveisEstoque.reduce((acc, peca) => {
      if (!acc[peca.categoria]) {
        acc[peca.categoria] = [];
      }
      acc[peca.categoria].push(peca);
      return acc;
    }, {} as { [categoria: string]: Array<{id: string, nome: string, categoria: string, quantidadeDisponivel: number, valorUnitario: number}> });
  }

  /**
   * Obtém as peças associadas a um serviço específico
   */
  getPecasServico(descricaoServico: string): Array<{item: string, quantidade: number, valorUnitario: number, valorTotal: number}> {
    const pecasValores = this.servicosPecasValores[descricaoServico];
    if (pecasValores && pecasValores.length > 0) {
      return pecasValores.map(peca => ({
        item: peca.item,
        quantidade: peca.quantidade,
        valorUnitario: peca.valorUnitario,
        valorTotal: peca.quantidade * peca.valorUnitario
      }));
    }
    return [];
  }

  /**
   * Alterna a exibição dos detalhes das peças para um serviço específico
   */
  togglePecasDetalhes(index: number): void {
    this.servicoDetalhesExpandidos[index] = !this.servicoDetalhesExpandidos[index];
  }

  /**
   * Gera boleto para um serviço específico
   */
  gerarBoletoServico(servico: ServicoOS): void {
    if (!servico.id) {
      this.openError('Erro: Serviço não possui ID válido.').subscribe();
      return;
    }

    // Dados do cliente (assumindo que temos acesso aos dados do cliente)
    const cliente = this.serviceOrderForm.get('clienteNome')?.value || 'Cliente não informado';
    const veiculo = this.serviceOrderForm.get('veiculoPlaca')?.value || 'Veículo não informado';
    
    // Dados do boleto
    const dadosBoleto = {
      servicoId: servico.id,
      descricao: servico.descricao,
      valor: servico.valorTotal,
      cliente: cliente,
      veiculo: veiculo,
      dataVencimento: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 dias
      numeroOS: this.serviceOrderForm.get('numeroOS')?.value || 'N/A'
    };

    this.confirmationService
      .confirm({
        title: 'Gerar boleto',
        message:
          `Serviço: ${servico.descricao}. Cliente: ${cliente}. Veículo: ${veiculo}. ` +
          `Valor: ${this.formatarValor(servico.valorTotal)}. Vencimento: ${dadosBoleto.dataVencimento.toLocaleDateString('pt-BR')}.`,
        confirmText: 'Gerar boleto',
        cancelText: 'Cancelar',
        confirmButtonClass: 'btn-danger',
        cancelButtonClass: 'btn-secondary',
        type: 'warning'
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.openSuccess('Boleto gerado com sucesso!').subscribe();
        this.adicionarEventoHistorico(
          'financeiro',
          'Boleto gerado',
          `Boleto gerado para serviço: ${servico.descricao} - Valor: ${this.formatarValor(servico.valorTotal)}`
        );
      });
  }
  
  /**
   * Retorna o ícone apropriado para cada tipo de arquivo
   */
  getIconeArquivo(nomeArquivo: string): string {
    const extensao = nomeArquivo.split('.').pop()?.toLowerCase();
    
    const icones: any = {
      'pdf': 'fas fa-file-pdf text-danger',
      'doc': 'fas fa-file-word text-primary',
      'docx': 'fas fa-file-word text-primary',
      'xls': 'fas fa-file-excel text-success',
      'xlsx': 'fas fa-file-excel text-success',
      'png': 'fas fa-file-image text-info',
      'jpg': 'fas fa-file-image text-info',
      'jpeg': 'fas fa-file-image text-info',
      'gif': 'fas fa-file-image text-info',
      'zip': 'fas fa-file-archive text-warning',
      'rar': 'fas fa-file-archive text-warning'
    };
    
    return icones[extensao || ''] || 'fas fa-file text-secondary';
  }

  // ========== MÉTODOS PARA CHECKLIST ==========

  /**
   * Visualiza um checklist PDF
   */
  visualizarChecklist(checklist: ChecklistOS): void {
    this.checklistSelecionado = checklist;
  }

  /**
   * Fecha o visualizador de PDF
   */
  fecharVisualizadorPDF(): void {
    this.checklistSelecionado = null;
  }

  /**
   * Faz download de um checklist PDF
   */
  baixarChecklist(checklist: ChecklistOS): void {
    if (!checklist.arquivo) {
      this.openError('Arquivo não disponível para download.').subscribe();
      return;
    }

    const link = document.createElement('a');
    
    // Se for uma string (URL), usar diretamente
    if (typeof checklist.arquivo === 'string') {
      link.href = checklist.arquivo;
      link.download = checklist.nomeArquivo;
      link.target = '_blank';
      link.click();
      return;
    }
    
    // Se for um File, criar URL do objeto
    if (checklist.arquivo instanceof File) {
      link.href = URL.createObjectURL(checklist.arquivo);
      link.download = checklist.nomeArquivo;
      link.click();
      return;
    }
    
    this.openError('Arquivo não disponível para download.').subscribe();
  }

  /**
   * Obtém a URL do PDF para visualização
   */
  getPDFUrl(checklist: ChecklistOS): SafeResourceUrl | null {
    if (!checklist.arquivo) {
      return null;
    }
    
    // Se for uma string (URL), sanitizar e retornar
    if (typeof checklist.arquivo === 'string') {
      return this.sanitizer.bypassSecurityTrustResourceUrl(checklist.arquivo);
    }
    
    // Se for um File, criar URL do objeto
    if (checklist.arquivo instanceof File) {
      const url = URL.createObjectURL(checklist.arquivo);
      return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
    
    return null;
  }


  /**
   * Calcula o tamanho total de todos os checklists
   */
  calcularTamanhoTotalChecklists(): number {
    return this.checklists.reduce((total, checklist) => total + checklist.tamanho, 0);
  }

  /**
   * Retorna a classe do badge para cada tipo de checklist
   */
  getBadgeClassChecklist(tipo: string): string {
    const classes: any = {
      'Pré-entrega': 'badge-primary',
      'Pós-entrega': 'badge-success',
      'Inspeção Técnica': 'badge-info',
      'Verificação de Segurança': 'badge-danger',
      'Controle de Qualidade': 'badge-warning',
      'Vistoria Geral': 'badge-secondary',
      'Outros': 'badge-dark'
    };
    
    return classes[tipo] || 'badge-secondary';
  }

  /**
   * Retorna dados mockados de checklists para demonstração
   */
  getMockChecklists(): ChecklistOS[] {
    const currentUser = this.authService.currentUserValue;
    const usuarioNome = currentUser?.username || 'Sistema';
    
    return [
      {
        id: 1,
        tipo: 'Vistoria Geral',
        descricao: 'Checklist de verificação completa do serviço realizado',
        nomeArquivo: 'pdfServico_57952bf8ca7af_24-07-2016_17-58-32.pdf',
        tamanho: 2048576, // 2MB
        arquivo: 'https://araucariageneticabovina.com.br/arquivos/servico/pdfServico_57952bf8ca7af_24-07-2016_17-58-32.pdf',
        usuarioAdicionou: usuarioNome,
        dataAdicao: new Date().toISOString(),
        preenchido: true
      }
    ];
  }

  // ========== MÉTODOS PARA SESSÕES DE TRABALHO ==========

  /**
   * Salva a sessão atual de serviços e peças
   */
  salvarSessaoTrabalho(): void {
    if (this.servicos.length === 0 && this.pecas.length === 0) {
      this.openWarning('Não há serviços ou peças para salvar.').subscribe();
      return;
    }

    this.confirmationService
      .confirm({
        title: 'Salvar histórico de serviços',
        message:
          `Serviços: ${this.servicos.length}. ` +
          `Peças: ${this.pecas.length}. ` +
          `Valor total: ${this.formatarValor(this.calcularValorTotalGeral())}. ` +
          'Esta ação cria um registro permanente no histórico e limpa os serviços/peças atuais para evitar duplicação.',
        confirmText: 'Salvar',
        cancelText: 'Cancelar',
        confirmButtonClass: 'btn-primary',
        cancelButtonClass: 'btn-secondary',
        type: 'question'
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.salvarSessaoTrabalhoConfirmada();
      });
  }

  /**
   * Atualiza uma WorkSection que está em revisão de orçamento. Apaga os
   * serviços/peças existentes, recria com os valores editados e reverte o
   * status da WorkSection ('Pendente') e do Budget ('Pendente'/clientApproved=false)
   * para que o cliente possa aprovar/rejeitar novamente.
   */
  private atualizarSessaoEmRevisao(servicosValidos: any[], pecasValidas: any[]): void {
    const wsId = this.editingWorkSectionId;
    if (!wsId) {
      this.openError('Nenhuma seção em edição para atualizar.').subscribe();
      return;
    }

    const isThirdPartyOsType = this.isThirdPartyOS();
    const descricaoSessao = `Serviço #${(this.sessoesTrabalho.findIndex((s) => s.id === wsId) + 1) || this.sessoesTrabalho.length}`;
    const valorTotalSessao =
      servicosValidos.reduce((total, s) => total + (s.valorTotal || 0), 0) +
      pecasValidas.reduce((total, p) => total + (p.valorTotal || 0), 0);

    this.salvandoSessaoTrabalho = true;

    const existingServices$ = this.serviceOrderWorkSectionsService
      .getServicesByWorkSectionId(wsId)
      .pipe(catchError(() => of([] as ServiceOrderWorkSectionService[])));
    const existingItems$ = this.serviceOrderWorkSectionsService
      .getItemsByWorkSectionId(wsId)
      .pipe(catchError(() => of([] as ServiceOrderWorkSectionItem[])));

    forkJoin({ services: existingServices$, items: existingItems$ })
      .pipe(
        switchMap(({ services, items }) => {
          const deleteOps: Observable<any>[] = [];
          (services || []).forEach((s) => {
            if (s?.id) deleteOps.push(this.serviceOrderWorkSectionsService.deleteService(s.id).pipe(catchError(() => of(null))));
          });
          (items || []).forEach((i) => {
            if (i?.id) deleteOps.push(this.serviceOrderWorkSectionsService.deleteItem(i.id).pipe(catchError(() => of(null))));
          });
          return deleteOps.length > 0 ? forkJoin(deleteOps) : of([]);
        }),
        switchMap(() => {
          const serviceRequests = servicosValidos.map((servico) =>
            this.serviceOrderWorkSectionsService.createService(
              {
                serviceOrderWorkSectionId: wsId,
                serviceTypeId: this.resolveServiceTypeId(servico.descricao) ?? null,
                externalServiceProviderId: isThirdPartyOsType
                  ? (servico.externalServiceProviderId ?? this.selectedProvider?.id ?? null)
                  : undefined,
                description: servico.descricao,
                quantity: servico.quantidade,
                unitValue: servico.valorUnitario,
                maintenanceKind: servico.maintenanceKind || 'Corretiva',
                chargePercent: this.canViewChargePercent ? (servico.chargePercent ?? null) : null,
                paymentStatus: servico.isAvaria ? (servico.statusAvaria || 'Pendente') : undefined,
                hasFault: !!servico.isAvaria
              },
              false
            )
          );
          const itemRequests = pecasValidas.map((peca) =>
            this.serviceOrderWorkSectionsService.createItem(
              {
                serviceOrderWorkSectionId: wsId,
                itemId: this.resolveItemId(peca.descricao) ?? null,
                description: peca.descricao,
                quantity: peca.quantidade,
                unitValue: peca.valorUnitario,
                maintenanceKind: peca.maintenanceKind || 'Corretiva',
                chargePercent: this.canViewChargePercent ? (peca.chargePercent ?? null) : null,
                paymentStatus: peca.isAvaria ? (peca.statusAvaria || 'Pendente') : undefined,
                hasFault: !!peca.isAvaria,
                withdrawnFromStock: this.canStockSection() && !isThirdPartyOsType,
                inStock: this.canStockSection() && !isThirdPartyOsType
              },
              false
            )
          );
          return forkJoin({
            services: serviceRequests.length > 0 ? forkJoin(serviceRequests) : of([]),
            items: itemRequests.length > 0 ? forkJoin(itemRequests) : of([])
          });
        }),
        switchMap(() =>
          this.serviceOrderWorkSectionsService
            .update(wsId, {
              serviceOrderId: this.serviceOrderId!,
              description: descricaoSessao,
              status: 'Pendente',
              externalServiceProviderId: isThirdPartyOsType ? (this.selectedProvider?.id ?? null) : undefined
            } as any)
            .pipe(catchError((err) => {
              console.error('Erro ao atualizar WorkSection em revisão:', err);
              return of(null);
            }))
        ),
        switchMap(() => {
          if (!isThirdPartyOsType) return of(null);
          return this.budgetServiceOrdersService.getByWorkSectionId(wsId).pipe(
            switchMap((budgets: any) => {
              const list = Array.isArray(budgets) ? budgets : (budgets ? [budgets] : []);
              const budget = list[0];
              if (!budget?.id) return of(null);
              return this.budgetServiceOrdersService
                .update(budget.id, {
                  ...budget,
                  status: 'Pendente',
                  clientApproved: false
                })
                .pipe(catchError(() => of(null)));
            }),
            catchError(() => of(null))
          );
        }),
        switchMap(() => this.loadWorkSectionsWithDetails(this.serviceOrderId!))
      )
      .subscribe({
        next: (sections) => {
          this.serviceWorkSections = sections || [];
          this.sessoesTrabalho = this.mapWorkSectionsToSessions(sections);

          this.adicionarEventoHistorico(
            'outros',
            'Orçamento revisado',
            `Revisão com ${servicosValidos.length} serviço(s) e ${pecasValidas.length} peça(s) - Total: ${this.formatarValor(valorTotalSessao)}`
          );

          this.editingWorkSectionId = null;
          this.limparServicosEPecas();
          this.salvandoSessaoTrabalho = false;

          this.openSuccess(
            `Orçamento revisado e enviado para nova aprovação do cliente. ` +
              `Serviços: ${servicosValidos.length}. Peças: ${pecasValidas.length}. Valor total: ${this.formatarValor(valorTotalSessao)}.`
          ).subscribe();

          setTimeout(() => {
            const sessoesElement = document.querySelector('.sessoes-container');
            if (sessoesElement) {
              sessoesElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 300);
        },
        error: (error) => {
          console.error('Erro ao atualizar sessão em revisão:', error);
          this.openError('Erro ao atualizar a sessão em revisão.').subscribe();
          this.salvandoSessaoTrabalho = false;
        }
      });
  }

  private salvarSessaoTrabalhoConfirmada(): void {
    if (!this.serviceOrderId) {
      this.openWarning('Gere a OS antes de salvar uma sessão de trabalho.').subscribe();
      return;
    }

    const servicosValidos = this.servicos.filter(servico => !!servico.descricao?.trim());
    const pecasValidas = this.pecas.filter(peca => !!peca.descricao?.trim());

    if (servicosValidos.length === 0 && pecasValidas.length === 0) {
      this.openWarning('Nenhum serviço ou peça válido foi encontrado para salvar.').subscribe();
      return;
    }

    // Caminho de revisão: atualiza a WorkSection e o Budget em vez de criar novos.
    if (this.isEditandoSessao()) {
      this.atualizarSessaoEmRevisao(servicosValidos, pecasValidas);
      return;
    }

    const isThirdPartyOsType = this.isThirdPartyOS();
    const isThirdParty = isThirdPartyOsType && !!this.selectedProvider;

    if (isThirdPartyOsType && !this.selectedProvider) {
      this.openWarning('Selecione um prestador antes de salvar a seção de trabalho.').subscribe();
      return;
    }

    // Regra atual: OS normal sempre solicita as peças ao estoque; OS terceiros nunca.
    const pecasParaMovimentar = !isThirdPartyOsType ? pecasValidas : [];

    // Separa peças com quantidade <= 0 para avisar o utilizador (não enviadas no payload).
    const pecasIgnoradasQtd = pecasParaMovimentar.filter(p => Number(p.quantidade || 0) <= 0);
    const pecasParaSolicitar = pecasParaMovimentar.filter(p => Number(p.quantidade || 0) > 0);

    if (pecasIgnoradasQtd.length > 0) {
      const nomes = pecasIgnoradasQtd
        .map(p => p.descricao || '(sem descrição)')
        .join(', ');
      this.openWarning(
        `As seguintes peças foram ignoradas na solicitação ao estoque por terem quantidade inválida (<= 0): ${nomes}.`
      ).subscribe();
    }

    if (!isThirdPartyOsType && pecasParaMovimentar.length > 0) {
      // Validação de saldo removida (não bloquear salvamento por quantidade em estoque)
    }

    const needsStockRequest = this.canStockSection() && !isThirdPartyOsType && pecasParaSolicitar.length > 0;

    // requestedStockId/sourceWarehouseId agora são derivados PER LINHA a partir do
    // próprio item escolhido (cada Item conhece seu stockId/warehouseId).
    type PecaRequestRow = {
      peca: typeof pecasParaSolicitar[number];
      requestedStockId: number;
      sourceWarehouseId: number | null;
    };

    let pecasComStockResolvido: PecaRequestRow[] = [];
    if (needsStockRequest) {
      const pecasSemStock: typeof pecasParaSolicitar = [];
      const formWarehouseId = this.toIntOrNull(this.serviceOrderForm.get('almoxarifadoId')?.value);
      const fallbackWarehouseId = this.toIntOrNull(this.selectedWarehouse?.id);
      for (const peca of pecasParaSolicitar) {
        const item = this.itemsDisponiveis.find((entry) => entry.name === peca.descricao);
        const stockId = this.toIntOrNull((item as any)?.stockId);
        const itemWarehouseId = this.toIntOrNull((item as any)?.warehouseId);
        const pecaWarehouseId = this.toIntOrNull((peca as any)?.warehouseId);
        const resolvedWarehouseId =
          (pecaWarehouseId && pecaWarehouseId > 0 ? pecaWarehouseId : null)
          ?? (itemWarehouseId && itemWarehouseId > 0 ? itemWarehouseId : null)
          ?? (formWarehouseId && formWarehouseId > 0 ? formWarehouseId : null)
          ?? (fallbackWarehouseId && fallbackWarehouseId > 0 ? fallbackWarehouseId : null);
        if (item && stockId && stockId > 0) {
          pecasComStockResolvido.push({
            peca,
            requestedStockId: stockId,
            sourceWarehouseId: resolvedWarehouseId
          });
        } else {
          pecasSemStock.push(peca);
        }
      }
      if (pecasSemStock.length > 0) {
        const nomes = pecasSemStock.map((p) => p.descricao || '(sem descrição)').join(', ');
        this.openError(
          `Não foi possível identificar o estoque das seguintes peças: ${nomes}. Selecione-as novamente.`
        ).subscribe();
        return;
      }
    }

    // DEBUG: rastrear o POST de service-order-stock-requests
    console.log('[OS][salvar] flags', {
      isThirdPartyOsType,
      pecasValidas: pecasValidas.length,
      pecasParaMovimentar: pecasParaMovimentar.length,
      needsStockRequest,
      pecasComStockResolvido: pecasComStockResolvido.length,
      configOrderIsThirdParty: this.configOrder?.isThirdParty,
      selectedOsTypeIsThirdParty: this.selectedOsTypeConfig?.isThirdParty,
      selectedProvider: this.selectedProvider?.id ?? null
    });

    const serviceOrderNumber = String(this.serviceOrderForm.get('numeroOS')?.value || '').trim();

    if (!isThirdParty) {
      const serviceWithoutType = servicosValidos.find(servico => !this.resolveServiceTypeId(servico.descricao));
      if (serviceWithoutType) {
        this.openWarning(`O serviço "${serviceWithoutType.descricao}" não foi encontrado na lista de tipos de serviço.`).subscribe();
        return;
      }

      const itemWithoutId = pecasValidas.find(peca => !this.resolveItemId(peca.descricao));
      if (itemWithoutId) {
        this.openWarning(`A peça "${itemWithoutId.descricao}" não foi encontrada na lista de itens.`).subscribe();
        return;
      }
    }

    const currentUser = this.authService.currentUserValue;
    const now = new Date();

    const descricaoSessao = `Serviço #${this.sessoesTrabalho.length + 1}`;
    const valorTotalSessao =
      servicosValidos.reduce((total, servico) => total + (servico.valorTotal || 0), 0) +
      pecasValidas.reduce((total, peca) => total + (peca.valorTotal || 0), 0);

    this.salvandoSessaoTrabalho = true;

    const staffId$ = of(null);

    staffId$
      .pipe(
        switchMap((staffId) => {
          return this.serviceOrderWorkSectionsService
            .create(
              {
                serviceOrderId: this.serviceOrderId!,
                description: descricaoSessao,
                status: 'Pendente',
                externalServiceProviderId: isThirdPartyOsType ? (this.selectedProvider?.id ?? null) : undefined
              },
              false
            )
            .pipe(
              switchMap((section) => {
                if (!isThirdPartyOsType && pecasComStockResolvido.length > 0) {
                  const priorityRaw = String(
                    this.serviceOrderForm.get('stockRequestPriority')?.value ?? ''
                  ).trim();
                  const priority = ['Normal', 'Alta', 'Urgente'].includes(priorityRaw)
                    ? priorityRaw
                    : 'Normal';

                  return this.stockMovementsService
                    .createServiceOrderStockRequest({
                      serviceOrderId: this.serviceOrderId!,
                      priority,
                      notes: 'Solicitacao de pecas da OS',
                      items: pecasComStockResolvido.map(({ peca, requestedStockId, sourceWarehouseId }) => {
                        const item: any = {
                          requestedStockId,
                          requestedQuantity: Number(peca.quantidade || 0),
                          notes: peca.descricao
                        };
                        if (sourceWarehouseId && sourceWarehouseId > 0) {
                          item.sourceWarehouseId = sourceWarehouseId;
                        }
                        return item;
                      })
                    })
                    .pipe(
                      switchMap((stockRequestResponse) => {
                        const stockRequestId = extractServiceOrderStockRequestId(stockRequestResponse);
                        if (!stockRequestId) {
                          return of(section);
                        }

                        return this.serviceOrderWorkSectionsService
                          .update(section.id, {
                            serviceOrderId: section.serviceOrderId ?? this.serviceOrderId!,
                            description: section.description,
                            status: section.status ?? 'Pendente',
                            serviceOrderStockRequestsId: stockRequestId
                          }, false)
                          .pipe(
                            map(() => section),
                            catchError((linkErr) => {
                              console.error('[OS][salvar] falha ao vincular ServiceOrderStockRequestsID', linkErr);
                              return of(section);
                            })
                          );
                      }),
                      catchError((err) => {
                        console.error('[OS][salvar] falha em createServiceOrderStockRequest', err);
                        this.openWarning(
                          'A solicitação de peças ao estoque falhou. A sessão de trabalho será salva mesmo assim — refaça o pedido depois.'
                        ).subscribe();
                        return of(section);
                      })
                    );
                }

                return of(section);
              }),
              switchMap((section) => {
                const serviceRequests = servicosValidos.map((servico) =>
                  this.serviceOrderWorkSectionsService.createService(
                    {
                      serviceOrderWorkSectionId: section.id,
                      serviceTypeId: this.resolveServiceTypeId(servico.descricao) ?? null,
                      externalServiceProviderId: isThirdPartyOsType ? (servico.externalServiceProviderId ?? this.selectedProvider?.id ?? null) : undefined,
                      description: servico.descricao,
                      quantity: servico.quantidade,
                      unitValue: servico.valorUnitario,
                      maintenanceKind: servico.maintenanceKind || 'Corretiva',
                      chargePercent: this.canViewChargePercent ? (servico.chargePercent ?? null) : null,
                      paymentStatus: servico.isAvaria ? (servico.statusAvaria || 'Pendente') : undefined,
                      hasFault: !!servico.isAvaria
                    },
                    false
                  )
                );

                const itemRequests = pecasValidas.map((peca) =>
                  this.serviceOrderWorkSectionsService.createItem(
                    {
                      serviceOrderWorkSectionId: section.id,
                      itemId: this.resolveItemId(peca.descricao) ?? null,
                      description: peca.descricao,
                      quantity: peca.quantidade,
                      unitValue: peca.valorUnitario,
                      maintenanceKind: peca.maintenanceKind || 'Corretiva',
                      chargePercent: this.canViewChargePercent ? (peca.chargePercent ?? null) : null,
                      paymentStatus: peca.isAvaria ? (peca.statusAvaria || 'Pendente') : undefined,
                      hasFault: !!peca.isAvaria,
                      withdrawnFromStock: this.canStockSection() && !isThirdPartyOsType,
                      inStock: this.canStockSection() && !isThirdPartyOsType
                    },
                    false
                  )
                );

                return forkJoin({
                  services: serviceRequests.length > 0 ? forkJoin(serviceRequests) : of([]),
                  items: itemRequests.length > 0 ? forkJoin(itemRequests) : of([])
                }).pipe(map(() => section));
              }),
              switchMap((section) => {
                if (!isThirdPartyOsType) {
                  return of(section);
                }

                const osNumber = String(
                  this.serviceOrderForm.get('numeroOS')?.value || ''
                ).trim();
                const rawPlateLabel = String(
                  this.serviceOrderForm.get('veiculoPlaca')?.value || ''
                ).trim();
                const plate =
                  this.selectedVehicle?.placa
                  || (rawPlateLabel.includes(' - ') ? rawPlateLabel.split(' - ')[0].trim() : rawPlateLabel);

                return this.budgetServiceOrdersService
                  .create({
                    serviceOrderWorkSectionId: section.id,
                    osNumber: osNumber || undefined,
                    plateVechile: plate || undefined,
                    status: 'Pendente',
                    clientApproved: false
                  })
                  .pipe(
                    map(() => section),
                    catchError((budgetErr) => {
                      console.error('Erro ao gerar orçamento da OS de Terceiros:', budgetErr);
                      return of(section);
                    })
                  );
              }),
              switchMap(() => this.loadWorkSectionsWithDetails(this.serviceOrderId!))
            );
        })
      )
      .subscribe({
      next: (sections) => {
        this.serviceWorkSections = sections || [];
        this.sessoesTrabalho = this.mapWorkSectionsToSessions(sections);

        const isThirdPartyHistoryLabel = isThirdPartyOsType
          ? 'Orçamento gerado'
          : 'Histórico de serviços salvo';

        this.adicionarEventoHistorico(
          'outros',
          isThirdPartyHistoryLabel,
          `Registro com ${servicosValidos.length} serviço(s) e ${pecasValidas.length} peça(s) - Total: ${this.formatarValor(valorTotalSessao)}`
        );

        this.limparServicosEPecas();
        this.salvandoSessaoTrabalho = false;

        const successPrefix = isThirdPartyOsType
          ? 'Orçamento gerado com sucesso!'
          : 'Histórico de serviços salvo com sucesso!';

        this.openSuccess(
          `${successPrefix} Data/Hora: ${this.formatarDataHora(now.toISOString())}. ` +
            `Serviços: ${servicosValidos.length}. Peças: ${pecasValidas.length}. Valor total: ${this.formatarValor(valorTotalSessao)}.`
        ).subscribe();

        setTimeout(() => {
          const sessoesElement = document.querySelector('.sessoes-container');
          if (sessoesElement) {
            sessoesElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      },
      error: (error) => {
        console.error('Erro ao salvar sessão de trabalho:', error);
        this.openError('Erro ao salvar sessão de trabalho.').subscribe();
        this.salvandoSessaoTrabalho = false;
      }
    });
  }

  private resolveServiceTypeId(description: string): number | null {
    const serviceType = this.tiposServicos.find((item) => item.name === description);
    return serviceType?.id ?? null;
  }

  private resolveItemId(description: string): number | null {
    const item = this.itemsDisponiveis.find((entry) => entry.name === description);
    return item?.id ?? null;
  }


  /**
   * Limpa os arrays de serviços e peças após salvar a sessão
   */
  limparServicosEPecas(): void {
    // Limpar array de serviços
    this.servicos = [];
    
    // Limpar array de peças
    this.pecas = [];
    
    // Limpar peças selecionadas do estoque
    this.pecasSelecionadasEstoque = [];
    
    // Limpar dados do serviço selecionado
    this.pecasServicoSelecionado = [];
    this.valorTotalServicoSelecionado = 0;
    this.valorUnitarioServico = 0;
    
    // Limpar detalhes expandidos
    this.servicoDetalhesExpandidos = [];
    
    // Adicionar evento ao histórico
    this.adicionarEventoHistorico(
      'outros',
      'Serviços e peças limpos',
      'Arrays de serviços e peças foram limpos após salvar a sessão de trabalho'
    );
  }

  /**
   * Calcula o subtotal de serviços de uma sessão (só linhas de avaria cobram).
   */
  calcularSubtotalServicos(servicos: ServicoOS[]): number {
    return servicos.reduce((total, servico) => total + this.valorLinhaCobranca(servico), 0);
  }

  /**
   * Calcula o subtotal de peças de uma sessão (só linhas de avaria cobram).
   */
  calcularSubtotalPecas(pecas: any[]): number {
    return pecas.reduce((total, peca) => total + this.valorLinhaCobranca(peca), 0);
  }

  // ========== MÉTODOS PARA STATUS DE AVARIAS ==========

  /**
   * Altera o status de pagamento de uma avaria de serviço
   */
  alterarStatusAvariaServico(index: number): void {
    if (index < 0 || index >= this.servicos.length) {
      return;
    }

    const servico = this.servicos[index];
    if (!servico.isAvaria) {
      return;
    }

    const statusAtual = servico.statusAvaria || 'Pendente';
    const novoStatus = statusAtual === 'Pendente' ? 'Pago' : 'Pendente';
    
    this.confirmationService
      .confirm({
        title: 'Alterar status de avaria',
        message:
          `Serviço: ${servico.descricao}. Valor: ${this.formatarValor(servico.valorTotal)}. ` +
          `Status atual: ${statusAtual}. Novo status: ${novoStatus}. Esta alteração será registrada no histórico da OS.`,
        confirmText: 'Alterar',
        cancelText: 'Cancelar',
        confirmButtonClass: 'btn-primary',
        cancelButtonClass: 'btn-secondary',
        type: 'question'
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;

        servico.statusAvaria = novoStatus;

        this.adicionarEventoHistorico(
          'financeiro',
          `Status de avaria alterado para ${novoStatus}`,
          `Serviço: ${servico.descricao} - Valor: ${this.formatarValor(servico.valorTotal)} - Status: ${statusAtual} → ${novoStatus}`
        );

        this.openSuccess(`Status da avaria alterado para "${novoStatus}" com sucesso!`).subscribe();
      });
  }

  /**
   * Altera o status de pagamento de uma avaria de peça
   */
  alterarStatusAvariaPeca(index: number): void {
    if (index < 0 || index >= this.pecas.length) {
      return;
    }

    const peca = this.pecas[index];
    if (!peca.isAvaria) {
      return;
    }

    const statusAtual = peca.statusAvaria || 'Pendente';
    const novoStatus = statusAtual === 'Pendente' ? 'Pago' : 'Pendente';
    
    this.confirmationService
      .confirm({
        title: 'Alterar status de avaria',
        message:
          `Peça: ${peca.descricao}. Valor: ${this.formatarValor(peca.valorTotal)}. ` +
          `Status atual: ${statusAtual}. Novo status: ${novoStatus}. Esta alteração será registrada no histórico da OS.`,
        confirmText: 'Alterar',
        cancelText: 'Cancelar',
        confirmButtonClass: 'btn-primary',
        cancelButtonClass: 'btn-secondary',
        type: 'question'
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;

        peca.statusAvaria = novoStatus;

        this.adicionarEventoHistorico(
          'financeiro',
          `Status de avaria alterado para ${novoStatus}`,
          `Peça: ${peca.descricao} - Valor: ${this.formatarValor(peca.valorTotal)} - Status: ${statusAtual} → ${novoStatus}`
        );

        this.openSuccess(`Status da avaria alterado para "${novoStatus}" com sucesso!`).subscribe();
      });
  }

  /**
   * Calcula o valor total de avarias pendentes
   */
  calcularValorAvariasPendentes(): number {
    const servicosPendentes = this.servicos
      .filter(s => s.isAvaria && (s.statusAvaria === 'Pendente' || !s.statusAvaria))
      .reduce((total, servico) => total + (servico.valorTotal || 0), 0);
    
    const pecasPendentes = this.pecas
      .filter(p => p.isAvaria && (p.statusAvaria === 'Pendente' || !p.statusAvaria))
      .reduce((total, peca) => total + (peca.valorTotal || 0), 0);
    
    return servicosPendentes + pecasPendentes;
  }

  /**
   * Calcula o valor total de avarias pagas
   */
  calcularValorAvariasPagas(): number {
    const servicosPagas = this.servicos
      .filter(s => s.isAvaria && s.statusAvaria === 'Pago')
      .reduce((total, servico) => total + (servico.valorTotal || 0), 0);
    
    const pecasPagas = this.pecas
      .filter(p => p.isAvaria && p.statusAvaria === 'Pago')
      .reduce((total, peca) => total + (peca.valorTotal || 0), 0);
    
    return servicosPagas + pecasPagas;
  }

  /**
   * Conta a quantidade de avarias pendentes
   */
  contarAvariasPendentes(): number {
    const servicosPendentes = this.servicos.filter(s => s.isAvaria && (s.statusAvaria === 'Pendente' || !s.statusAvaria)).length;
    const pecasPendentes = this.pecas.filter(p => p.isAvaria && (p.statusAvaria === 'Pendente' || !p.statusAvaria)).length;
    return servicosPendentes + pecasPendentes;
  }

  /**
   * Conta a quantidade de avarias pagas
   */
  contarAvariasPagas(): number {
    const servicosPagas = this.servicos.filter(s => s.isAvaria && s.statusAvaria === 'Pago').length;
    const pecasPagas = this.pecas.filter(p => p.isAvaria && p.statusAvaria === 'Pago').length;
    return servicosPagas + pecasPagas;
  }

  private openSuccess(message: string, title: string = 'Sucesso') {
    return this.confirmationService
      .confirm({
        title,
        message,
        type: 'info',
        confirmText: 'OK',
        confirmButtonClass: 'btn-primary',
        cancelButtonClass: 'd-none'
      })
      .pipe(take(1));
  }

  private openError(message: string, title: string = 'Erro') {
    return this.confirmationService
      .confirm({
        title,
        message,
        type: 'danger',
        confirmText: 'OK',
        confirmButtonClass: 'btn-danger',
        cancelButtonClass: 'd-none'
      })
      .pipe(take(1));
  }

  private openWarning(message: string, title: string = 'Atenção') {
    return this.confirmationService
      .confirm({
        title,
        message,
        type: 'warning',
        confirmText: 'OK',
        confirmButtonClass: 'btn-primary',
        cancelButtonClass: 'd-none'
      })
      .pipe(take(1));
  }
}

